import { EmailHint } from "@/components/EmailHint";
import { entryMapKey } from "@/lib/entry-key";
import { useState, useRef, useMemo, useCallback, useEffect, useId, Fragment } from "react";
import { Link as WouterLink, useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionContent } from "@/components/ui/accordion";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { RequiredStar } from "@/components/RequiredFieldStar";
import { formatPhone } from "@shared/phone";
import { fieldRendersStar } from "@shared/lib/field-star";
import { entryUserActioned } from "@shared/lib/entry-provenance";
import { LENS_FIELD_PARAM, LENS_ROW_PARAM, resolveLensReveal, lensFieldAnchor, lensColumnAnchor } from "@/lib/planner-lens-ui";
import { FieldDeclineControl } from "@/components/FieldDeclineControl";
import { type DeclineReason } from "@shared/constants/decline-reason";
import { RadioOptions } from "@/components/RadioOptions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { EditableCell } from "@/components/EditableCell";
import { ExpandableSectionLayout } from "@/components/ExpandableSectionLayout";
import { useExecutorEditActive, useExecutorPlanOverlays, useExecutorRemovedRows, fieldEditOf } from "@/lib/executor-plan-edit";
import { ExecutorRowActions, ExecutorPendingRowActions, ExecutorAddButton } from "@/components/executor/plan-view/ExecutorPlanEditAffordances";
import { ExecutorInviteButton, type ExecutorInviteInfo } from "@/components/ExecutorInviteButton";
import { AddressInput } from "@/components/AddressInput";
import { InstitutionInput } from "@/components/InstitutionInput";
import { ContactInput } from "@/components/ContactInput";
import { FileViewModal } from "@/components/FileViewModal";
import { PendingExtractionBanner } from "@/components/PendingExtractionBanner";
import { FanOutBanner, type FanOutSection } from "@/components/FanOutBanner";
import { FanOutReviewModal, type FanOutSelection } from "@/components/FanOutReviewModal";
import { DocumentSummaryModal, type DocumentSummaryTarget } from "@/components/DocumentSummaryModal";
import { EXPANDABLE_LAYOUT_CONFIG } from "@shared/constants/section-layout-config";
import { sectionDeclaresEmptyStarter, isLegacyBlankRow, isRowHidden, partitionUserRowsFirst, rowIsVisibleUserRecord, isUntouchedSuggestionRow } from "@/lib/table-row-visibility";
import { subscribeUploadComplete } from "@/lib/upload-events";
import { useExtractionJobs } from "@/hooks/useExtractionJobs";

// Attached-document shape carried through fieldDocuments. Superset of
// DocumentSection's DocumentItem ({id, filename}) — adds metadata for FileViewModal.
type FieldDoc = { id: string; filename: string; fileSize?: number | null; mimeType?: string | null; uploadedAt?: string | null; extractedMetadata?: { summary?: string | null; documentType?: string | null } | null };
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { NoClientSelected, LoadingState } from "@/components/StateComponents";
import { DetailDialog, InstructionsSection } from "@/components/DetailDialog";
import { DocumentSection, type DocumentItem } from "@/components/DocumentSection";
import type { ExtractedData } from "@/components/AIExtractionReviewModal";
import type { ExtractionField } from "@/components/DocumentSection";
import { formatExtractionAsNotes } from "@/lib/aiUtils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AlertCircle, AlertTriangle, ArrowLeft, Camera, Check, ChevronDown, ChevronRight, ChevronUp, CircleSlash, DollarSign, ExternalLink, Eye, EyeOff, FileSearch, FileText, GitBranch, GripVertical, Hash, Info, Link, Loader2, Lock, Mail, Paperclip, Pencil, Phone, Plus, PlusCircle, RotateCcw, Search, Settings2, Smartphone, Sparkles, ThumbsDown, ThumbsUp, Trash2, Upload, X as XIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Base64UploadButton } from "@/components/Base64UploadButton";
import { DOCUMENT_ACCEPT } from "@/lib/upload-accept";
import { ConditionRuleEditor } from "@/components/ConditionRuleEditor";
import type { ConditionRule } from "@/components/ConditionRuleEditor";
import {
  Dialog as ConditionDialog,
  DialogContent as ConditionDialogContent,
  DialogHeader as ConditionDialogHeader,
  DialogTitle as ConditionDialogTitle,
  DialogFooter as ConditionDialogFooter,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useGenericRowUpload } from "@/hooks/useGenericRowUpload";
import { ConfirmExtractionModal } from "@/components/ConfirmExtractionModal";
import { NO_EXTRACTION_TOAST } from "@/lib/extraction-toasts";
import { getIcon } from "@/lib/brandIcons";
import { SendToPhoneDialog } from "@/components/SendToPhoneDialog";
import { WhereIsEverything } from "@/components/WhereIsEverything";
import { ProtectYourAccess } from "@/components/ProtectYourAccess";
import { useEstateReadyScore } from "@/hooks/useEstateReadyScore";
import { AutofillHoneypot } from "@/components/AutofillHoneypot";
import { getAffordances, rendersExpanded, HIDDEN_SECTIONS, INLINE_CARD_SECTIONS } from "@shared/constants/section-affordances";
import {
  GATE_SECTIONS,
  SECTION_NA_GATE,
  CATEGORY_LEVEL_GATE,
  CATEGORY_OR_GATES,
  // s162 (CATEGORY-GATE-DEFORK-1): the ONE gate predicate — this page no longer reimplements it.
  isSectionAutoNA,
  type SituationFlags,
  ABOUT_YOU_GATE_FIELDS,
  ABOUT_YOU_CAPTURE_FLAG_FIELDS,
  ABOUT_YOU_TOGGLE_HINTS,
  GATE_DISPLAY_NAME,
  MANUAL_NA_ELIGIBLE_SECTIONS,
  isManualNaEligibleSection,
  type AutoNaGate,
} from "@shared/constants/section-gating";
import { SectionNaConfirmDialog, needsNaConfirm } from "@/components/SectionNaConfirmDialog";
import { SectionNotApplicable } from "@/components/SectionNotApplicable";
import { useClientSectionNa } from "@/hooks/useClientSectionNa";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { isCategoryAField } from "@/lib/data-classification";
import { isSectionComplete, sectionProgress, completionSectionOf, type SectionProgressResult } from "@/lib/section-completion";
import { ESSENTIAL_SECTION_SLUGS } from "@shared/readiness";
import { computeSectionStatus, sectionHeaderBadge, type SectionStatusResult } from "@/lib/section-status";
import { evaluateShowIf, type ShowIfCondition } from "@/lib/evaluate-show-if";
import { CaptureUpstreamRenderer } from "@/components/CaptureUpstreamRenderer";
import { SavedElsewhereNotice } from "@/components/SavedElsewhereNotice";
import { SpecifyOnOtherInput } from "@/components/SpecifyOnOtherInput";
import { captureForToggle, deriveOwnsBusiness } from "@shared/constants/capture-upstream";
import { FIELD_KEYS } from "@shared/constants/fields";
import { sectionHelperText } from "@/lib/section-helper-text";
import { isLockedIdentityField, formatPreviewValue, composeSectionPreview } from "@/lib/section-data-preview";
import { identityKeysFromFields, composeRecordLabel } from "@shared/constants/record-fields";
import { SensitiveFieldInput } from "@/components/SensitiveFieldInput";
import { useGateClearConfirm, GateClearCancelledError } from "@/components/GateClearConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { openFeedbackPanel } from "@/components/FeedbackWidget";
import type {
  Client,
  GenericCategory,
  GenericSection,
  GenericField,
  GenericEntry,
  GenericDocument,
} from "@shared/schema";
import { isLockedBuiltIn, lockedBuiltInRowId, isSeededDefaultRow } from "@shared/constants/locked-rows";
import { TOP_ZONE_SCAFFOLDS } from "@shared/constants/top-zone-scaffolds";
import { CATEGORY_SLUGS } from '@shared/constants/categories';
import { SECTION_SLUGS } from '@shared/constants/sections';
import {
  getSectionGroups,
  FIELD_PLACEHOLDERS,
  getDefaultColSpan,
  getColSpanClass,
  CARD_HIDDEN_FIELD_KEYS,
} from '@shared/constants/section-groups';
import { SectionSaveStatus } from '@/components/SectionSaveStatus';
import { VehicleInsuranceSubCard } from '@/components/VehicleInsuranceSubCard';
import { InsuresPicker } from '@/components/InsuresPicker';
import { CLAY, FONT, SAGE, WARM } from "@shared/brand";
import { formatPlannerCurrency } from "@/lib/estate-format";

interface GenericCategoryPageProps {
  clientData: Client | null;
  categorySlug: string;
  readOnly?: boolean;
}

interface SectionWithFields extends GenericSection {
  fields: GenericField[];
}

interface EntryMap {
  [fieldId: string]: GenericEntry;
}


type InstructionPart = { type: 'text' | 'link'; content: string; href?: string };

function parseInstructions(instructions: string): InstructionPart[] {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts: InstructionPart[] = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(instructions)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: instructions.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'link', content: match[1], href: match[1] });
    lastIndex = match.index + match[1].length;
  }

  if (lastIndex < instructions.length) {
    parts.push({ type: 'text', content: instructions.slice(lastIndex) });
  }

  return parts;
}

// `evaluateShowIf` + its condition type now live in a shared util so the
// expandable drawer (DB-29) evaluates conditions identically to the card/table
// paths. Local alias keeps the existing call-site casts readable.
type FieldConditions = ShowIfCondition;

function InstructionsTooltip({ instructions }: { instructions: string }) {
  const parts = parseInstructions(instructions);
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help ml-1.5 inline-flex items-center">
          <Info className="h-3.5 w-3.5 text-blue-500 hover:text-blue-600" />
        </span>
      </TooltipTrigger>
      <TooltipContent 
        side="right" 
        className="max-w-xs text-sm bg-popover border shadow-lg p-3"
        sideOffset={5}
      >
        <div className="space-y-1">
          {parts.map((part, idx) =>
            part.type === "link" ? (
              <a
                key={idx}
                href={part.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline inline-flex items-center gap-0.5"
                onClick={(e) => e.stopPropagation()}
              >
                {part.content.length > 40 ? `${part.content.slice(0, 40)}...` : part.content}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <span key={idx}>{part.content}</span>
            )
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}


// Stable empty set for sections with no open drawers (avoids re-creating one per
// render while keeping ExpandableSectionLayout in controlled-expand mode).
const EMPTY_EXPANDED_ROWS: ReadonlySet<string> = new Set<string>();

// Passive per-category-page thumbs (s102 DB-16 1c; moved off per-section in the DB-16
// polish — one prompt per page, not per expanded section). A quiet "Was this page
// clear?" line at the bottom of a category page, F&F-only. 👍 / 👎 write a kind='thumbs'
// row carrying the CATEGORY slug + sentiment via the shared feedback endpoint; 👎 also
// opens the feedback widget pre-filled. Passive: failures are swallowed (never blocks
// the planner). Cleared at launch with the F&F flag.
function CategoryThumbs({ categorySlug, categoryName }: { categorySlug: string; categoryName: string }) {
  const { user } = useAuth();
  const [voted, setVoted] = useState<"up" | "down" | null>(null);
  // D-170.6: converted from a raw apiRequest. Class A — a thumbs vote INSERTs a feedback row.
  // Registered ABOVE the F&F early-return for the rules of hooks.
  const voteFlight = useSingleFlight();
  const voteMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      await apiRequest("POST", "/api/feedback", payload),
  });
  if (!user?.isFAndF) return null;

  const vote = async (sentiment: "up" | "down") => {
    setVoted(sentiment);
    try {
      await voteFlight.run(() =>
        voteMutation.mutateAsync({
          kind: "thumbs",
          sectionSlug: categorySlug, // feedback row stores the category slug (DB-16 polish)
          sentiment,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
        }),
      );
    } catch {
      // Passive — never surface an error to the planner.
    }
    if (sentiment === "down") {
      openFeedbackPanel({ type: "confusing", body: `Page: ${categoryName}\n\n` });
    }
  };

  if (voted) {
    return (
      <p className="pt-6 pb-2 text-center text-[11px] text-warm-taupe" data-testid={`category-thumbs-thanks-${categorySlug}`}>
        Thanks for the feedback.
      </p>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 pt-6 pb-2 text-[11px] text-warm-taupe" data-testid={`category-thumbs-${categorySlug}`}>
      <span>Was this page clear?</span>
      <button
        type="button"
        aria-label="Yes, this page was clear"
        data-testid={`category-thumbs-up-${categorySlug}`}
        onClick={() => vote("up")}
        className="rounded p-0.5 hover:text-foreground hover:bg-primary/10 transition-colors"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label="No, this page was confusing"
        data-testid={`category-thumbs-down-${categorySlug}`}
        onClick={() => vote("down")}
        className="rounded p-0.5 hover:text-foreground hover:bg-primary/10 transition-colors"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function SortableSectionRow({
  section,
  isExpanded,
  onToggle,
  colSpan,
  readOnly,
  onCustomize,
  isCustomizing = false,
  hasSummary,
  onViewSummary,
  noChevron = false,
  anchorId,
  progress = null,
  status,
  dataPreview,
  allowReorder = false,
  onMarkNotApplicable,
}: {
  section: SectionWithFields;
  isExpanded: boolean;
  onToggle: () => void;
  colSpan: number;
  readOnly?: boolean;
  // s171 DS-1 Block B: `onAddRow` / `addRowDisabled` REMOVED from this component. The header
  // Add-Row button they fed was retired in f630784d (the unified-category restructure), which
  // replaced a working `onAddRow={… handleAddRow …}` with `onAddRow={undefined}` — superseded by
  // the in-table Add Row and the expandable layout's own control, not abandoned by accident.
  // `ExpandableSectionLayout` declares its OWN identically-named props and is live; these were
  // never shared. The live D-169.10 affordance coverage sits on the in-table button.
  onCustomize?: () => void;
  isCustomizing?: boolean;
  hasSummary?: boolean;
  onViewSummary?: () => void;
  noChevron?: boolean;
  anchorId?: string;
  /** Shared section progress (`{complete, filled, total}` | null). Feeds the single
   *  header badge (`sectionHeaderBadge`) — never recomputed here (G7). */
  progress?: SectionProgressResult;
  /** s97: header status (complete / attention / not_started / na / no_status). The
   *  single source for the one right-side badge (D-99.1). */
  status?: SectionStatusResult;
  /** s99 (D-99.2): muted preview of recorded row names ("TD Canada Trust · RBC"),
   *  shown in the middle of the collapsed header. Empty → nothing rendered. */
  dataPreview?: string;
  /** s84: show the drag-reorder grip ONLY for custom (planner-created) categories.
   *  Built-in template sections render in the locked-IA `genericSections.sortOrder`;
   *  per-client reorder is dead state (nothing reads `client_section_order`), so the
   *  grip on built-in sections was a broken affordance. Drag machinery is left intact
   *  for when custom-category reorder is wired. */
  allowReorder?: boolean;
  /** s85 (D-84.8): when set, render a "Not applicable" header action that tucks an
   *  OPTIONAL non-gating section away (manual N/A). Absent for ineligible sections,
   *  read-only views, and sections already marked N/A. */
  onMarkNotApplicable?: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  // s99 (D-99.1): exactly ONE status badge per collapsed row, derived from the
  // shared status + progress (no recompute — G7).
  const badge = status ? sectionHeaderBadge(status, progress ?? null) : { kind: "none" as const, label: null };
  // D-99.3: the N/A × shows only on eligible, not-complete, not-already-NA sections
  // (the caller passes onMarkNotApplicable only for eligible + non-NA sections).
  const showNaDismiss = !!onMarkNotApplicable && badge.kind !== "complete" && badge.kind !== "na";

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // D-99.1 #5: a section that "Doesn't apply" dims the whole row to 50%.
    opacity: isDragging ? 0.5 : badge.kind === "na" ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      id={anchorId}
      style={style}
      className="bg-muted/30 cursor-pointer hover:bg-[#ECE7DC] transition-colors scroll-mt-24"
      onClick={onToggle}
    >
      <TableCell colSpan={colSpan} className="py-1">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 font-medium min-w-0 shrink-0">
            {!readOnly && allowReorder && (
              <span
                {...attributes}
                {...listeners}
                onClick={(e) => e.stopPropagation()}
                className="cursor-grab"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </span>
            )}
            {!noChevron && (isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            ))}
            {getIcon(section.icon)}
            <span>{section.name}</span>
            {/* s83: sections feeding the Ten Essentials carry a sage badge so the
                "why does this matter" signal sits next to the section name. Derived
                from the shared ESSENTIAL_SECTION_SLUGS (G7), never a local list. */}
            {ESSENTIAL_SECTION_SLUGS.has(section.slug) && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide shrink-0"
                style={{ backgroundColor: SAGE[200], color: SAGE[800] }}
                data-testid={`section-essential-${section.slug}`}
              >
                Essential
              </span>
            )}
          </div>

          {/* D-99.2: data preview fills the middle with recorded record names.
              Empty sections render nothing (no italic placeholder). */}
          <div
            className="flex-1 min-w-0 text-xs truncate"
            style={{ color: WARM.taupe }}
            title={dataPreview || undefined}
            data-testid={`section-preview-${section.slug}`}
          >
            {dataPreview}
          </div>

          {/* Right cluster: exactly ONE status badge (D-99.1), the N/A × (D-99.3),
              then expanded-only action buttons. */}
          <div
            className="flex items-center gap-3 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {badge.kind !== "none" && badge.label && (() => {
              if (badge.kind === "complete") {
                return (
                  <span
                    className="inline-flex items-center gap-1 shrink-0 text-[12px] font-medium rounded-[6px]"
                    style={{ backgroundColor: SAGE[50], color: SAGE[600], padding: "3px 10px" }}
                    data-testid={`section-status-complete-${section.slug}`}
                  >
                    <Check className="h-3.5 w-3.5" />
                    {badge.label}
                  </span>
                );
              }
              if (badge.kind === "attention") {
                return (
                  <span
                    className="inline-flex items-center gap-1 shrink-0 text-[12px] font-medium rounded-[6px]"
                    style={{ backgroundColor: CLAY[100], color: CLAY[600], padding: "3px 10px" }}
                    data-testid={`section-status-attention-${section.slug}`}
                  >
                    <AlertCircle className="h-3.5 w-3.5" />
                    {badge.label}
                  </span>
                );
              }
              if (badge.kind === "in_progress") {
                return (
                  <span
                    className="shrink-0 text-xs font-medium tabular-nums"
                    style={{ color: WARM.taupe }}
                    data-testid={`section-status-in_progress-${section.slug}`}
                  >
                    {badge.label}
                  </span>
                );
              }
              if (badge.kind === "na") {
                return (
                  <span
                    className="inline-flex items-center gap-1 shrink-0 text-[12px] font-medium italic"
                    style={{ color: "#B4B2A9" }}
                    data-testid={`section-status-na-${section.slug}`}
                  >
                    <CircleSlash className="h-3.5 w-3.5" />
                    {badge.label}
                  </span>
                );
              }
              // not_started — muted text, no icon.
              return (
                <span
                  className="shrink-0 text-xs font-medium"
                  style={{ color: WARM.taupe }}
                  data-testid={`section-status-not_started-${section.slug}`}
                >
                  {badge.label}
                </span>
              );
            })()}

            {/* D-99.3: section-level "doesn't apply" — a muted × (subsumes the old
                Hide Category link). Always visible (mobile-safe), eligible sections
                only, hidden when complete / already N/A. */}
            {showNaDismiss && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onMarkNotApplicable!(); }}
                title="This doesn't apply to me"
                aria-label={`Mark ${section.name} as not applicable`}
                data-testid={`button-mark-na-${section.id}`}
                className="shrink-0 hover:opacity-100 transition-opacity"
                style={{ opacity: 0.4 }}
              >
                <XIcon className="h-3.5 w-3.5" style={{ color: WARM.taupe }} />
              </button>
            )}

            {onCustomize && isExpanded && (
              <Button
                size="sm"
                variant={isCustomizing ? "default" : "ghost"}
                className={`h-7 px-2 text-xs font-medium ${isCustomizing ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
                onClick={onCustomize}
                data-testid={`button-customize-${section.id}`}
              >
                {isCustomizing ? (
                  <>
                    <Check className="h-3.5 w-3.5 mr-1" />
                    Done
                  </>
                ) : (
                  <>
                    <Settings2 className="h-3.5 w-3.5 mr-1" />
                    Customize
                  </>
                )}
              </Button>
            )}
            {hasSummary && onViewSummary && isExpanded && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.stopPropagation(); onViewSummary(); }}
              >
                <FileText className="h-4 w-4" aria-hidden="true" /> View Summary
              </Button>
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

function fieldWidthToColSpan(width: string | null | undefined, fieldType?: string): string {
  if (width === "half") return "col-span-6";
  if (width === "third") return "col-span-4";
  if (width === "full") return "col-span-12";
  // Field-type aware defaults when layoutWidth is not set
  if (!width && fieldType) {
    const n = getDefaultColSpan(fieldType);
    return `col-span-${n}`;
  }
  return "col-span-12";
}

function CurrencyFieldInput({
  fieldId,
  value,
  onChange,
  onBlur,
  placeholder,
  readOnly,
}: {
  fieldId: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  placeholder: string;
  readOnly?: boolean;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const displayValue = isFocused || !value ? value : formatPlannerCurrency(value);

  return (
    <div className="relative">
      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => { setIsFocused(false); onBlur(); }}
        placeholder={placeholder || "0.00"}
        readOnly={readOnly}
        className="pl-9 text-sm"
        data-testid={`input-card-${fieldId}`}
      />
    </div>
  );
}

function PasswordCardInput({ field, value, onChange, onBlur, readOnly, placeholder }: {
  field: GenericField; value: string; onChange: (v: string) => void; onBlur: () => void; readOnly?: boolean; placeholder: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        readOnly={readOnly}
        className="text-sm pr-9"
        id={`input-card-${field.id}`}
        name={field.fieldKey || ''}
        data-testid={`input-card-${field.id}`}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        tabIndex={-1}
        aria-label={show ? "Hide" : "Show"}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function SortableCustomizeFieldRow({
  field,
  isHidden,
  renamingFieldId,
  onToggleHide,
  onRename,
  onRenameSubmit,
  onRenameCancel,
  onDelete,
}: {
  field: GenericField;
  isHidden: boolean;
  renamingFieldId: string | null;
  onToggleHide: () => void;
  onRename: () => void;
  onRenameSubmit: (name: string) => void;
  onRenameCancel: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const isRenaming = renamingFieldId === field.id;

  return (
    <div ref={setNodeRef} style={style} className={`flex items-center gap-2 px-4 py-2 border-b last:border-b-0 ${isHidden ? 'opacity-50' : ''}`}>
      {/* Drag handle */}
      <span {...attributes} {...listeners} className="cursor-grab text-muted-foreground hover:text-foreground shrink-0">
        <GripVertical className="h-4 w-4" />
      </span>

      {/* Hide/unhide toggle */}
      <button
        type="button"
        onClick={onToggleHide}
        className={`shrink-0 ${isHidden ? 'text-muted-foreground' : 'text-foreground'}`}
        title={isHidden ? "Restore this item" : "Hide this item"}
      >
        {isHidden ? <EyeOff className="h-4 w-4 text-amber-500" /> : <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />}
      </button>

      {/* Field name / rename input */}
      {isRenaming ? (
        <Input
          autoFocus
          defaultValue={field.name}
          className="h-7 text-sm flex-1"
          placeholder="Item name"
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && v !== field.name) onRenameSubmit(v);
            else onRenameCancel();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') { e.preventDefault(); onRenameCancel(); }
          }}
        />
      ) : (
        <span className={`flex-1 text-sm ${isHidden ? 'line-through text-muted-foreground' : ''}`}>{field.name}</span>
      )}

      {/* Custom field actions */}
      {field.isCustom && !isRenaming && (
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={onRename} title="Rename" className="text-muted-foreground hover:text-foreground p-1 rounded">
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={onDelete} title="Delete" className="text-muted-foreground hover:text-destructive p-1 rounded">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {!field.isCustom && (
        <span className="text-xs text-muted-foreground shrink-0 pr-1">built-in</span>
      )}
    </div>
  );
}

// ── Signature canvas input ──────────────────────────────────────────────────
function SignatureInput({ value, onChange, onBlur, readOnly, fieldId }: {
  value: string; onChange: (v: string) => void; onBlur: () => void; readOnly?: boolean; fieldId: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (value?.startsWith("data:image/")) {
      const img = new Image();
      img.onload = () => { ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0); };
      img.src = value;
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [value]);

  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ("touches" in e) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    if (readOnly) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    setIsDrawing(true);
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    e.preventDefault();
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || readOnly) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "hsl(var(--foreground))";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    e.preventDefault();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange(canvas.toDataURL("image/png"));
    onBlur();
  };

  return (
    <div className="space-y-2">
      <div
        className={`relative border rounded-md overflow-hidden bg-background ${readOnly ? "opacity-60" : "cursor-crosshair"}`}
        style={{ touchAction: "none" }}
      >
        <canvas
          ref={canvasRef}
          width={600}
          height={120}
          className="w-full"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          data-testid={`input-card-${fieldId}`}
        />
        {!value && !readOnly && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-sm text-muted-foreground">Draw your signature here</span>
          </div>
        )}
      </div>
      {!readOnly && (
        <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => {
            const canvas = canvasRef.current;
            if (canvas) canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
            onChange("");
            onBlur();
          }}
          type="button"
        >
          Clear
        </Button>
      )}
    </div>
  );
}

// AddressInput (structured-address editor) extracted to
// @/components/AddressInput so EditableCell + WizardStepField can reuse it.

// ── Repeating group input ────────────────────────────────────────────────────
type SubFieldDef = { key: string; label: string };
type RepeatRow = Record<string, string>;

export function RepeatingGroupInput({ field, value, onChange, onBlur, readOnly }: {
  field: GenericField; value: string; onChange: (v: string) => void; onBlur: () => void; readOnly?: boolean;
}) {
  const uid = useId();
  const subFields: SubFieldDef[] = useMemo(() => {
    const opts = (field.options as { subFields?: SubFieldDef[] } | null)?.subFields;
    return opts && opts.length > 0 ? opts : [{ key: "value", label: "Value" }];
  }, [field.options]);

  const rows: RepeatRow[] = useMemo(() => {
    try {
      const parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [value]);

  const pushRows = (newRows: RepeatRow[]) => { onChange(JSON.stringify(newRows)); onBlur(); };

  if (readOnly) {
    if (rows.length === 0) return <span className="text-sm text-muted-foreground">—</span>;
    return (
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="rounded border p-2 text-sm space-y-1">
            {subFields.map(sf => (
              <div key={sf.key} className="flex gap-2">
                <span className="text-muted-foreground shrink-0">{sf.label}:</span>
                <span>{row[sf.key] || "—"}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((row, rowIdx) => (
        <div key={rowIdx} className="rounded border p-3 pr-10 space-y-2 relative bg-background">
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: `repeat(${Math.min(subFields.length, 3)}, 1fr)` }}
          >
            {subFields.map(sf => (
              <div key={sf.key} className="space-y-1">
                <Label className="text-xs text-muted-foreground" htmlFor={`${uid}-${rowIdx}-${sf.key}`}>{sf.label}</Label>
                <Input id={`${uid}-${rowIdx}-${sf.key}`}
                  value={row[sf.key] || ""}
                  onChange={(e) => {
                    const next = rows.map((r, i) => i === rowIdx ? { ...r, [sf.key]: e.target.value } : r);
                    onChange(JSON.stringify(next));
                  }}
                  onBlur={onBlur}
                  className="text-sm h-8"
                  placeholder={sf.label}
                />
              </div>
            ))}
          </div>
          <Button
            variant="ghost" size="icon" type="button"
            className="absolute top-2 right-2 h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={() => pushRows(rows.filter((_, i) => i !== rowIdx))}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline" size="sm" type="button"
        className="h-8 gap-1.5 text-xs"
        onClick={() => pushRows([...rows, Object.fromEntries(subFields.map(sf => [sf.key, ""]))])}
      >
        <PlusCircle className="h-3.5 w-3.5" />
        Add Another
      </Button>
    </div>
  );
}

export function CardFieldInput({ // s236: exported for the EMAIL-FORMAT-1 surface test
  field,
  value,
  onChange,
  onBlur,
  readOnly,
  uploadProps,
  clientData,
}: {
  field: GenericField;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  readOnly?: boolean;
  uploadProps?: {
    onUploadFile: (file: File) => Promise<void>;
    docCount?: number;
  };
  clientData?: { id: string; secureFileSetupAt?: Date | null } | null;
}) {
  // Category A intercept — must come before all fieldType-based branches.
  // These fields are never stored on the server; they live only in the planner's
  // Secure Information File. SensitiveFieldInput handles masking, setup trigger,
  // and the dual-path (Chrome/Edge silent-save / Safari-Firefox banner) save flow.
  if (isCategoryAField(field) && !readOnly && clientData) {
    return (
      <SensitiveFieldInput
        field={field}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        clientId={clientData.id}
        secureFileSetupAt={clientData.secureFileSetupAt ?? null}
      />
    );
  }

  const placeholder = field.placeholder || (() => {
    const n = field.name;
    switch (field.fieldType) {
      case "email": return `e.g. name@example.com`;
      case "phone": return `e.g. (555) 000-0000`;
      case "url": return `https://`;
      case "currency": return `$0.00`;
      case "number": return `0`;
      case "date": return ``;
      case "multiline":
      case "notes": return `Enter ${n.toLowerCase()}…`;
      default: return `Enter ${n.toLowerCase()}`;
    }
  })();

  if (field.fieldType === "multiline" || field.fieldType === "notes") {
    return (
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        className="resize-y min-h-[80px] text-sm"
        readOnly={readOnly}
        id={`input-card-${field.id}`}
        name={field.fieldKey || ''}
        data-testid={`input-card-${field.id}`}
      />
    );
  }

  if (field.fieldType === "checkbox") {
    return (
      <div className="flex items-center gap-2 pt-1">
        <input
          type="checkbox"
          checked={value === "true"}
          onChange={(e) => !readOnly && onChange(e.target.checked ? "true" : "false")}
          disabled={readOnly}
          className="h-4 w-4 rounded border-input cursor-pointer"
          id={`input-card-${field.id}`}
        name={field.fieldKey || ''}
        data-testid={`input-card-${field.id}`}
        />
        <span className="text-sm text-muted-foreground">{value === "true" ? "Yes" : "No"}</span>
      </div>
    );
  }

  if (field.fieldType === "password" || field.fieldType === "pin") {
    return <PasswordCardInput field={field} value={value} onChange={onChange} onBlur={onBlur} readOnly={readOnly} placeholder={placeholder} />;
  }

  if (field.fieldType === "number") {
    return (
      <div className="relative">
        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder || "0"}
          readOnly={readOnly}
          className="pl-9 text-sm"
          id={`input-card-${field.id}`}
        name={field.fieldKey || ''}
        data-testid={`input-card-${field.id}`}
        />
      </div>
    );
  }

  if (field.fieldType === "currency") {
    return (
      <CurrencyFieldInput
        fieldId={field.id}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        readOnly={readOnly}
      />
    );
  }

  if (field.fieldType === "phone") {
    return (
      <div className="relative">
        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="tel"
          inputMode="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => { onChange(formatPhone(value)); onBlur(); }}
          placeholder={placeholder || "(555) 000-0000"}
          readOnly={readOnly}
          className="pl-9 text-sm"
          id={`input-card-${field.id}`}
        name={field.fieldKey || ''}
        data-testid={`input-card-${field.id}`}
        />
      </div>
    );
  }

  if (field.fieldType === "email") {
    return (
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="email"
          inputMode="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder || "name@example.com"}
          readOnly={readOnly}
          autoComplete="new-password"
          className="pl-9 text-sm"
          id={`input-card-${field.id}`}
          name={`field-cell-${field.fieldKey || ''}`}
          data-testid={`input-card-${field.id}`}
        />
        {/* s236 (EMAIL-FORMAT-1): the quiet line under the box; the value saves as typed. */}
        <EmailHint value={value} testId={`input-card-${field.id}-email-hint`} />
      </div>
    );
  }

  if (field.fieldType === "url") {
    return (
      <div className="relative">
        <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="url"
          inputMode="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder || "https://"}
          readOnly={readOnly}
          className="pl-9 text-sm"
          id={`input-card-${field.id}`}
        name={field.fieldKey || ''}
        data-testid={`input-card-${field.id}`}
        />
      </div>
    );
  }

  if (field.fieldType === "select") {
    const choices = (field.options as { choices?: string[] } | null)?.choices ?? [];
    if (readOnly) {
      return <span className="text-sm">{value || <span className="text-muted-foreground">—</span>}</span>;
    }
    // FIELD-AUDIT-1 Phase 3 item 5 (0c, D-153.3/D-154.5): 2–4 options → radio; 5+ → dropdown.
    // Card primitive only (all card-layout selects inherit); value strings preserved exactly.
    // Unanswered (value === "") highlights NO option — a real "not yet answered" state.
    if (choices.length >= 2 && choices.length <= 4) {
      return (
        <RadioOptions
          name={`radio-${field.id}`}
          value={value}
          options={choices.map((c) => ({ value: c, label: c }))}
          onChange={(v) => { onChange(v); onBlur(); }}
          id={`input-card-${field.id}`}
          testIdPrefix={`input-card-${field.id}`}
        />
      );
    }
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        id={`input-card-${field.id}`}
        name={field.fieldKey || ''}
        data-testid={`input-card-${field.id}`}
      >
        <option value="">Select...</option>
        {choices.map((c: string) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    );
  }

  if (field.fieldType === "date") {
    return (
      <Input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        readOnly={readOnly}
        className="text-sm"
        id={`input-card-${field.id}`}
        name={field.fieldKey || ''}
        data-testid={`input-card-${field.id}`}
      />
    );
  }

  if (field.fieldType === "yes_no") {
    // FIELD-AUDIT-1 (0e + D-154 2a/2b): explicit Yes/No via the shared RadioOptions primitive —
    // unboxed, horizontal, whole-label click target, with a NULL unanswered state (value "" → neither
    // checked, distinct from an explicit "No"). Values stay "true"/"false" — gates, showIf, and
    // capture-upstream unchanged. (Replaced the s154 segmented buttons + the original <Switch>.)
    return (
      <RadioOptions
        name={`radio-${field.id}`}
        value={value}
        options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
        onChange={(v) => { onChange(v); onBlur(); }}
        readOnly={readOnly}
        id={`input-card-${field.id}`}
        testIdPrefix={`input-card-${field.id}`}
      />
    );
  }

  if (field.fieldType === "signature") {
    return <SignatureInput value={value} onChange={onChange} onBlur={onBlur} readOnly={readOnly} fieldId={field.id} />;
  }

  if (field.fieldType === "address") {
    return <AddressInput value={value} onChange={onChange} onBlur={onBlur} readOnly={readOnly} fieldId={field.id} />;
  }

  // INSTITUTION-TYPEAHEAD-1 (D-202.3) — the card path renders the control directly, the same
  // way it does for address and signature. The table and drawer paths reach it through
  // EditableCell's own `institution` branch, so this is the only extra render site.
  if (field.fieldType === "institution") {
    return <InstitutionInput value={value} onChange={onChange} onBlur={onBlur} readOnly={readOnly} fieldId={field.id} placeholder={field.placeholder ?? undefined} />;
  }

  // PEOPLE-RECORD-1 (D-202.1) — the institution branch's twin on the card path, for the same
  // reason: table and drawer reach the control through EditableCell, so this is the only extra
  // render site. It passes `clientData?.id` because the suggestion list and the mint are BOTH
  // per-client; without it the control still accepts and saves free text, which is the correct
  // degradation rather than a broken cell.
  if (field.fieldType === "contact") {
    return <ContactInput value={value} onChange={onChange} onBlur={onBlur} readOnly={readOnly} fieldId={field.id} clientId={clientData?.id} placeholder={field.placeholder ?? undefined} />;
  }

  if (field.fieldType === "file_upload") {
    if (readOnly) {
      return (
        <div className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
          <Paperclip className="h-4 w-4" />
          <span>{uploadProps?.docCount ? `${uploadProps.docCount} file(s) attached` : "No files attached"}</span>
        </div>
      );
    }
    if (uploadProps) {
      return (
        <div className="flex items-center gap-3">
          <Base64UploadButton
            onUploadFile={uploadProps.onUploadFile}
            buttonVariant="outline"
            buttonSize="sm"
          >
            <Upload className="h-4 w-4 mr-2" />
            Attach Files
          </Base64UploadButton>
          {uploadProps.docCount ? (
            <span className="text-xs text-muted-foreground">{uploadProps.docCount} file(s) attached</span>
          ) : null}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
        <Paperclip className="h-4 w-4" />
        <span>Attach files using the Documents section below</span>
      </div>
    );
  }

  if (field.fieldType === "slider") {
    const opts = (field.options as { min?: number; max?: number; step?: number } | null) ?? {};
    const min = opts.min ?? 0;
    const max = opts.max ?? 100;
    const step = opts.step ?? 1;
    const numValue = parseFloat(value) || min;
    return (
      <div className="space-y-3 pt-1" id={`input-card-${field.id}`}
        data-testid={`input-card-${field.id}`}>
        <Slider
          value={[numValue]}
          min={min}
          max={max}
          step={step}
          disabled={readOnly}
          onValueChange={([v]) => onChange(String(v))}
          onValueCommit={() => onBlur()}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatPlannerCurrency(String(min))}</span>
          <span className="font-medium text-foreground text-sm">{formatPlannerCurrency(value || String(min))}</span>
          <span>{formatPlannerCurrency(String(max))}</span>
        </div>
      </div>
    );
  }

  if (field.fieldType === "repeating_group") {
    return <RepeatingGroupInput field={field} value={value} onChange={onChange} onBlur={onBlur} readOnly={readOnly} />;
  }

  if (process.env.NODE_ENV !== 'production') {
    // Intentionally hit by `text` and `file_ref`. Any other fieldType here
    // means a new schema enum value was added without a matching branch in
    // CardFieldInput. See architecture-map.md §4 / RB-13.
    const KNOWN_FALLTHROUGH = new Set(['text', 'file_ref']);
    if (!KNOWN_FALLTHROUGH.has(field.fieldType ?? '')) {
      console.warn(
        `[CardFieldInput] Unhandled fieldType "${field.fieldType}" rendering as text input (field: ${field.name} / ${field.id}). Add an explicit branch in CardFieldInput.`
      );
    }
  }

  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      readOnly={readOnly}
      autoComplete="new-password"
      className="text-sm"
      id={`input-card-${field.id}`}
      name={`field-cell-${field.fieldKey || ''}`}
      data-testid={`input-card-${field.id}`}
    />
  );
}

function toTitleCase(str: string): string {
  return str.replace(/\w+/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase());
}

// NO_EXTRACTION_TOAST now lives in @/lib/extraction-toasts (shared with the
// guided wizard so both surfaces show one copy of the "no fields found" state — s76).

export default function GenericCategoryPage({
  clientData,
  categorySlug,
  readOnly 
}: GenericCategoryPageProps) {
  const { toast } = useToast();
  const { user } = useAuth();

  // EXECUTOR-WRITE-1 Phase 4 (s144): the executor's post-death per-category plan view becomes
  // editable IN PLACE (correct a fact, remove/undo a record, add a discovered asset). Gated on
  // readOnly + executor role + verified death; the affordances below all no-op otherwise, so the
  // planner/advisor render stays byte-identical. D-143.7 split model; decision #1 (this surface only).
  const executorEditActive = useExecutorEditActive(clientData?.id, !!readOnly && user?.role === "executor");

  // ── Situational gate (auto-NA) plumbing (session 73) ──────────────────────
  // The 4 locked gates live on `clients` columns. About You's yes/no toggles and
  // the section "Mark as applicable" affordance both write here via situation-flags.
  const situationFlagMutation = useMutation({
    mutationFn: async (payload: Record<string, boolean>) => {
      const res = await apiRequest("PATCH", `/api/clients/${clientData!.id}/situation-flags`, payload);
      if (!res.ok) throw new Error((await res.json()).message || "Failed to update");
      return res.json();
    },
    // Optimistically flip the gate in the ["/api/clients"] cache — the exact query
    // `clientData` (and the controlled Quick-facts Switch's `checked`) read from — so
    // the toggle reflects the change IMMEDIATELY, both directions, no reload. A post-
    // success invalidate alone (async refetch) did not re-sync the controlled Radix
    // Switch once the old confirm modal's re-render was removed (s79 / a1091a0 fix).
    onMutate: async (payload: Record<string, boolean>) => {
      // The read is user-scoped (Issue B, s84) so get/setQueryData MUST use the same
      // exact key — `user?.id` here is the same authenticated user ClientDashboard
      // fetched under. cancelQueries stays bare (prefix-matches the scoped key).
      await queryClient.cancelQueries({ queryKey: ["/api/clients"] });
      const clientsKey = ["/api/clients", user?.id];
      const previous = queryClient.getQueryData<Client[]>(clientsKey);
      queryClient.setQueryData<Client[]>(clientsKey, (old) =>
        old?.map((c) => (c.id === clientData?.id ? { ...c, ...payload } : c)),
      );
      return { previous };
    },
    onError: (error: any, _payload, context: any) => {
      // Roll the optimistic change back on failure.
      if (context?.previous) queryClient.setQueryData(["/api/clients", user?.id], context.previous);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
    onSettled: () => {
      // Reconcile with the server (keeps sidebar, section NA state, etc. correct).
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    },
  });
  const markGateApplicable = (gate: AutoNaGate) => situationFlagMutation.mutate({ [gate]: true });

  // ── Manual per-section "Not Applicable" (s85, D-84.8) ─────────────────────
  // A parallel path to the auto-NA gates, for OPTIONAL non-gating sections the
  // planner legitimately has none of (e.g. Powers of Attorney). Marking tucks the
  // section away (SectionNotApplicable + "Bring it back") with no nag / no green
  // check, and drops it from NEXT UP. Eligibility = MANUAL_NA_ELIGIBLE_SECTIONS.
  const sectionNa = useClientSectionNa(clientData?.id);
  // D-177.7 (s177, A2): the essentials speed bump. Marking an ESSENTIAL section
  // "doesn't apply" confirms once in plain language; a non-essential marks immediately.
  // The pending slug IS the dialog's open state — one variable, so the dialog can never
  // be open for a section the handler is not about to mark.
  const [naConfirmSlug, setNaConfirmSlug] = useState<string | null>(null);
  const requestMarkNa = (slug: string) => {
    if (needsNaConfirm(slug)) setNaConfirmSlug(slug);
    else sectionNa.markNa(slug);
  };
  // Both directions apply immediately (s79): turning a gate OFF tucks the section/
  // category away, but with a non-blocking Undo toast instead of a blocking confirm
  // modal — the toggle just works and is reversible (Reade: the modal read as broken).
  const handleGateToggle = (gate: AutoNaGate, next: boolean, label: string) => {
    situationFlagMutation.mutate(
      { [gate]: next },
      {
        onSuccess: () => {
          if (next) return; // turning ON needs no toast
          toast({
            description: `${label} tucked away — your entries are saved.`,
            action: (
              <ToastAction
                altText={`Undo — bring ${label} back`}
                onClick={() => situationFlagMutation.mutate({ [gate]: true })}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1 inline" />
                Undo
              </ToastAction>
            ),
          });
        },
      },
    );
  };

  const [localEdits, setLocalEdits] = useState<Record<string, Partial<GenericEntry>>>({});
  const [tableLocalEdits, setTableLocalEdits] = useState<Record<string, string>>({}); // keyed by `${fieldId}:${rowId}`
  // RADIO-RACE-1 (s154 round 2): SYNCHRONOUS mirrors of the two local-edit maps. A discrete control
  // (RadioOptions / yes_no) fires onChange THEN onBlur in the SAME tick; the blur handler used to read
  // the just-set value from the setState closure, which hadn't committed yet → it saw "no edit",
  // cancelled the debounce, and returned without saving (the "clicked Neither, snapped back to Own" /
  // "clicked Rent, nothing happened" bug). The blur handlers now read the FRESH value from these refs.
  const pendingFieldEditsRef = useRef<Record<string, Partial<GenericEntry>>>({});   // mirrors localEdits
  const pendingTableEditsRef = useRef<Record<string, string>>({});                  // mirrors tableLocalEdits (`${fieldId}:${rowId}`)
  // One-section-at-a-time accordion: at most one section is expanded on the
  // category page. `null` = all collapsed (initial state). Single-section
  // categories (incl. About You, s109) get their lone section auto-expanded on
  // mount by the effect below, but the header chevron still collapses it.
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  // s83: the "Your Executor" accordion row's green check derives from the ERS
  // Jump-to-section (sidebar tree links here with ?section=<slug>).
  const locationSearch = useSearch();
  const jumpScrolledRef = useRef<string | null>(null);
  const jumpExpandedRef = useRef<string | null>(null);
  // PLANNER-LENS-1 (s152): per-field landing guard — once-per "<section>:<field>" so
  // the click-through highlight+scroll fires a single time, not on every re-render.
  const jumpFieldRef = useRef<string | null>(null);
  // (s160) The per-field `LensGapBadge` this page used to feed was REMOVED at D-154.16 —
  // the required star is the sole field marker; motivation copy lives only on the
  // What's-Missing / planner-lens surfaces. The gap set, its memo, and the
  // `usePlannerLens` fetch that backed them are gone with it.
  const sectionsRef = useRef<SectionWithFields[]>([]);
  const [pendingCardInstances, setPendingCardInstances] = useState<Record<string, string[]>>({});
  const [expandedTableRows, setExpandedTableRows] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  
  const [expandedFieldId, setExpandedFieldId] = useState<string | null>(null);
  const [detailField, setDetailField] = useState<GenericField | null>(null);
  const [detailFieldSection, setDetailFieldSection] = useState<SectionWithFields | null>(null);
  const [detailRowId, setDetailRowId] = useState<string | null>(null);
  // Notes save-status for the Detail (Notes & documents) popup (s79) — makes the
  // existing auto-save visible. `armed` guards the card path's shared-upsert watch.
  const [notesSaveStatus, setNotesSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const notesSavedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notesSaveArmedRef = useRef(false);
  const markNotesSaved = () => {
    setNotesSaveStatus("saved");
    if (notesSavedTimerRef.current) clearTimeout(notesSavedTimerRef.current);
    notesSavedTimerRef.current = setTimeout(() => setNotesSaveStatus("idle"), 2000);
  };
  const resetNotesSaveStatus = () => {
    notesSaveArmedRef.current = false;
    if (notesSavedTimerRef.current) clearTimeout(notesSavedTimerRef.current);
    setNotesSaveStatus("idle");
  };
  const [hiddenTableRows, setHiddenTableRows] = useState<Record<string, Set<string>>>({});
  // s99 drawer-stability fix (#1): record-drawer open state lives HERE, in the page
  // (which survives ExpandableSectionLayout's remount), keyed by sectionId. A
  // background refetch that remounts the layout no longer collapses an open drawer.
  const [expandedRowsBySection, setExpandedRowsBySection] = useState<Record<string, Set<string>>>({});
  const setRowExpanded = useCallback((sectionId: string, rowId: string, open: boolean) => {
    setExpandedRowsBySection((prev) => {
      const cur = new Set(prev[sectionId] ?? []);
      if (open) cur.add(rowId);
      else cur.delete(rowId);
      return { ...prev, [sectionId]: cur };
    });
  }, []);
  const [deleteColumnConfirm, setDeleteColumnConfirm] = useState<{ sectionId: string; fieldId: string; fieldName: string } | null>(null);
  const [fieldDocuments, setFieldDocuments] = useState<Record<string, FieldDoc[]>>({});
  const [loadingFieldDocs, setLoadingFieldDocs] = useState<Set<string>>(new Set());
  const [creatingEntryFieldId, setCreatingEntryFieldId] = useState<string | null>(null);
  const [conditionField, setConditionField] = useState<{ field: GenericField; section: SectionWithFields } | null>(null);
  const [conditionDraft, setConditionDraft] = useState<ConditionRule | null>(null);

  // Async extraction (session 90): fileUpload is created before the extraction-job
  // watcher below, so onJobQueued reaches `watch` through this ref (assigned once
  // the watcher exists). Refs keep the hook order stable without reshuffling state.
  const watchExtractionRef = useRef<(jobId: string, ctx: { sectionId: string; rowId: string | null }) => void>(() => {});

  // Per-field / per-row upload — managed by useFileUpload hook
  const fileUpload = useFileUpload({
    clientId: clientData?.id ?? "",
    onSuccess: (result, file, ctx) => {
      const { sectionId, fieldId, rowId } = ctx;
      const editedFieldIds = new Set(Object.keys(localEdits));
      const editedCellKeys = new Set(Object.keys(tableLocalEdits));
      const highlightType: 'confident' | 'uncertain' = (result.confidence ?? 0) > 0.7 ? 'confident' : 'uncertain';
      const newHighlights: Record<string, 'confident' | 'uncertain'> = {};
      for (const f of (result.fields ?? []) as Array<{ fieldId: string }>) {
        const key = rowId ? entryMapKey(f.fieldId, rowId) : f.fieldId;
        if (rowId ? editedCellKeys.has(key) : editedFieldIds.has(f.fieldId)) continue;
        newHighlights[key] = highlightType;
      }
      setFieldHighlights(prev => ({ ...prev, ...newHighlights }));
      setAiSummary(prev => ({
        ...prev,
        [sectionId]: { filled: result.filledCount ?? 0, total: result.totalFields ?? result.fields?.length ?? 0 },
      }));
      if ((result.filledCount ?? 0) > 0) {
        setSummaryBySectionId(prev => ({
          ...prev,
          [sectionId]: { text: result.rawDescription ?? "", documentName: file.name, fieldsFilled: result.filledCount ?? 0, date: new Date().toLocaleString() },
        }));
      }
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "generic-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientData?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "document-summaries", category?.id] });
    },
    onPendingConfirmation: (result, file, ctx) => {
      const { sectionId } = ctx;
      setPendingExtractionBySection(prev => ({
        ...prev,
        [sectionId]: { id: result.pendingId!, documentName: file.name, fieldsFilled: result.filledCount ?? 0, fields: result.fields ?? [], summaryText: result.rawDescription ?? "" },
      }));
      setConfirmExtractionState(prev => ({ ...prev, [sectionId]: result.fields ?? [] }));
    },
    onJobQueued: (jobId, _file, ctx) => {
      // Async (session 90): extraction queued — watch it for completion/failure.
      watchExtractionRef.current(jobId, { sectionId: ctx.sectionId, rowId: ctx.rowId ?? null });
    },
    onError: (err) => {
      // Distinguish a timeout (client AbortController or server 504/503) from a
      // generic failure so the user knows it was slowness, not a hard error.
      const isTimeout = err.name === "UploadTimeoutError" || (err as any).status === 504 || (err as any).status === 503;
      toast(isTimeout
        ? { title: "This is taking longer than expected", description: "Your document may be large or our AI service is busy. Please try again.", variant: "destructive" }
        : { title: "Upload failed", description: err.message, variant: "destructive" });
    },
  });
  const [fieldHighlights, setFieldHighlights] = useState<Record<string, 'confident' | 'uncertain'>>({});
  const [aiSummary, setAiSummary] = useState<Record<string, { filled: number; total: number }>>({});
  const [pendingExtractionBySection, setPendingExtractionBySection] = useState<Record<string, { id: string; documentName: string; fieldsFilled: number; fields: Array<{fieldId: string; name: string; value: string; replacesValue?: string; currentValue?: string}>; summaryText?: string }>>({});
  // s96: per-document AI summary modal (one summary per document, viewable anywhere
  // the document appears). Distinct from the legacy section-level summary below.
  const [viewingSummaryDoc, setViewingSummaryDoc] = useState<DocumentSummaryTarget | null>(null);
  const [viewSummarySectionId, setViewSummarySectionId] = useState<string | null>(null);
  const [summaryBySectionId, setSummaryBySectionId] = useState<Record<string, { text: string; documentName: string; fieldsFilled: number; date: string }>>({});
  const [confirmExtractionState, setConfirmExtractionState] = useState<Record<string, Array<{fieldId: string; name: string; value: string; replacesValue?: string; currentValue?: string}>>>({});
  const [confirmingExtractionSection, setConfirmingExtractionSection] = useState<string | null>(null);
  // Row the review modal is bound to (expandable layout). Null for card layout.
  const [confirmingExtractionRowId, setConfirmingExtractionRowId] = useState<string | null>(null);
  const [savingConfirmedExtraction, setSavingConfirmedExtraction] = useState(false);
  // Per-section row to open+scroll in the expandable layout when an out-of-band
  // (phone/SSE) upload completes — mirrors the in-page upload's expand behavior.
  // The layout clears it via onRowRevealed once handled.
  const [revealRowBySection, setRevealRowBySection] = useState<Record<string, string | null>>({});
  // PLANNER-LENS-1 (s152) landing fix: controlled open-set for the grouped-card sub-card
  // accordion (About You: Identity/Family/Home&Work), keyed by section.id. Absent key →
  // the render falls back to the section's `defaultOpenGroupIds`; the field-landing effect
  // adds the sub-card that holds a deep-linked field so its content mounts + can be tinted.
  const [openGroupsBySection, setOpenGroupsBySection] = useState<Record<string, string[]>>({});
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // ── Cross-section fan-out (session 96) ──
  // When an upload's extraction also yields data for OTHER sections, we surface a
  // banner + per-section "Review & Populate" modal. One active fan-out at a time.
  const [fanout, setFanout] = useState<{ jobId: string; sections: FanOutSection[]; reuploadWarning?: boolean } | null>(null);
  const [fanoutDismissed, setFanoutDismissed] = useState(false);
  const [fanoutOpen, setFanoutOpen] = useState(false);
  const [fanoutPopulatedIds, setFanoutPopulatedIds] = useState<Set<string>>(new Set());
  const [fanoutPopulatingId, setFanoutPopulatingId] = useState<string | null>(null);
  // DB-45 1D: Populate All feedback — a spinner while writing, then a brief
  // "Data added to N sections ✓" success state before the modal closes.
  const [fanoutPopulatingAll, setFanoutPopulatingAll] = useState(false);
  const [fanoutAllDoneCount, setFanoutAllDoneCount] = useState<number | null>(null);

  // s97 Fix 5: "N new rows added" notice per section, after a fan-out populate.
  // Auto-fades after 8s; timers tracked so re-populates reset the countdown cleanly.
  const [newRowsBySection, setNewRowsBySection] = useState<Record<string, number>>({});
  const newRowsTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const clearNewRows = useCallback((sectionId: string) => {
    const t = newRowsTimers.current.get(sectionId);
    if (t) { clearTimeout(t); newRowsTimers.current.delete(sectionId); }
    setNewRowsBySection(prev => { const n = { ...prev }; delete n[sectionId]; return n; });
  }, []);
  const flagNewRows = useCallback((sectionId: string, count: number) => {
    if (count <= 0) return;
    const existing = newRowsTimers.current.get(sectionId);
    if (existing) clearTimeout(existing);
    setNewRowsBySection(prev => ({ ...prev, [sectionId]: count }));
    const timer = setTimeout(() => {
      newRowsTimers.current.delete(sectionId);
      setNewRowsBySection(prev => { const n = { ...prev }; delete n[sectionId]; return n; });
    }, 8000);
    newRowsTimers.current.set(sectionId, timer);
  }, []);
  useEffect(() => () => { newRowsTimers.current.forEach(clearTimeout); newRowsTimers.current.clear(); }, []);

  // Smooth-scroll to a section (expanding it — one-section accordion) so new rows show.
  const scrollToSection = useCallback((sectionId: string, slug: string) => {
    setExpandedSectionId(sectionId);
    requestAnimationFrame(() => {
      document.getElementById(`section-anchor-${slug}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): populate-section APPENDS — every extracted row becomes a NEW
  // row with a fresh rowId (extraction-routes.ts), so a double-fire doubles the records written.
  // `populateAllFanout` awaits this sequentially, so the lock releases between sections and the
  // Populate All loop is unaffected.
  const populateFlight = useSingleFlight();

  // ── D-170.6: the raw apiRequest writes on this page, hoisted to component scope ────────────
  // Declared together rather than beside each call site: they are used from several callbacks,
  // and hooks must not sit inside them. No new locks — each is class B/C, or already runs
  // inside an existing useSingleFlight (populate, executor-row), which the registry records as
  // A-enclosed. Behaviour is unchanged: every one of these is the same request it replaced.
  const populateSectionMutation = useMutation({
    mutationFn: async (v: { jobId: string; body: Record<string, unknown> }) =>
      await apiRequest("POST", `/api/extraction-jobs/${v.jobId}/populate-section`, v.body),
  });
  const recoverExecutorRowMutation = useMutation({
    mutationFn: async (v: { rowId: string; section: string }) =>
      await apiRequest("POST", `/api/executor/${clientData!.id}/row/${v.rowId}/recover`, { section: v.section }),
  });
  const createExecutorRowMutation = useMutation({
    mutationFn: async (v: { section: string; fields: unknown }) =>
      await apiRequest("POST", `/api/executor/${clientData!.id}/row`, { section: v.section, fields: v.fields }),
  });
  const seedDefaultRowsMutation = useMutation({
    mutationFn: async (categoryId: string) =>
      await apiRequest("POST", `/api/clients/${clientData!.id}/seed-default-rows`, { categoryId }),
  });
  const attachFlight = useSingleFlight();
  const attachDocumentMutation = useMutation({
    mutationFn: async (v: { entryId: string; body: Record<string, unknown> }) =>
      await apiRequest("POST", `/api/generic-entries/${v.entryId}/documents/upload`, v.body),
  });
  const createEntryMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      await apiRequest("POST", `/api/clients/${clientData!.id}/generic-entries`, body),
  });
  const patchEntryMutation = useMutation({
    mutationFn: async (v: { entryId: string; body: Record<string, unknown> }) =>
      await apiRequest("PATCH", `/api/generic-entries/${v.entryId}`, v.body),
  });
  // Distinct from `deleteDocumentMutation` below, which also carries a fieldId for its cache
  // update. This one is the bare delete used by the detail dialog.
  const deleteDocumentByIdMutation = useMutation({
    mutationFn: async (docId: string) => await apiRequest("DELETE", `/api/generic-documents/${docId}`),
  });
  const deletePendingMutation = useMutation({
    mutationFn: async (pendingId: string) =>
      await apiRequest("DELETE", `/api/pending-extractions/${pendingId}`),
  });
  const confirmPendingMutation = useMutation({
    mutationFn: async (v: { pendingId: string; body: Record<string, unknown> }) =>
      await apiRequest("POST", `/api/pending-extractions/${v.pendingId}/confirm`, v.body),
  });

  const populateFanoutSection = useCallback(async (sectionId: string, rowIndexes?: number[]) => {
    if (!fanout) return;
    await populateFlight.run(async () => {
    setFanoutPopulatingId(sectionId);
    try {
      const res = await populateSectionMutation.mutateAsync({
        jobId: fanout.jobId,
        body: rowIndexes ? { sectionId, rowIndexes } : { sectionId },
      });
      const body = await res.json().catch(() => ({} as { rowsWritten?: number }));
      setFanoutPopulatedIds(prev => new Set(prev).add(sectionId));
      flagNewRows(sectionId, body?.rowsWritten ?? 0);
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "generic-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientData?.id] });
    } catch {
      toast({ title: "Couldn't populate that section.", description: "Please try again.", variant: "destructive" });
    } finally {
      setFanoutPopulatingId(null);
    }
    });
  }, [fanout, queryClient, clientData?.id, toast, flagNewRows, populateFlight]);

  const populateAllFanout = useCallback(async (selections: FanOutSelection[]) => {
    if (!fanout) return;
    // DB-45 1D: show a "Populating…" spinner on the button, write every selected
    // section, then a "Data added to N sections ✓" success state for 2s before the
    // modal closes — so the planner gets unambiguous confirmation instead of a
    // silent close.
    setFanoutPopulatingAll(true);
    let count = 0;
    for (const sel of selections) {
      if (!fanoutPopulatedIds.has(sel.sectionId) && sel.rowIndexes.length > 0) {
        await populateFanoutSection(sel.sectionId, sel.rowIndexes);
        count++;
      }
    }
    setFanoutPopulatingAll(false);
    setFanoutAllDoneCount(count);
    setTimeout(() => {
      setFanoutOpen(false);
      setFanoutDismissed(true);
      setFanoutAllDoneCount(null);
    }, 2000);
  }, [fanout, fanoutPopulatedIds, populateFanoutSection]);

  // Open the pending-extraction review modal for a section/row from a staged
  // extraction. Shared by the async-job watcher and the phone-upload SSE path.
  const openPendingReviewForSection = useCallback((sectionId: string, rowId: string | null, pending: { id: string; documentName: string; fieldsFilled: number; fields: Array<{ fieldId: string; name: string; value: string; replacesValue?: string; currentValue?: string }>; summaryText?: string }) => {
    setPendingExtractionBySection(prev => ({
      ...prev,
      [sectionId]: { id: pending.id, documentName: pending.documentName, fieldsFilled: pending.fieldsFilled, fields: pending.fields, summaryText: pending.summaryText },
    }));
    setConfirmExtractionState(prev => ({ ...prev, [sectionId]: pending.fields }));
    setConfirmingExtractionRowId(rowId);
    setConfirmingExtractionSection(sectionId);
  }, []);

  // ── Async extraction (session 90): watch queued jobs from desktop uploads. ──
  const handleExtractionComplete = useCallback(async (info: { sectionId: string; rowId: string | null; hasPending: boolean; pendingId: string | null; filledCount: number; hasFanout: boolean; jobId: string }) => {
    // Cross-section fan-out (s96): additive — runs alongside the upload-section
    // review. Fetch the completed job's result for the OTHER sections' data.
    if (info.hasFanout && info.jobId) {
      try {
        const res = await apiRequest("GET", `/api/extraction-jobs/${info.jobId}`);
        const job = await res.json();
        const sections: FanOutSection[] = job?.result?.fanout ?? [];
        if (sections.length > 0) {
          setFanout({ jobId: info.jobId, sections, reuploadWarning: !!job?.result?.reuploadOfPopulated });
          setFanoutDismissed(false);
          setFanoutPopulatedIds(new Set());
        }
      } catch {
        /* fan-out is best-effort; the upload-section review still proceeds */
      }
    }
    if (info.hasPending && info.pendingId) {
      try {
        const res = await apiRequest("GET", `/api/pending-extractions/${info.pendingId}`);
        const pending = await res.json();
        if (pending?.id) openPendingReviewForSection(info.sectionId, info.rowId, pending);
      } catch {
        /* manual refresh still surfaces the pending banner */
      }
    } else if (info.filledCount === 0 && !info.hasFanout) {
      toast(NO_EXTRACTION_TOAST);
    } else if (info.filledCount > 0) {
      // Direct-save path (confirmBeforeSaving=false): values already written.
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "generic-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientData?.id] });
      toast({ title: "Data extracted from your document." });
    }
  }, [openPendingReviewForSection, queryClient, clientData?.id, toast]);

  const handleExtractionFailed = useCallback((_info: { sectionId: string; rowId: string | null }) => {
    // DB-45: the section-level error banner was removed with Fill from Document. A
    // terminal extraction failure now surfaces via this toast; the per-row in-progress
    // indicator clears itself when the job leaves the processing set.
    toast({ title: "We couldn't extract data from this document.", description: "Try uploading again.", variant: "destructive" });
  }, [toast]);

  const { watch: watchExtraction, isProcessing: isExtracting, processingKeys: extractionProcessingKeys } = useExtractionJobs({
    clientId: clientData?.id,
    onComplete: handleExtractionComplete,
    onFailed: handleExtractionFailed,
  });
  // Bridge for fileUpload (created above) — see watchExtractionRef.
  watchExtractionRef.current = watchExtraction;
  // True when ANY row (or the card-level null row) in a section is mid-extraction.
  const sectionExtracting = useCallback(
    (sid: string) => Array.from(extractionProcessingKeys).some(k => k.startsWith(sid + ":")),
    [extractionProcessingKeys],
  );

  // Map to store newly created entries by fieldId during upload (before entryMap updates)
  const pendingEntriesRef = useRef<Map<string, GenericEntry>>(new Map());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data: category, isLoading: categoryLoading } = useQuery<GenericCategory>({
    queryKey: ["/api/generic-categories/by-slug", categorySlug, clientData?.id],
    queryFn: async () => {
      // apiRequest routes through throwIfResNotOk: a non-auth 401 (expired session)
      // redirects to /login instead of falling through to the "Category not found"
      // render; a genuine 404 still throws → category undefined → "Category not found".
      const res = await apiRequest("GET", `/api/generic-categories/by-slug/${categorySlug}?clientId=${clientData?.id || 'null'}`);
      return res.json();
    },
    enabled: !!categorySlug,
  });

  const { data: sections = [], isLoading: sectionsLoading } = useQuery<GenericSection[]>({
    queryKey: ["/api/generic-categories", category?.id, "sections"],
    enabled: !!category?.id,
  });

  const { data: fields = [], isLoading: fieldsLoading } = useQuery<GenericField[]>({
    queryKey: ["/api/generic-fields/by-type", category?.categoryType],
    queryFn: async () => {
      // Via apiRequest so a non-auth 401 (expired session) redirects to /login instead
      // of surfacing as a generic "Failed to load fields"; other non-OK still throws.
      const res = await apiRequest("GET", `/api/generic-fields/by-type/${category?.categoryType}?isDefaultTemplate=true`);
      return res.json();
    },
    enabled: !!category?.categoryType,
  });

  const { data: customFields = [] } = useQuery<GenericField[]>({
    queryKey: ["/api/clients", clientData?.id, "custom-fields"],
    queryFn: async () => {
      // The endpoint returns 200 [] for the no-custom-fields case, so a non-OK is always
      // a real error (401/403/500) — route through apiRequest (401 → /login). On other
      // errors the `= []` destructure default keeps the empty path intact.
      const res = await apiRequest("GET", `/api/clients/${clientData?.id}/custom-fields`);
      return res.json();
    },
    enabled: !!clientData?.id && !readOnly,
  });

  // Executor auth/claim rows — only when this category contains the executor section,
  // so the "Send invitation" control (s91, 13a follow-up) can read each record's invite
  // state (status + invite_sent_at) keyed by executorRowId. Person data stays in entries.
  const hasExecutorSection = sections.some((s) => s.slug === SECTION_SLUGS.LEGAL_YOUR_EXECUTOR);
  const { data: executors = [] } = useQuery<
    Array<{ id: string; executorRowId: string | null; status: ExecutorInviteInfo["status"]; inviteSentAt: string | null; email: string | null }>
  >({
    queryKey: ["/api/clients", clientData?.id, "executors"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clients/${clientData?.id}/executors`);
      return res.json();
    },
    enabled: !!clientData?.id && !readOnly && hasExecutorSection,
    // Default app staleTime is Infinity; keep this fresh so a newly-added executor's
    // invite state shows up when the planner returns to / re-opens the section.
    staleTime: 0,
    refetchOnMount: "always",
  });
  const executorInviteByRowId = useMemo(
    () =>
      new Map<string, ExecutorInviteInfo>(
        executors
          .filter((e) => e.executorRowId)
          .map((e) => [e.executorRowId!, { id: e.id, status: e.status, inviteSentAt: e.inviteSentAt, email: e.email }]),
      ),
    [executors],
  );

  const { data: entries = [], isLoading: entriesLoading } = useQuery<GenericEntry[]>({
    queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientData?.id}/generic-entries?categoryType=${category?.categoryType}`);
      if (!res.ok) throw new Error("Failed to load entries");
      return res.json();
    },
    enabled: !!clientData?.id && !!category?.categoryType,
  });

  // s99: the per-client "Hide Category" mechanism (hidden-subcategories) was
  // subsumed by the section-level N/A × (D-99.3). The client no longer reads or
  // writes it; the server endpoints + table are left dormant. Sections that were
  // hidden under the old mechanism simply render again (and can be tucked away via
  // the N/A × where eligible).

  // Government forms lookup for formTags badges
  const { data: govForms = [] } = useQuery<Array<{ id: string; title: string }>>({
    queryKey: ["/api/government-forms"],
    staleTime: Infinity,
  });
  const govFormMap = useMemo(() => new Map(govForms.map(f => [f.id, f.title])), [govForms]);

  // Load existing document summaries for sections in this category
  const { data: existingSummaries = [] } = useQuery<Array<{ sectionId: string; documentName: string; summaryText: string; fieldsFilled: number; createdAt: string }>>({
    queryKey: ["/api/clients", clientData?.id, "document-summaries", category?.id],
    queryFn: async () => {
      if (!clientData?.id || !category?.id) return [];
      const sections = await fetch(`/api/generic-categories/${category.id}/sections`).then(r => r.ok ? r.json() : []);
      if (!Array.isArray(sections) || sections.length === 0) return [];
      const allSummaries: any[] = [];
      for (const sec of sections) {
        const res = await fetch(`/api/clients/${clientData.id}/sections/${encodeURIComponent(sec.slug)}/document-summaries`);
        if (res.ok) {
          const sums = await res.json();
          if (sums.length > 0) allSummaries.push({ ...sums[0], sectionId: sec.id });
        }
      }
      return allSummaries;
    },
    enabled: !!clientData?.id && !!category?.id && !readOnly,
    staleTime: 30000,
  });

  // Populate summaryBySectionId from server-loaded summaries (only once on initial load)
  const existingSummariesRef = useRef<typeof existingSummaries>([]);
  useEffect(() => {
    if (existingSummaries.length > 0 && existingSummariesRef.current !== existingSummaries) {
      existingSummariesRef.current = existingSummaries;
      const map: Record<string, { text: string; documentName: string; fieldsFilled: number; date: string }> = {};
      for (const s of existingSummaries) {
        map[s.sectionId] = {
          text: s.summaryText,
          documentName: s.documentName,
          fieldsFilled: s.fieldsFilled,
          date: new Date(s.createdAt).toLocaleString(),
        };
      }
      setSummaryBySectionId(prev => ({ ...map, ...prev })); // don't overwrite session-freshly-created summaries
    }
  }, [existingSummaries]);

  // Rehydrate pending extraction banners on page load — so yellow banner survives a page refresh
  useEffect(() => {
    if (!clientData?.id || !sections || sections.length === 0) return;
    // Only run once on initial mount (pendingExtractionBySection starts empty)
    if (Object.keys(pendingExtractionBySection).length > 0) return;
    (async () => {
      for (const sec of sections) {
        try {
          const res = await fetch(`/api/clients/${clientData.id}/sections/${sec.id}/pending-extraction`, { credentials: "include" });
          if (!res.ok) continue;
          const pending = await res.json();
          if (!pending) continue;
          setPendingExtractionBySection(prev => ({
            ...prev,
            [sec.id]: {
              id: pending.id,
              documentName: pending.documentName,
              fieldsFilled: pending.fieldsFilled,
              fields: pending.fields ?? [],
              summaryText: pending.summaryText ?? "",
            },
          }));
          setConfirmExtractionState(prev => ({
            ...prev,
            [sec.id]: pending.fields ?? [],
          }));
        } catch {
          // non-critical — silently skip
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData?.id, sections]);

  const [showHidden, setShowHidden] = useState(false);
  const [viewFile, setViewFile] = useState<FieldDoc | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchInputId = useId();

  const entryIds = useMemo(() => entries.map(e => e.id), [entries]);
  const { data: documentCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ["/api/generic-entries/documents/counts", entryIds],
    queryFn: async () => {
      if (entryIds.length === 0) return {};
      const response = await apiRequest("POST", "/api/generic-entries/documents/counts", { entryIds });
      return response.json();
    },
    enabled: entryIds.length > 0,
  });

  const loadFieldDocuments = useCallback(async (entryId: string, fieldId: string) => {
    if (!entryId) return;
    setLoadingFieldDocs(prev => new Set(prev).add(fieldId));
    try {
      const res = await fetch(`/api/generic-entries/${entryId}/documents`, {
        credentials: "include",
      });
      if (res.ok) {
        const docs = await res.json();
        setFieldDocuments(prev => ({
          ...prev,
          [fieldId]: docs.map((d: GenericDocument) => ({ id: d.id, filename: d.filename, fileSize: d.fileSize, mimeType: d.mimeType, uploadedAt: d.uploadedAt as unknown as string, extractedMetadata: d.extractedMetadata as { summary?: string | null; documentType?: string | null } | null })),
        }));
      }
    } catch (error) {
      console.error("Failed to load documents:", error);
    } finally {
      setLoadingFieldDocs(prev => {
        const next = new Set(prev);
        next.delete(fieldId);
        return next;
      });
    }
  }, []);

  const FIXED_COLUMN_WIDTHS = {
    completed: 32,
    delete: 32,
    details: 72,
  };

  const allSectionsWithFields = useMemo<SectionWithFields[]>(() => {
    const allFields = [...fields, ...customFields];
    const visibleSections = sections.filter(s => s.isActive !== false);
    return visibleSections.map(section => ({
      ...section,
      fields: allFields
        .filter(f => f.sectionId === section.id)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    }));
  }, [sections, fields, customFields]);

  const sectionsWithFields = useMemo<SectionWithFields[]>(() => {
    // Note (session 73): business sections are no longer HIDDEN here. The
    // ownsBusiness gate now drives an auto-NA affordance (see the section render
    // below), covering all 3 business sections — including
    // financial-business-self-employment, which the old hide missed.
    // s99: the per-client Hide Category filter was removed (subsumed by N/A ×).
    return allSectionsWithFields;
  }, [allSectionsWithFields]);

  // ── Executor inline editing (EXECUTOR-WRITE-1 Phase 4R, s144, D-144) ──────────
  // The executor edits the real form fields in place (no corrections panel). This block owns the
  // executor-only overlay (provenance + soft-deleted/added rows) and the pending "record you found"
  // rows; the field inputs go editable via `fieldsEditableWhenReadOnly`, and both save mutations
  // reroute to the executor change spine. All gated on `executorEditActive` → planner path untouched.
  const executorEditableSlugs = useMemo(
    () => (executorEditActive ? sectionsWithFields.filter(s => s.writeMode !== "append").map(s => s.slug) : []),
    [executorEditActive, sectionsWithFields],
  );
  const executorOverlayFor = useExecutorPlanOverlays(clientData?.id, executorEditableSlugs, executorEditActive);
  // F2 (D-144): the SHARED executor-removed-row set — fetched on BOTH surfaces (planner + executor)
  // via the owner-authorized endpoint, so removed rows tell the same story everywhere. The executor
  // gets Undo; the planner sees "removed by the executor" (state only). Enabled whenever a client is
  // loaded (react-query dedupes by clientId).
  const executorRemovedRowIds = useExecutorRemovedRows(clientData?.id, !!clientData?.id);
  // Planner's first name for the testimony "preserved as written" note (s145 Phase 5).
  // clientData is the deceased planner's record in the executor plan view; fall back to a
  // neutral possessive when the name isn't loaded.
  const plannerFirstName = (clientData?.name ?? "").trim().split(/\s+/)[0] || null;
  // Pending "record you found" rows: sectionId → minted rowIds (filled inline, saved as one add_row).
  const [pendingExecutorRows, setPendingExecutorRows] = useState<Record<string, string[]>>({});
  const [savingPendingRowId, setSavingPendingRowId] = useState<string | null>(null);
  const isPendingExecutorRow = useCallback(
    (rowId: string) => Object.values(pendingExecutorRows).some(ids => ids.includes(rowId)),
    [pendingExecutorRows],
  );
  const invalidateExecutorOverlay = useCallback((sectionSlug: string) => {
    queryClient.invalidateQueries({ queryKey: [`/api/executor/${clientData?.id}/plan/section/${sectionSlug}`] });
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "executor-removed-rows"] }); // shared removed-state
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType] });
    queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientData?.id] });
  }, [clientData?.id, category?.categoryType]);

  // F2: executor Undo of a removed row (recover via the change spine). Planner never gets this.
  const undoExecutorRemoval = useCallback(async (sectionSlug: string, rowId: string) => {
    if (!clientData?.id) return;
    try {
      await recoverExecutorRowMutation.mutateAsync({ rowId, section: sectionSlug });
      invalidateExecutorOverlay(sectionSlug);
    } catch (e) {
      toast({ title: "Couldn't restore the record", description: (e as Error).message, variant: "destructive" });
    }
  }, [clientData?.id, invalidateExecutorOverlay, toast]);

  // "Add a record you found": append a blank pending row the executor fills inline with the section's
  // normal widgets. It does NOT per-field save — the filled values batch into one add_row on Save.
  const addPendingExecutorRow = useCallback((sectionId: string) => {
    const rowId = crypto.randomUUID();
    setPendingExecutorRows(prev => ({ ...prev, [sectionId]: [...(prev[sectionId] ?? []), rowId] }));
    setRowExpanded(sectionId, rowId, true); // open the drawer so the executor can fill it
  }, [setRowExpanded]);

  const dropPendingExecutorRow = useCallback((sectionId: string, rowId: string) => {
    setPendingExecutorRows(prev => {
      const next = (prev[sectionId] ?? []).filter(r => r !== rowId);
      const copy = { ...prev };
      if (next.length === 0) delete copy[sectionId]; else copy[sectionId] = next;
      return copy;
    });
    // clear any local edits held for this pending row
    setTableLocalEdits(prev => {
      const copy = { ...prev };
      for (const k of Object.keys(copy)) if (k.endsWith(`:${rowId}`)) delete copy[k];
      return copy;
    });
  }, []);

  const executorRowFlight = useSingleFlight();

  const savePendingExecutorRow = useCallback(async (section: SectionWithFields, rowId: string) => {
    if (!clientData?.id) return;
    const fieldsPayload = section.fields
      .filter(f => !isCategoryAField(f) && f.isActive !== false)
      .map(f => ({ fieldId: f.id, fieldKey: f.fieldKey, value: tableLocalEdits[entryMapKey(f.id, rowId)] ?? "" }))
      .filter(f => f.value.trim() !== "");
    if (fieldsPayload.length === 0) {
      toast({ title: "Nothing to save", description: "Enter at least one value for the new record.", variant: "destructive" });
      return;
    }
    // SINGLE-FLIGHT-PRIMITIVE-1 (s170): each fire creates a new executor-added record.
    await executorRowFlight.run(async () => {
    setSavingPendingRowId(rowId);
    try {
      const res = await createExecutorRowMutation.mutateAsync({ section: section.slug, fields: fieldsPayload });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || "Save failed");
      dropPendingExecutorRow(section.id, rowId);
      invalidateExecutorOverlay(section.slug);
    } catch (e) {
      toast({ title: "Couldn't add the record", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSavingPendingRowId(null);
    }
    });
  }, [clientData?.id, tableLocalEdits, dropPendingExecutorRow, invalidateExecutorOverlay, toast, executorRowFlight]);

  // Live upload-completion (SSE): when a phone upload lands for a row in this
  // category, reload that row's documents instantly — no manual refresh.
  useEffect(() => {
    const unsub = subscribeUploadComplete(async (detail) => {
      // Async desktop extraction events are handled by useExtractionJobs — this
      // subscriber is only for the out-of-band phone upload completion.
      if (detail.type !== "upload-complete") return;
      if (!clientData?.id || detail.clientId !== clientData.id || !detail.rowId || !detail.entryId) return;
      const section = sectionsWithFields.find(s => s.id === detail.sectionId);
      const firstField = section?.fields[0];
      if (!firstField) return;
      loadFieldDocuments(detail.entryId, entryMapKey(firstField.id, detail.rowId));
      queryClient.invalidateQueries({ queryKey: ["/api/generic-entries/documents/counts"] });
      // Open + scroll the affected row so a phone-originated upload reveals its
      // record exactly as the in-page (computer) path does. No-op for non-
      // expandable sections (no consumer of revealRowId).
      setRevealRowBySection(prev => ({ ...prev, [detail.sectionId]: detail.rowId! }));

      // DB-45: surface cross-section fan-out for phone uploads too (previously only
      // the desktop path did). The worker now sends jobId + hasFanout on the
      // upload-complete event; fetch the completed job's fan-out and open the same
      // review banner the in-page path uses. Best-effort, additive to the row review.
      if (detail.hasFanout && detail.jobId) {
        try {
          const res = await apiRequest("GET", `/api/extraction-jobs/${detail.jobId}`);
          const job = await res.json();
          const sections: FanOutSection[] = job?.result?.fanout ?? [];
          if (sections.length > 0) {
            setFanout({ jobId: detail.jobId, sections, reuploadWarning: !!job?.result?.reuploadOfPopulated });
            setFanoutDismissed(false);
            setFanoutPopulatedIds(new Set());
          }
        } catch {
          /* fan-out is best-effort; the row review still proceeds */
        }
      }

      // Phone upload staged AI-extracted fields → open the review modal bound to
      // THIS row and THIS exact extraction. Values are NOT written until the
      // planner confirms. rowId + pendingId both travel in the SSE payload (the
      // upload link was created for this row); fetching by pendingId avoids a
      // mismatch when two row uploads are pending at once.
      if (detail.hasPending && detail.pendingId) {
        try {
          const res = await apiRequest("GET", `/api/pending-extractions/${detail.pendingId}`);
          const pending = await res.json();
          if (pending && pending.id) openPendingReviewForSection(detail.sectionId, detail.rowId, pending);
        } catch {
          /* best-effort; manual refresh still surfaces the pending banner */
        }
      } else if (detail.documentId) {
        // Phone upload attached a document but staged nothing for review —
        // extraction found no usable fields (or failed silently server-side;
        // tracked in build-roadmap). Give the same explicit terminal state.
        toast(NO_EXTRACTION_TOAST);
      }
    });
    return unsub;
  }, [clientData?.id, sectionsWithFields, category?.categoryType, loadFieldDocuments, openPendingReviewForSection, queryClient, toast]);


  const filteredSectionsWithFields = useMemo<SectionWithFields[]>(() => {
    const visible = sectionsWithFields.filter(s => !HIDDEN_SECTIONS.has(s.slug) && !INLINE_CARD_SECTIONS.has(s.slug));
    if (!searchQuery.trim()) return visible;
    const q = searchQuery.toLowerCase();
    return visible
      .map(section => ({
        ...section,
        fields: section.fields.filter(f => f.name.toLowerCase().includes(q)),
      }))
      .filter(section => section.name.toLowerCase().includes(q) || section.fields.length > 0);
  }, [sectionsWithFields, searchQuery]);

  // Keep the latest visible sections accessible to the scroll loop below without
  // making that effect depend on them (so async entry loading doesn't cancel it).
  sectionsRef.current = filteredSectionsWithFields;

  // Jump-to-section SCROLL: when linked here with ?section=<slug> (sidebar tree),
  // re-pin the scroll to that section's header anchor across a short window.
  // (Expansion is handled by the separate effect below — see its comment.)
  // A single timed scroll is unreliable — on a cold load the anchor renders late,
  // and on a warm nav sections above grow (entries load) and push the target down
  // after the scroll. So we run a bounded rAF loop (~1.5s): each frame scroll to
  // the anchor (once it exists) and re-pin until its viewport-top stops moving.
  // The effect depends only on the param, so entry-load re-renders never cancel it.
  useEffect(() => {
    const sectionParam = new URLSearchParams(locationSearch).get('section');
    if (!sectionParam) { jumpScrolledRef.current = null; return; }
    if (jumpScrolledRef.current === sectionParam) return;

    let raf = 0;
    const startTs = performance.now();
    let lastTop = NaN;
    let stableFrames = 0;

    const tick = (now: number) => {
      const el = document.getElementById(`section-anchor-${sectionParam}`);
      if (el) {
        el.scrollIntoView({ block: 'start' });
        // Mark done only once a scroll has actually fired, so a too-early frame
        // (anchor not yet rendered) never burns the once-per-param guard.
        if (jumpScrolledRef.current !== sectionParam) jumpScrolledRef.current = sectionParam;
        const top = Math.round(el.getBoundingClientRect().top);
        if (top === lastTop) {
          if (++stableFrames >= 3) return; // settled — stop re-pinning
        } else {
          stableFrames = 0;
          lastTop = top;
        }
      }
      if (now - startTs < 1500) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [locationSearch]);

  // Jump-to-section EXPANSION (one-section-at-a-time): deliberately SEPARATE from
  // the rAF scroll loop above so it re-applies when sections finish loading. The
  // scroll loop is bounded to ~1.5s from the param change; on a slow cold load the
  // sections (and thus the target) can arrive after that window closes, so an
  // expand wired into the loop silently no-ops once the data lands late — the s78
  // prod deep-link regression. Depending on `filteredSectionsWithFields` re-runs
  // this the moment they load; a once-per-param ref applies the expand a single
  // time so a later manual collapse of the same section isn't fought.
  useEffect(() => {
    const sectionParam = new URLSearchParams(locationSearch).get('section');
    if (!sectionParam) { jumpExpandedRef.current = null; return; }
    if (jumpExpandedRef.current === sectionParam) return;
    const target = filteredSectionsWithFields.find(s => s.slug === sectionParam);
    if (!target) return; // sections not loaded yet — re-runs when they are (dep below)
    jumpExpandedRef.current = sectionParam;
    setExpandedSectionId(target.id);
    // Guarantee scroll-into-view even when the rAF loop's window elapsed before the
    // section existed; defer a frame so the now-expanded layout has rendered.
    requestAnimationFrame(() => {
      document.getElementById(`section-anchor-${sectionParam}`)?.scrollIntoView({ block: 'start' });
    });
  }, [locationSearch, filteredSectionsWithFields]);

  // PLANNER-LENS-1 (s152) per-field landing (D-151.11): a `?section=<slug>&field=<key>`
  // deep-link (from the gap card / an inline badge) lands on the ONE real field — not
  // just its section. After the section-expand effect above opens the section, find the
  // field's `data-lens-field` node, scroll it to center, and apply a transient sage tint
  // (fieldHighlights tint PLUS scrollIntoView). Bounded rAF loop (~1.5s) mirrors the
  // section scroll — the field can render late as entries/the drawer load. Once-per
  // (section:field) guard so a later manual scroll isn't fought.
  useEffect(() => {
    const params = new URLSearchParams(locationSearch);
    const sectionParam = params.get('section');
    const fieldParam = params.get(LENS_FIELD_PARAM);
    if (!sectionParam || !fieldParam) { jumpFieldRef.current = null; return; }
    const targetKey = `${sectionParam}:${fieldParam}`;
    if (jumpFieldRef.current === targetKey) return;

    // REVEAL-FIRST (s152 landing fix, D-151.11): a collapsed About You sub-card or a
    // closed expandable-record drawer keeps the target field OUT of the DOM, so the poll
    // below would never find it. Resolve what to reveal (pure helper) and open it — the
    // re-render mounts the `data-lens-field` node, which the poll then scrolls + tints.
    const rowParam = params.get(LENS_ROW_PARAM);
    let rowMissing = false;
    const target = filteredSectionsWithFields.find((s) => s.slug === sectionParam);
    if (target) {
      const groups = getSectionGroups(sectionParam)?.groups;
      const plan = resolveLensReveal(fieldParam, {
        groups,
        isExpandable: target.layoutType === 'expandable',
        rowIds: tableSectionRows[target.id],
        // s158 PER-RECORD-LENS-1: open the record the link NAMES, not the first one.
        targetRowId: rowParam,
      });
      rowMissing = plan.rowNotFound === true;
      if (plan.groupId) {
        // Union the target sub-card into the open set (preserving whatever is open, or the
        // section's defaults on first touch) — never collapse groups already open.
        const defaults = (groups ?? []).filter((g) => !g.defaultCollapsed).map((g) => g.id);
        setOpenGroupsBySection((prev) => {
          const cur = prev[target.id] ?? defaults;
          return cur.includes(plan.groupId!) ? prev : { ...prev, [target.id]: [...cur, plan.groupId!] };
        });
      }
      // Open the TARGETED record's drawer directly via the controlled expand setter (s158;
      // empty section → addRecord, handled by the section scroll landing on the "add a record"
      // empty-state — no auto-create). Direct `setRowExpanded` (not the upload reveal channel)
      // is deterministic across the section-mount → drawer-open → node-mount sequence; the poll
      // below then scrolls + tints once the field mounts.
      if (plan.rowId) setRowExpanded(target.id, plan.rowId, true);
    }

    // 🔴 Sweep invariant, s158 hard case: the link named a record that is not among the section's
    // currently-known rows. Land on the section, and do NOT fall back to another record — tinting a
    // field in someone else's row is worse than not tinting at all.
    //
    // 🔴 Deliberately does NOT stamp `jumpFieldRef`. On a COLD load `tableSectionRows` is empty
    // until entries arrive, so the first pass looks exactly like "row deleted"; stamping the ref
    // here would make the once-per-target guard swallow the re-run that `entries` triggers, and the
    // drawer would never open. Leaving the ref unset costs one cheap re-evaluation and is what makes
    // a real per-record landing work at all. (Caught in the s158 browser check: unit tests passed
    // and the drawer still never opened — the client path has to be exercised, not assumed.)
    if (rowMissing) return;

    let raf = 0;
    const startTs = performance.now();
    const tick = (now: number) => {
      // Match by dataset (not a CSS attribute selector) — this file's `CSS` import is
      // dnd-kit's, not the browser global, so `CSS.escape` isn't available, and a raw
      // selector could break on refs with special chars.
      //
      // s158 (ruling A): prefer the anchor on the TARGETED RECORD. A field instance carries
      // `data-lens-row`; when the link names a row, only that record's node may be tinted —
      // otherwise a 10-record section would highlight whichever cell happened to be first.
      const fieldNodes = Array.from(document.querySelectorAll<HTMLElement>('[data-lens-field]'))
        .filter((n) => n.dataset.lensField === targetKey);
      let el = rowParam
        ? fieldNodes.find((n) => n.dataset.lensRow === rowParam)
        : fieldNodes.find((n) => !n.dataset.lensRow) ?? fieldNodes[0];
      if (!el && !rowParam) el = fieldNodes[0];
      // Defensive fallback to the column label. Should be unreachable now that every record cell
      // emits its own anchor — so if it ever fires, the sweep should see it and say so.
      if (!el) {
        const col = Array.from(document.querySelectorAll<HTMLElement>('[data-lens-column]'))
          .find((n) => n.dataset.lensColumn === targetKey);
        if (col) {
          console.warn(`[lens] no per-record anchor for ${targetKey}${rowParam ? ` row=${rowParam}` : ''} — fell back to the column label`);
          el = col;
        }
      }
      if (el) {
        jumpFieldRef.current = targetKey;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // Transient tint via inline style (no Tailwind-purge dependency); sage 50.
        el.style.transition = 'background-color 0.5s ease';
        el.style.backgroundColor = SAGE[50];
        window.setTimeout(() => { el.style.backgroundColor = ''; }, 2600);
        return; // landed — stop the loop
      }
      // Bounded (~2.5s) — longer than the plain 1.5s because a revealed sub-card/drawer
      // mounts after a re-render + the Radix open animation before the node exists.
      if (now - startTs < 2500) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // `entries` is in the deps so a COLD category-page load (entries arrive after the
    // section list) re-runs the reveal once the section's rows exist — otherwise a
    // drawer-field gap would resolve to `addRecord` before its records had loaded.
  }, [locationSearch, filteredSectionsWithFields, expandedSectionId, entries]);

  // Active-category collapse (s78): the sidebar dispatches `oe:collapse-sections`
  // when the already-active category is re-clicked — a same-path nav wouter treats
  // as a no-op, so it can't be detected via routing. Collapse the open section.
  useEffect(() => {
    const onCollapse = (e: Event) => {
      const detail = (e as CustomEvent<{ categoryUrl?: string }>).detail;
      if (detail?.categoryUrl === `/category/${categorySlug}`) setExpandedSectionId(null);
    };
    window.addEventListener('oe:collapse-sections', onCollapse);
    return () => window.removeEventListener('oe:collapse-sections', onCollapse);
  }, [categorySlug]);

  // ── Auto-NA gating (session 73) ───────────────────────────────────────────
  // Category-level NA (Children & Dependents): when the gate is false, the gated
  // sections collapse into ONE category-level affordance; non-gated sections in
  // the category (e.g. children-dependents-gov-ids, manual_only) still render.
  // The primary gate sources the section list that collapses; the N/A *condition*
  // is the AND-of-all OR-gates when the category appears in CATEGORY_OR_GATES
  // (s79: Children & Dependents is N/A only when neither children NOR dependents —
  // any `true` or `null` on either gate keeps it rendering).
  // s162 (CATEGORY-GATE-DEFORK-1): this page used to REIMPLEMENT the gate rule inline, despite
  // section-gating's own "one predicate, do not fork it" instruction. That fork is why the s162
  // flag-OR-rows fix would not have reached the surface the bug was reported on: the page never
  // called the shared predicate at all. It now calls `isSectionAutoNA` like every other reader.
  //
  // Row presence uses the SHARED `rowIsVisibleUserRecord` predicate over the entries this page
  // already loads — the same rule the table renderer, the next-up walk, the planner lens and the
  // executor binding resolver apply.
  // 🔴 s177 (A1 follow-up, F1): does this category actually RENDER a shaded row right now?
  //
  // The category-level shaded key below used to gate on `TOP_ZONE_SCAFFOLDS[slug]` — i.e. on a
  // CONFIG ENTRY EXISTING, not on anything being on screen. That was fine while every configured
  // section also seeded rows. After the s177 retirement (D-177.4) those config entries are
  // vestigial for the retired sections, so on Assets the legend explained a colour that appears
  // nowhere on the page. On Online Presence it stays truthful, because the password-log checklists
  // still seed and still shade (D-177.9a) — which is why the legend is gated, not deleted.
  //
  // 🔴 s177 (D-177.17): the row must also be UNTOUCHED. The first cut asked only "is a visible
  // seeded row on screen?", which was mechanically right and semantically wrong — after the
  // retirement the ONLY seeded rows still visible are ENGAGED ones (the migration hid every
  // untouched one), so the legend pointed at rows the planner had already filled and told them
  // those were "recommended for everyone". `assets-properties__0` holds a real address.
  //
  // The honest predicate is an untouched RECOMMENDATION: a visible seeded row carrying no planner
  // data. On Assets that count is zero on both staging and prod, so the legend goes; on Online
  // Presence the password-log checklists are untouched (D-177.9a), so it stays and stays true.
  //
  // ⏳ The deeper problem is NOT fixed here and is logged for A3/Magic (D-177.17b): shading itself
  // has inverted its meaning — `isSeededDefaultRow` now marks filled records rather than
  // suggestions. The interview is expected to retire shading outright, so this stops the legend
  // lying without rebuilding a model that is about to be deleted.
  const categoryRendersShadedRow = useMemo<boolean>(() => {
    if (entries.length === 0) return false;
    const rows = new Map<string, GenericEntry[]>();
    for (const e of entries) {
      if (!e.rowId || !e.sectionId || !isSeededDefaultRow(e.rowId)) continue;
      const key = `${e.sectionId}::${e.rowId}`;
      let b = rows.get(key);
      if (!b) { b = []; rows.set(key, b); }
      b.push(e);
    }
    const byId = new Map(sectionsWithFields.map((s) => [s.id, s]));
    for (const [key, rowEs] of Array.from(rows)) {
      const sep = key.indexOf("::");
      const sec = byId.get(key.slice(0, sep));
      if (!sec) continue; // a row on a section that isn't rendered on this page
      const rowId = key.slice(sep + 2);
      // D-177.17: ONE shared predicate — seeded, visible, not a legacy blank, and carrying no
      // planner data. Composed in shared/ so this page cannot drift from the migration and the
      // completion math, and so the rule is unit-testable away from this component.
      if (isUntouchedSuggestionRow(rowId, rowEs, sectionDeclaresEmptyStarter(sec.defaultRows))) return true;
    }
    return false;
  }, [entries, sectionsWithFields]);

  const sectionSlugsWithRows = useMemo<ReadonlySet<string>>(() => {
    const out = new Set<string>();
    if (entries.length === 0) return out;
    const rows = new Map<string, GenericEntry[]>();
    for (const e of entries) {
      if (!e.rowId || !e.sectionId) continue;
      const key = `${e.sectionId}::${e.rowId}`;
      let b = rows.get(key);
      if (!b) { b = []; rows.set(key, b); }
      b.push(e);
    }
    const byId = new Map(allSectionsWithFields.map(s => [s.id, s]));
    for (const [key, rowEs] of Array.from(rows)) {
      const sep = key.indexOf("::");
      const sec = byId.get(key.slice(0, sep));
      if (!sec || out.has(sec.slug)) continue;
      if (rowIsVisibleUserRecord(key.slice(sep + 2), rowEs, sectionDeclaresEmptyStarter(sec.defaultRows))) {
        out.add(sec.slug);
      }
    }
    return out;
  }, [allSectionsWithFields, entries]);

  const situationFlags = useMemo(() => ({
    hasChildren: clientData?.hasChildren ?? null,
    hasDependents: clientData?.hasDependents ?? null,
    hasPets: clientData?.hasPets ?? null,
    ownsProperty: clientData?.ownsProperty ?? null,
    ownsBusiness: clientData?.ownsBusiness ?? null,
  }) as SituationFlags, [clientData]);

  const categoryGate = CATEGORY_LEVEL_GATE[categorySlug];
  const categoryGatedSlugs = categoryGate ? new Set<string>(GATE_SECTIONS[categoryGate]) : null;
  // The category-level affordance is all-or-nothing: it shows only when EVERY gated section in the
  // category is auto-N/A by the shared predicate (which is itself flag-OR-rows since s162).
  const isCategoryNA = !!categoryGate
    && GATE_SECTIONS[categoryGate].length > 0
    && GATE_SECTIONS[categoryGate].every(slug =>
        isSectionAutoNA(categorySlug, slug, situationFlags, sectionSlugsWithRows));
  const renderSections = isCategoryNA && categoryGatedSlugs
    ? filteredSectionsWithFields.filter(s => !categoryGatedSlugs.has(s.slug))
    : filteredSectionsWithFields;

  // Single-section categories: auto-expand the lone section (s108). The
  // one-click-to-open accordion adds nothing when there's only one section
  // (e.g. Children & Dependents after the DB-S5 restructure, and About You as of
  // s109). Applied once per mount, after sections load, and only when nothing else
  // owns the expansion — a `?section=` deep-link jump takes precedence, and the ref
  // guard means a later manual collapse isn't fought (the planner can collapse the
  // section header and it stays collapsed).
  const singleSectionAutoExpandedRef = useRef(false);
  useEffect(() => {
    if (singleSectionAutoExpandedRef.current) return;
    if (renderSections.length !== 1) return;
    if (jumpExpandedRef.current) return; // a deep-link jump owns the expansion
    if (new URLSearchParams(locationSearch).get('section')) return;
    singleSectionAutoExpandedRef.current = true;
    setExpandedSectionId(renderSections[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderSections, locationSearch]);

  const entryMap = useMemo<EntryMap>(() => {
    const map: EntryMap = {};
    entries.forEach(entry => {
      // Card layout entries (rowId is null): keyed by fieldId
      // Table layout entries (rowId set): keyed by `${fieldId}:${rowId}`
      const key = entry.rowId ? entryMapKey(entry.fieldId, entry.rowId) : entry.fieldId;
      map[key] = entry;
    });
    return map;
  }, [entries]);

  // ── Continuation loop (s80) ─────────────────────────────────────────────────
  // When a section the planner is working in flips incomplete→complete, surface a
  // one-line banner pointing at the next item — `computeNextSection` on fresh data
  // via the shared dashboard hook (no forked logic). Subtle; dismissed by navigating.
  // (s84 #4a) The "Next" directive was dropped; useDashboardProgress is no longer read
  // here. The dashboard NEXT UP card is the single source of "what's next".
  const completionRef = useRef<Map<string, boolean>>(new Map());
  const completionSeededRef = useRef(false);
  const [justCompletedSectionId, setJustCompletedSectionId] = useState<string | null>(null);

  useEffect(() => {
    const prev = completionRef.current;
    const nextMap = new Map<string, boolean>();
    let transitioned: string | null = null;
    for (const section of renderSections) {
      const naGate = SECTION_NA_GATE[section.slug];
      // s162 (DEFORK): the per-section variant goes through the shared predicate too, so
      // completion and the rendered N/A state can never disagree. Manual N/A is a deliberate
      // planner choice and still wins regardless of rows.
      const isNA = (!!naGate && isSectionAutoNA(categorySlug, section.slug, situationFlags, sectionSlugsWithRows))
        || sectionNa.naSlugs.has(section.slug);
      const complete = !isNA && isSectionComplete(completionSectionOf(section), entries, { removedRowIds: executorRemovedRowIds });
      nextMap.set(section.id, complete);
      if (completionSeededRef.current && complete && prev.get(section.id) === false) {
        transitioned = section.id;
      }
    }
    completionRef.current = nextMap;
    // Seed on first run so already-complete sections don't fire the banner on load.
    if (!completionSeededRef.current) { completionSeededRef.current = true; return; }
    if (transitioned) {
      setJustCompletedSectionId(transitioned);
      // Keep the dashboard's all-entries query (read by its NEXT UP card) fresh when a
      // section completes here; it isn't covered by the per-category save invalidations.
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "generic-entries", "all"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on entries (the save signal); sections/clientData read live
  }, [entries]);

  // (s84 #4a) Auto-dismiss the "Saved — [Section] complete" confirmation. It used to be
  // dismissed by navigating (it was a "Next" link); now it's a plain confirmation, so
  // clear it on a short timer to keep it transient rather than a standing banner.
  useEffect(() => {
    if (!justCompletedSectionId) return;
    const t = setTimeout(() => setJustCompletedSectionId(null), 4000);
    return () => clearTimeout(t);
  }, [justCompletedSectionId]);

  const hiddenFieldIds = useMemo(() => {
    const ids = new Set<string>();
    Object.entries(entryMap).forEach(([fieldId, entry]) => {
      if ((entry as any).isHidden) ids.add(fieldId);
    });
    return ids;
  }, [entryMap]);

  // Merge saved entries with unsaved local edits so conditions re-evaluate on every keystroke.
  const liveEntryMap = useMemo<EntryMap>(() => {
    const map: EntryMap = { ...entryMap };
    for (const [fieldId, edits] of Object.entries(localEdits)) {
      if (edits.value !== undefined) {
        map[fieldId] = { ...(map[fieldId] ?? {}), value: edits.value } as GenericEntry;
      }
    }
    return map;
  }, [entryMap, localEdits]);

  // Per-section ordered list of unique rowIds (table / expandable layouts), in
  // sortOrder (the entries query orders by it). Built-in locked rows are now REAL
  // seeded rows (deterministic ids via lockedBuiltInRowId, sortOrder 0..n), so they
  // appear here through normal collection — no virtual `__virt__` synthesis. A row's
  // locked state is derived from its rowId (isLockedBuiltIn), not from this list.
  // DB-36: caches the last emitted row order per section so the order stays STICKY
  // across data edits (see the partition note in the memo below).
  const sectionOrderCacheRef = useRef<Map<string, string[]>>(new Map());
  const tableSectionRows = useMemo<Record<string, string[]>>(() => {
    // s84 (Fix 2, Option D): suppress legacy all-empty scaffold rows so they don't
    // render as blank rows. Predicate — hide a rowId iff:
    //   (every entry's value is empty/blank) AND (every entry is_seeded) AND
    //   (the section's spec defaultRows does NOT declare an empty {} starter row).
    // The third clause preserves the 6 sections (financial-business-self-employment
    // + the 5 children-* detail sections) whose spec intentionally seeds blank
    // starter rows. Labeled __builtin__ scaffold (id_type etc.) has a non-empty
    // value → not all-empty → renders. User mid-edit rows are is_seeded=false →
    // not hidden. Mirrors the spec-aware cleanup SQL.
    const declaresEmptyStarter = new Map<string, boolean>();
    for (const s of allSectionsWithFields) {
      declaresEmptyStarter.set(s.id, sectionDeclaresEmptyStarter(s.defaultRows));
    }

    // Per (section,rowId): preserve first-seen order + track all-empty / all-seeded.
    const order = new Map<string, string[]>();
    const rowEntries = new Map<string, GenericEntry[]>();
    entries.forEach(entry => {
      if (!entry.rowId || !entry.sectionId) return;
      const key = `${entry.sectionId}::${entry.rowId}`;
      if (!order.has(entry.sectionId)) order.set(entry.sectionId, []);
      if (!rowEntries.has(key)) {
        order.get(entry.sectionId)!.push(entry.rowId);
        rowEntries.set(key, []);
      }
      rowEntries.get(key)!.push(entry);
    });

    const result: Record<string, string[]> = {};
    order.forEach((rowIds, sectionId) => {
      const specEmpty = declaresEmptyStarter.get(sectionId) ?? false;
      const visible = rowIds.filter(rowId => {
        const entriesForRow = rowEntries.get(`${sectionId}::${rowId}`)!;
        // s106 (MIG-1 Issue 1): honour generic_entries.is_hidden in the read path — a
        // fully soft-hidden row is suppressed (locked built-ins exempt).
        return !isLegacyBlankRow(entriesForRow, specEmpty) && !isRowHidden(rowId, entriesForRow);
      });
      // s98 Fix C: float user-data rows above untouched scaffolds. Fan-out / user-added
      // rows are created last → returned last by the API, so they'd otherwise sit below
      // the seeded scaffolding. Locked-builtin + header rows stay anchored (see helper).
      //
      // DB-36: keep the order STICKY across data edits. Recomputing the partition on
      // every `entries` change made a just-filled scaffold visibly jump to the top
      // mid-edit (its row flips to "user data" on save). Re-sort ONLY when the SET of
      // visible rowIds changes (a row added/removed — fan-out, Add, delete); when only
      // a row's data changed, reuse the cached order so positions hold. New rows still
      // float, because adding one changes the set and triggers a fresh partition.
      // FIELDFLICKER-1 Fix 3 (s142): partition ONCE, then keep existing rows' positions stable.
      // DB-36 already reused the cache when the row set was unchanged; but a set CHANGE (adding a
      // second child, then typing into it) still triggered a full re-partition that could reorder
      // rows already on screen — the reported "second line flashes to first". Fix: partition only
      // on the first non-empty computation (mount/open); on later set changes, preserve the cached
      // order for rows still present and APPEND newly-added rowIds (in visible order), dropping
      // removed ones. An existing row therefore never jumps under the user's cursor; new rows the
      // user just added sit at the bottom where they're looking. Full float-user-above-scaffold
      // (s98) still applies to the initial render.
      const cached = sectionOrderCacheRef.current.get(sectionId);
      if (!cached || cached.length === 0) {
        const partitioned = partitionUserRowsFirst(
          visible,
          rowId => rowEntries.get(`${sectionId}::${rowId}`)!,
        );
        sectionOrderCacheRef.current.set(sectionId, partitioned);
        result[sectionId] = partitioned;
      } else {
        const visibleSet = new Set(visible);
        const kept = cached.filter(id => visibleSet.has(id));      // existing rows keep their order
        const keptSet = new Set(kept);
        const added = visible.filter(id => !keptSet.has(id));      // new rows append (visible order)
        const merged = kept.concat(added);
        sectionOrderCacheRef.current.set(sectionId, merged);
        result[sectionId] = merged;
      }
    });
    return result;
  }, [entries, allSectionsWithFields]);

  // Per-section ordered list of instance rowIds for card-layout sections.
  // Always starts with null (the default / first instance), then any extra rowId instances.
  const cardSectionInstanceRowIds = useMemo<Record<string, (string | null)[]>>(() => {
    const cardSectionIds = new Set(
      allSectionsWithFields.filter(s => s.layoutType === 'card').map(s => s.id)
    );
    const seen = new Map<string, Set<string>>();
    const result: Record<string, (string | null)[]> = {};
    entries.forEach(entry => {
      if (entry.rowId && entry.sectionId && cardSectionIds.has(entry.sectionId)) {
        if (!seen.has(entry.sectionId)) {
          seen.set(entry.sectionId, new Set());
          result[entry.sectionId] = [null];
        }
        if (!seen.get(entry.sectionId)!.has(entry.rowId)) {
          seen.get(entry.sectionId)!.add(entry.rowId);
          result[entry.sectionId].push(entry.rowId);
        }
      }
    });
    // Merge in pending instances not yet persisted
    for (const [sectionId, rowIds] of Object.entries(pendingCardInstances)) {
      if (!result[sectionId]) result[sectionId] = [null];
      for (const rowId of rowIds) {
        if (!result[sectionId].includes(rowId)) {
          result[sectionId].push(rowId);
        }
      }
    }
    return result;
  }, [allSectionsWithFields, entries, pendingCardInstances]);

  // Auto-seed default rows for table-layout categories. Fires once per category visit after
  // entries finish loading. Always invalidates the entries cache on success so that stale
  // browser-side cache (e.g. entries=[] cached before a server-side heal) is busted.
  const SEEDED_CATEGORY_TYPES = new Set(["dependents", "checklist", "passwords"]);
  const [seededCategoryId, setSeededCategoryId] = useState<string | null>(null);
  useEffect(() => {
    if (!clientData?.id || !category?.id) return;
    if (entriesLoading || fieldsLoading) return;
    if (!SEEDED_CATEGORY_TYPES.has(category.categoryType ?? "")) return;
    if (seededCategoryId === category.id) return;

    setSeededCategoryId(category.id);
    seedDefaultRowsMutation.mutateAsync(category.id)
      .then(async (res) => {
        const { created } = await res.json();
        queryClient.invalidateQueries({
          queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType],
        });
        if (created > 0) {
          queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientData?.id] });
        }
      })
      .catch(() => {/* non-fatal — user can still add rows manually */});
  }, [entriesLoading, fieldsLoading, seededCategoryId, clientData?.id, category?.id, category?.categoryType]);

  // Defense against Chrome credential autofill leaking into the search input.
  // AutofillHoneypot catches most attempts, but if Chrome still targets the
  // search input, the animationstart event fires the moment autofill lands.
  // Clear immediately to prevent it polluting the filter on first user click.
  // See AUTOFILL-REG-1, session 51.
  useEffect(() => {
    const input = searchInputRef.current;
    if (!input) {
      return;
    }

    const handleAnimationStart = (e: AnimationEvent) => {
      if (e.animationName === 'onAutoFillStart') {
        input.value = '';
        setSearchQuery('');
      }
    };

    input.addEventListener('animationstart', handleAnimationStart);

    return () => input.removeEventListener('animationstart', handleAnimationStart);
  }, []);

  // Read a File as base64 (no data: prefix) for the server-side attach route (s105).
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Attach one file to an entry via the server-side base64 route (s105 DB-1) — the
  // replacement for the retired presigned R2 flow (browser→R2 PUT + finalize). Returns
  // the created generic_documents row.
  // s170 (D-170.6): class A, and the conversion is what made it visible — every fire INSERTs a
  // generic_documents row AND uploads a fresh R2 object, so a double-drop attaches the same file
  // twice. The lock sits on this shared helper (both callers are per-file handlers taking exactly
  // one file), matching the useGenericRowUpload precedent. Resolves `undefined` on a skipped
  // duplicate; both callers check before reading the document.
  const attachDocumentToEntry = useCallback(async (entryId: string, file: File) => {
    return attachFlight.run(async () => {
      const base64 = await fileToBase64(file);
      const res = await attachDocumentMutation.mutateAsync({
        entryId,
        body: { data: base64, filename: file.name, contentType: file.type },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(err.message || "Upload failed");
      }
      return res.json();
    });
  }, [attachFlight, attachDocumentMutation]);

  // Detail-dialog document upload (s105): resolve/create the field's entry, then attach.
  const uploadDetailDocument = useCallback(async (file: File) => {
    if (!clientData?.id || !detailField || !category) throw new Error("Missing client, field, or category");

    // Check pendingEntriesRef first (entry may have been created by handleOpenDetail but not yet in entryMap)
    let entry = entryMap[detailField.id] || pendingEntriesRef.current.get(detailField.id);
    if (!entry) {
      const field = fields.find(f => f.id === detailField.id);
      if (!field) throw new Error("Field not found");
      const createRes = await createEntryMutation.mutateAsync({
        categoryId: category.id,
        sectionId: field.sectionId,
        fieldId: detailField.id,
      });
      if (!createRes.ok) {
        const error = await createRes.json().catch(() => ({} as { message?: string }));
        throw new Error(error.message || "Failed to create entry");
      }
      entry = await createRes.json();
      pendingEntriesRef.current.set(detailField.id, entry);
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData.id, "generic-entries", category.categoryType],
      });
    }

    try {
      const newDoc = await attachDocumentToEntry(entry.id, file);
      if (!newDoc) return; // duplicate drop — the original attach owns the toast
      const fieldId = detailField.id;
      setFieldDocuments(prev => ({
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), { id: newDoc.id, filename: newDoc.filename, fileSize: newDoc.fileSize, mimeType: newDoc.mimeType, uploadedAt: newDoc.uploadedAt }],
      }));
      queryClient.invalidateQueries({ queryKey: ["/api/generic-entries/documents/counts"] });
      toast({ title: "Document uploaded", description: "Your document has been attached successfully." });
    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    }
  }, [clientData?.id, detailField, category, entryMap, fields, attachDocumentToEntry, queryClient, toast]);

  const deleteDocumentMutation = useMutation({
    mutationFn: async ({ docId, fieldId }: { docId: string; fieldId: string }) => {
      await apiRequest("DELETE", `/api/generic-documents/${docId}`);
      return { docId, fieldId };
    },
    onSuccess: ({ docId, fieldId }) => {
      setFieldDocuments(prev => ({
        ...prev,
        [fieldId]: (prev[fieldId] || []).filter(d => d.id !== docId),
      }));
      queryClient.invalidateQueries({ queryKey: ["/api/generic-entries/documents/counts"] });
      toast({
        title: "Document removed",
        description: "The document has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error removing document",
        description: error.message,
        variant: "destructive",
      });
    },
  });


  const handleDownloadDocument = useCallback(async (docId: string) => {
    try {
      const res = await fetch(`/api/generic-documents/${docId}/download`, {
        credentials: "include",
      });
      if (res.ok) {
        const { url, downloadUrl } = await res.json();
        window.open(url || downloadUrl, "_blank");
      } else {
        throw new Error("Failed to get download URL");
      }
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Could not download the document.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const handleDeleteDocument = useCallback(
    (docId: string) => {
      if (!detailField) return;
      deleteDocumentMutation.mutate({ docId, fieldId: detailField.id });
    },
    [detailField, deleteDocumentMutation]
  );

  const saveExtractionMutation = useMutation({
    mutationFn: async ({ docId, fieldId, extractedMetadata }: { docId: string; fieldId: string; extractedMetadata: ExtractedData }) => {
      // Save extraction metadata to document record
      const res = await apiRequest("PATCH", `/api/generic-documents/${docId}/extraction`, {
        extractedMetadata,
        approved: true,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Save failed");
      }
      return { doc: await res.json(), fieldId, extractedMetadata };
    },
    onSuccess: async ({ doc, fieldId, extractedMetadata }) => {
      // Get entry - must exist to save notes
      const entryForNotes = entryMap[fieldId] || pendingEntriesRef.current.get(fieldId);
      if (!entryForNotes) {
        toast({
          title: "Cannot save to Notes",
          description: "No entry found for this item. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Format AI extraction as notes text
      const aiNotesText = formatExtractionAsNotes(extractedMetadata);
      
      // Get current notes from local edits first, then from entryMap
      const existingLocalNotes = localEdits[fieldId]?.notes;
      const currentNotes = existingLocalNotes !== undefined ? existingLocalNotes : (entryForNotes?.notes || "");
      const separator = (currentNotes || "").trim() ? "\n\n" : "";
      const newNotes = (currentNotes || "") + separator + aiNotesText;
      
      // Save notes to database FIRST (before updating any local state)
      const notesRes = await patchEntryMutation.mutateAsync({ entryId: entryForNotes.id, body: { notes: newNotes } });
      if (!notesRes.ok) {
        const error = await notesRes.json().catch(() => ({ message: "Failed to save notes" }));
        toast({
          title: "Notes save failed",
          description: error.message || "Could not save AI results to notes.",
          variant: "destructive",
        });
        return;
      }
      
      // Only update local state after ALL saves succeed
      setFieldDocuments(prev => ({
        ...prev,
        [fieldId]: (prev[fieldId] || []).map(d => 
          d.id === doc.id ? { ...d, ...doc } : d
        ),
      }));
      
      setLocalEdits(prev => ({
        ...prev,
        [fieldId]: {
          ...(prev[fieldId] || {}),
          notes: newNotes,
        },
      }));
      
      // Invalidate entries query to ensure fresh data across the app
      if (clientData?.id && category?.categoryType) {
        await queryClient.invalidateQueries({ 
          queryKey: ["/api/clients", clientData.id, "generic-entries", category.categoryType] 
        });
      }
      
      toast({
        title: "AI results saved to Notes",
        description: "The extracted information has been added to your notes.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Save failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const prepareFieldEntry = useCallback(async (field: GenericField, section?: SectionWithFields) => {
    const fieldId = field.id;
    let entry = entryMap[fieldId] || pendingEntriesRef.current.get(fieldId);
    if (!entry && clientData?.id && category?.id) {
      setCreatingEntryFieldId(fieldId);
      try {
        const createRes = await createEntryMutation.mutateAsync({
          categoryId: category.id,
          sectionId: field.sectionId,
          fieldId,
        });
        if (createRes.ok) {
          entry = await createRes.json();
          pendingEntriesRef.current.set(fieldId, entry);
          queryClient.invalidateQueries({
            queryKey: ["/api/clients", clientData.id, "generic-entries", category.categoryType],
          });
          setFieldDocuments(prev => ({ ...prev, [fieldId]: [] }));
        }
      } catch (error) {
        console.error("Failed to create entry:", error);
      } finally {
        setCreatingEntryFieldId(prev => prev === fieldId ? null : prev);
      }
    } else if (entry) {
      loadFieldDocuments(entry.id, fieldId);
    }
  }, [entryMap, loadFieldDocuments, clientData?.id, category?.id, category?.categoryType]);

  const handleOpenDetail = useCallback(async (field: GenericField, section?: SectionWithFields) => {
    setDetailField(field);
    setDetailFieldSection(section ?? null);
    setDetailRowId(null);
    await prepareFieldEntry(field, section);
  }, [prepareFieldEntry]);

  const handleOpenTableRowDetail = useCallback(async (section: SectionWithFields, rowId: string) => {
    const firstField = section.fields[0];
    if (!firstField) return;
    setDetailField(firstField);
    setDetailFieldSection(section);
    setDetailRowId(rowId);
    const entryKey = entryMapKey(firstField.id, rowId);
    const existingEntry = entryMap[entryKey];
    if (existingEntry) {
      loadFieldDocuments(existingEntry.id, entryKey);
    } else if (clientData?.id && category?.id) {
      setCreatingEntryFieldId(entryKey);
      try {
        const createRes = await createEntryMutation.mutateAsync({
          categoryId: category.id,
          sectionId: firstField.sectionId,
          fieldId: firstField.id,
          rowId,
        });
        if (createRes.ok) {
          const newEntry = await createRes.json();
          queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, "generic-entries"] });
          setFieldDocuments(prev => ({ ...prev, [entryKey]: [] }));
          loadFieldDocuments(newEntry.id, entryKey);
        }
      } catch (error) {
        console.error("Failed to create entry:", error);
      } finally {
        setCreatingEntryFieldId(prev => prev === entryKey ? null : prev);
      }
    }
  }, [entryMap, loadFieldDocuments, clientData?.id, category?.id, category?.categoryType, prepareFieldEntry]);

  // Build 1 (expandable layout): load a row's documents inline (no detail dialog).
  // Mirrors handleOpenTableRowDetail's entry-resolve + doc-load, keyed by `${firstFieldId}:${rowId}`.
  const ensureRowDocuments = useCallback(async (section: SectionWithFields, rowId: string) => {
    const firstField = section.fields[0];
    if (!firstField) return;
    const entryKey = entryMapKey(firstField.id, rowId);
    const existingEntry = entryMap[entryKey];
    if (existingEntry) {
      loadFieldDocuments(existingEntry.id, entryKey);
    } else if (clientData?.id && category?.id) {
      try {
        const createRes = await createEntryMutation.mutateAsync({
          categoryId: category.id,
          sectionId: firstField.sectionId,
          fieldId: firstField.id,
          rowId,
        });
        if (createRes.ok) {
          const newEntry = await createRes.json();
          queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, "generic-entries"] });
          setFieldDocuments(prev => ({ ...prev, [entryKey]: [] }));
          loadFieldDocuments(newEntry.id, entryKey);
        }
      } catch (error) {
        console.error("Failed to ensure row documents:", error);
      }
    }
  }, [entryMap, loadFieldDocuments, clientData?.id, category?.id, category?.categoryType]);

  const getRowDocCount = useCallback((section: SectionWithFields, rowId: string): number => {
    const firstField = section.fields[0];
    if (!firstField) return 0;
    const entry = entryMap[entryMapKey(firstField.id, rowId)];
    return entry ? (documentCounts[entry.id] ?? 0) : 0;
  }, [entryMap, documentCounts]);

  // Delete an attached document (expandable layout). Optimistically drops it
  // from the row's doc list, then refetches the count.
  const handleDeleteRowDocument = useCallback(async (section: SectionWithFields, rowId: string, docId: string) => {
    const firstField = section.fields[0];
    if (!firstField) return;
    const entryKey = entryMapKey(firstField.id, rowId);
    try {
      await deleteDocumentByIdMutation.mutateAsync(docId);
      setFieldDocuments(prev => ({
        ...prev,
        [entryKey]: (prev[entryKey] || []).filter(d => d.id !== docId),
      }));
      queryClient.invalidateQueries({ queryKey: ["/api/generic-entries/documents/counts"] });
    } catch (e: any) {
      toast({ title: "Couldn't remove document", description: e?.message ?? "Please try again.", variant: "destructive" });
    }
  }, [queryClient, toast]);

  // Build 1 fix (expandable layout): one file → attach AND extract.
  // Attach: presigned R2 upload → createGenericDocument on the row's first-field
  // entry. Extract: same Gemini path; confirm-before-saving surfaces the
  // pending-extraction banner. The two write to different tables — no clobber.
  const { uploadRowDocument } = useGenericRowUpload();

  const handleExpandableRowUpload = useCallback(async (section: SectionWithFields, rowId: string, file: File) => {
    if (!clientData?.id || !category?.id) return;
    const firstField = section.fields[0];
    if (!firstField) return;
    const entryKey = entryMapKey(firstField.id, rowId);
    try {
      // Shared upload+extract flow (also used by the guided wizard). Returns the
      // resolved entry, the attached document, and the queued extraction job.
      const uploaded = await uploadRowDocument({
        clientId: clientData.id,
        categoryId: category.id,
        section,
        rowId,
        file,
        existingEntry: entryMap[entryKey] ?? null,
      });
      if (!uploaded) return; // s170: the lock rejected this as a duplicate drop
      const { entry, document: newDoc, job } = uploaded;
      setFieldDocuments(prev => ({
        ...prev,
        [entryKey]: [...(prev[entryKey] || []), { id: newDoc.id, filename: newDoc.filename, fileSize: newDoc.fileSize, mimeType: newDoc.mimeType, uploadedAt: newDoc.uploadedAt }],
      }));
      // Authoritative reload — supersedes any in-flight expand-time load that
      // could otherwise resolve later and clobber the optimistic add (Bug 1).
      loadFieldDocuments(entry.id, entryKey);
      queryClient.invalidateQueries({ queryKey: ["/api/generic-entries/documents/counts"] });

      // EXTRACT is async (session 90): watch the job — on completion the watcher
      // surfaces the review modal (confirm-before-saving) bound to THIS row, or the
      // explicit "no fields found" terminal state. The "Extracting…" indicator on
      // the row covers the wait; no blocking modal.
      if (job) {
        watchExtraction(job.jobId, { sectionId: section.id, rowId });
      } else {
        toast({ title: "Document attached", description: "Your file was attached to this record." });
      }
    } catch (e: any) {
      toast({ title: "Upload failed", description: e?.message ?? "Could not upload the file.", variant: "destructive" });
    }
  }, [clientData?.id, category?.id, entryMap, queryClient, toast, loadFieldDocuments, uploadRowDocument, watchExtraction]);

  // Shared by the card + expandable pending-extraction banners.
  const discardPendingExtraction = useCallback(async (sectionId: string) => {
    const pending = pendingExtractionBySection[sectionId];
    if (!pending) return;
    await deletePendingMutation.mutateAsync(pending.id);
    setPendingExtractionBySection(prev => { const n = { ...prev }; delete n[sectionId]; return n; });
    setConfirmExtractionState(prev => { const n = { ...prev }; delete n[sectionId]; return n; });
  }, [pendingExtractionBySection]);

  // Get a table cell's current value (local edit → DB entry). Built-in locked rows
  // are real seeded rows now, so their values come from entryMap like any other row.
  const getTableCellValue = useCallback((fieldId: string, rowId: string): string => {
    const key = entryMapKey(fieldId, rowId);
    const localVal = tableLocalEdits[key];
    if (localVal !== undefined) return localVal;
    return entryMap[key]?.value ?? "";
  }, [tableLocalEdits, entryMap]);

  const handleTableCellChange = useCallback((fieldId: string, rowId: string, value: string) => {
    if (readOnly && !executorEditActive) return; // Phase 4R: executor edits a readOnly section
    // Clear AI highlight for this cell on first edit
    setFieldHighlights(prev => {
      const cellKey = entryMapKey(fieldId, rowId);
      if (!prev[cellKey]) return prev;
      const next = { ...prev };
      delete next[cellKey];
      return next;
    });
    setTableLocalEdits(prev => ({ ...prev, [entryMapKey(fieldId, rowId)]: value }));
    // RADIO-RACE-1: synchronous mirror so a same-tick drawer-radio blur reads the fresh value.
    pendingTableEditsRef.current[entryMapKey(fieldId, rowId)] = value;
  }, [readOnly, executorEditActive]);

  // GATE-CLEAR-1 (s147): the planner confirm for a gate-closing edit that would clear dependent
  // fields. Wraps the two planner save mutations below; the executor edit path clears via the spine.
  const { writeWithGateClear, dialog: gateClearDialog } = useGateClearConfirm();

  // The record's own name (e.g. the child) so the confirm reads "Clear 3 details for Avery Barber?"
  // — reads the section's identity field value for this row from the entries cache. Card rows (no
  // record) return undefined → the dialog omits the "for …" clause.
  const recordLabelForRow = useCallback((sectionId: string, rowId: string): string | undefined => {
    const sec = sectionsWithFields.find(s => s.id === sectionId);
    // 🔴 s199 Phase 3b, ruling 1 — this reader asks BOTH questions, so it uses both declarations in
    // that order and does not collapse them:
    //   MEMBERSHIP — "should this surface label the record at all?" → `labelCurated`. It is the same
    //     question the executor change log asked, and it used to be answered by testing membership
    //     of `RECORD_FIELD_BY_SECTION`, which made that map's contents load-bearing here too:
    //     growing the map would have widened this dialog from 23 sections to 33 in silence.
    //   VALUE — "which field?" → the identity role, read off the FETCHED field rows. No constant and
    //     no spec import: `identity_ordinal` is a real column and arrives in this page's own
    //     /api/generic-fields/by-type payload.
    const identityFields = (sec?.fields ?? [])
      .filter(f => typeof f.identityOrdinal === "number" && f.identityOrdinal > 0)
      .sort((a, b) => (a.identityOrdinal ?? 0) - (b.identityOrdinal ?? 0));
    const curated = identityFields.some(f => f.labelCurated === true);
    // 🔴 COMPOSITE BEHAVIOUR PRESERVED, deliberately. Today `RECORD_FIELD_BY_SECTION` returns an
    // ARRAY for the two person sections and `find(f => f.fieldKey === idKey)` compares a string to
    // an array, so it never matches and those sections get NO "for …" clause. That is a quirk, not a
    // decision — but changing it here would be a second change riding on this one, so a composite
    // still yields no label. Its own ruling, not this build's.
    const idField = curated && identityFields.length === 1 ? identityFields[0] : undefined;
    if (!idField) return undefined;
    const all = queryClient.getQueryData<GenericEntry[]>(entriesQueryKey()) ?? [];
    const v = (all.find(e => e.fieldId === idField.id && (e.rowId ?? null) === rowId)?.value ?? "").trim();
    return v || undefined;
  }, [sectionsWithFields, queryClient]);

  const upsertTableEntryMutation = useMutation({
    mutationFn: async ({ field, rowId, sectionId, value }: { field: GenericField; rowId: string; sectionId: string; value: string }) => {
      if (!clientData?.id || !category?.id) throw new Error("Missing client or category");
      // Phase 4R (D-144): a verified executor's edit routes through the change spine (the sole
      // fact-edit writer), NOT the planner save path. Same optimistic-cache shape, different endpoint.
      if (executorEditActive) {
        const slug = sectionsWithFields.find(s => s.id === sectionId)?.slug;
        if (!slug) throw new Error("Section not found");
        const res = await apiRequest("PATCH", `/api/executor/${clientData.id}/entry`, {
          section: slug, rowId, fieldId: field.id, fieldKey: field.fieldKey, newValue: value,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ message: "Save failed" }));
          throw new Error(err.message || "Save failed");
        }
        invalidateExecutorOverlay(slug);
        return res.json();
      }
      const res = await writeWithGateClear(
        (confirm) =>
          apiRequest("POST", `/api/clients/${clientData.id}/generic-entries`, {
            categoryId: category.id,
            sectionId,
            fieldId: field.id,
            rowId,
            value,
            ...(confirm ? { confirmGateClear: true } : {}),
          }),
        { recordLabel: recordLabelForRow(sectionId, rowId) },
      );
      return res.json();
    },
    // FIELDFLICKER-1 Fix 2 (s142): same optimistic pattern as the card path, for every
    // expandable/table section. Merge the outgoing cell value into the entries cache before the
    // POST leaves; onSuccess still clears tableLocalEdits + invalidates, onError rolls back the
    // snapshot AND restores the dropped draft. Closes the blur-save→refetch window where a saved
    // cell briefly reverted to its old value.
    onMutate: async ({ field, rowId, value }: { field: GenericField; rowId: string; sectionId: string; value: string }) => {
      const key = entriesQueryKey();
      queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GenericEntry[]>(key);
      queryClient.setQueryData<GenericEntry[]>(key, (old = []) => {
        const idx = old.findIndex(e => e.fieldId === field.id && ((e.rowId ?? null) === rowId));
        if (idx >= 0) { const next = old.slice(); next[idx] = { ...next[idx], value }; return next; }
        return [...old, { id: `optimistic-${field.id}-${rowId}`, fieldId: field.id, rowId, value } as GenericEntry];
      });
      const localKey = entryMapKey(field.id, rowId);
      const previousTableLocalEdit = tableLocalEdits[localKey];
      return { previous, previousTableLocalEdit, localKey };
    },
    onSuccess: (_, { field, rowId }) => {
      delete pendingTableEditsRef.current[entryMapKey(field.id, rowId)]; // RADIO-RACE-1: keep the ref in step
      setTableLocalEdits(prev => {
        const next = { ...prev };
        delete next[entryMapKey(field.id, rowId)];
        return next;
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType],
      });
      // Keep the Where Is Everything aggregate fresh when a source location field is
      // edited from a table/expandable section (it pulls from these entries).
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, CATEGORY_SLUGS.WHERE_IS_EVERYTHING],
      });
      // s172: same reason as the card path — and this is the bigger one. Every value the balance
      // sheet reads (property value, title_ownership, mortgage_balance, loan_balance, premium
      // frequency, account balances) is written from a table/expandable row, so without this the
      // Financial Summary page could sit stale after editing the very fields it totals.
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "estate-summary"],
      });
      // Saving an executor field can create/sync its executor_access row (server awaits
      // the sync before responding), so refresh the executors list — this is what makes
      // the "Send invitation" button appear for a just-added executor (s91 fix).
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "executors"],
      });
      // For dependents category: reset seeded flag so the cross-section name sync
      // re-runs after any Overview entry is saved (new names propagate to sibling tables).
      if (category?.categoryType === "dependents") {
        setSeededCategoryId(null);
      }
    },
    onError: (error: Error, _vars, context: { previous?: GenericEntry[]; previousTableLocalEdit?: string; localKey: string } | undefined) => {
      // Restore the dropped draft before rolling the cache back (see the card mutation's note) so
      // no intermediate render shows the pre-save value on the error path.
      if (context && context.previousTableLocalEdit !== undefined) {
        pendingTableEditsRef.current[context.localKey] = context.previousTableLocalEdit; // RADIO-RACE-1
        setTableLocalEdits(prev => ({ ...prev, [context.localKey]: context.previousTableLocalEdit! }));
      } else if (context) {
        delete pendingTableEditsRef.current[context.localKey]; // no prior draft → clear the ref too
      }
      if (context?.previous !== undefined) queryClient.setQueryData(entriesQueryKey(), context.previous);
      // GATE-CLEAR-1: the planner declined the clear — the rollback above already reverted the
      // source cell to its prior value (nothing cleared). It's a choice, not an error: no toast.
      if (error instanceof GateClearCancelledError) return;
      toast({ title: "Error saving", description: error.message, variant: "destructive" });
    },
  });

  const handleTableCellBlur = useCallback((field: GenericField, rowId: string, sectionId: string) => {
    // Phase 4R: the executor edits on a readOnly section — allow the blur-save. A PENDING "record
    // you found" row does NOT per-field save (its values are batched into one add_row on Save).
    if (readOnly && !executorEditActive) return;
    if (isPendingExecutorRow(rowId)) return;
    const localKey = entryMapKey(field.id, rowId);
    // RADIO-RACE-1: read from the synchronous ref (fresh on a same-tick radio onChange→onBlur),
    // falling back to the state map.
    let localVal = pendingTableEditsRef.current[localKey] ?? tableLocalEdits[localKey];
    if (localVal === undefined) return;

    // Format phone values on blur before saving
    if (field.fieldType === 'phone' && localVal) {
      const formatted = formatPhone(localVal);
      if (formatted !== localVal) {
        localVal = formatted;
        pendingTableEditsRef.current[localKey] = localVal;
        setTableLocalEdits(prev => ({ ...prev, [localKey]: localVal as string }));
      }
    }

    upsertTableEntryMutation.mutate({ field, rowId, sectionId, value: localVal });
  }, [readOnly, executorEditActive, isPendingExecutorRow, tableLocalEdits, upsertTableEntryMutation]);

  const deleteRowMutation = useMutation({
    mutationFn: async ({ rowId, sectionId }: { rowId: string; sectionId: string }) => {
      if (!clientData?.id) throw new Error("Missing client");
      await apiRequest("DELETE", `/api/clients/${clientData.id}/generic-rows/${rowId}?sectionId=${sectionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType],
      });
      toast({ title: "Row deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Error deleting row", description: error.message, variant: "destructive" });
    },
  });

  const toggleSection = (sectionId: string) => {
    // One-section-at-a-time accordion: opening a section collapses any other
    // open one; clicking the already-open section closes it.
    setExpandedSectionId(prev => (prev === sectionId ? null : sectionId));
  };

  const addCardInstance = useCallback((sectionId: string) => {
    const newRowId = crypto.randomUUID();
    setPendingCardInstances(prev => ({
      ...prev,
      [sectionId]: [...(prev[sectionId] ?? []), newRowId],
    }));
  }, []);

  const deleteCardInstance = useCallback((sectionId: string, rowId: string) => {
    setPendingCardInstances(prev => {
      const existing = prev[sectionId] ?? [];
      const next = existing.filter(r => r !== rowId);
      if (next.length === 0) {
        const { [sectionId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [sectionId]: next };
    });
    deleteRowMutation.mutate({ rowId, sectionId });
  }, [deleteRowMutation]);

  const addFieldInstance = useCallback((fieldId: string) => {
    setPendingFieldInstances(prev => ({
      ...prev,
      [fieldId]: [...(prev[fieldId] ?? []), crypto.randomUUID()],
    }));
  }, []);

  const deleteFieldInstance = useCallback((fieldId: string, rowId: string) => {
    setPendingFieldInstances(prev => {
      const next = (prev[fieldId] ?? []).filter(r => r !== rowId);
      if (next.length === 0) {
        const { [fieldId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [fieldId]: next };
    });
    setTableLocalEdits(prev => {
      const next = { ...prev };
      delete next[entryMapKey(fieldId, rowId)];
      return next;
    });
  }, []);

  // FIELDFLICKER-1 (s142): entriesKey helper — the exact card-entries cache key, reused by
  // the optimistic onMutate/onError so the merge/rollback target the query getFieldValue reads.
  const entriesQueryKey = () => ["/api/clients", clientData?.id, "generic-entries", category?.categoryType];
  const upsertEntryMutation = useMutation({
    mutationFn: async (data: Partial<GenericEntry> & { fieldId: string }) => {
      if (!clientData?.id || !category?.id) throw new Error("Category not loaded — please refresh the page");
      const field = [...fields, ...customFields].find(f => f.id === data.fieldId);
      if (!field?.sectionId) throw new Error("Field not found — please refresh the page");
      const res = await writeWithGateClear((confirm) =>
        apiRequest("POST", `/api/clients/${clientData.id}/generic-entries`, {
          categoryId: category?.id,
          sectionId: field.sectionId,
          ...data,
          ...(confirm ? { confirmGateClear: true } : {}),
        }),
      );
      return res.json();
    },
    // FIELDFLICKER-1 (s142): TRUE optimistic update. Merge the outgoing value into the entries
    // cache HERE — before the POST leaves — so entryMap holds the typed value the instant
    // handleFieldChange drops its localEdits draft. Previously the input snapped back to the
    // stale cached value for the whole POST+refetch window (the reported appear/disappear/reappear).
    // cancelQueries fires (unawaited) then setQueryData runs SYNCHRONOUSLY, so the cache carries
    // the value before any re-render — no dependency on microtask/batching timing.
    onMutate: async (data: Partial<GenericEntry> & { fieldId: string }) => {
      const key = entriesQueryKey();
      queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<GenericEntry[]>(key);
      const rowId = (data.rowId ?? null) as string | null;
      queryClient.setQueryData<GenericEntry[]>(key, (old = []) => {
        const idx = old.findIndex(e => e.fieldId === data.fieldId && ((e.rowId ?? null) === rowId));
        if (idx >= 0) {
          const next = old.slice();
          next[idx] = { ...next[idx], ...data };
          return next;
        }
        return [...old, { id: `optimistic-${data.fieldId}-${rowId ?? ''}`, rowId, ...data } as GenericEntry];
      });
      return { previous };
    },
    onSuccess: (_data, variables) => {
      // Draft is dropped HERE (not synchronously at debounce fire): the cache already holds the
      // value via onMutate, and the invalidation below re-confirms from the server — so removing
      // localEdits now can never expose a stale cache read.
      setLocalEdits(prev => {
        const fieldEdits = prev[variables.fieldId];
        if (!fieldEdits) return prev;
        const rest: Partial<GenericEntry> = { ...fieldEdits };
        for (const k of Object.keys(variables)) if (k !== "fieldId") delete (rest as Record<string, unknown>)[k];
        const next = { ...prev };
        if (Object.keys(rest).length === 0) delete next[variables.fieldId];
        else next[variables.fieldId] = rest;
        return next;
      });
      // RADIO-RACE-1: mirror the same key-drop on the synchronous ref (debounced path).
      {
        const refEdits = pendingFieldEditsRef.current[variables.fieldId];
        if (refEdits) {
          for (const k of Object.keys(variables)) if (k !== "fieldId") delete (refEdits as Record<string, unknown>)[k];
          if (Object.keys(refEdits).length === 0) delete pendingFieldEditsRef.current[variables.fieldId];
        }
      }
      setLastSavedAt(new Date());
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType]
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/unified/progress"]
      });
      // Keep the Where Is Everything aggregate fresh when a source location field is
      // edited from a card section (e.g. About You → Phone Location).
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, CATEGORY_SLUGS.WHERE_IS_EVERYTHING],
      });
      // s172: the estate summary is DERIVED from these entries (About You's multiple-wills answer
      // changes the probate base), and nothing invalidated it. The dashboard card appeared to
      // update only because its observer sets staleTime 60s and refetched on mount; the full
      // Financial Summary page sets none, inherits the global staleTime:Infinity, and served a
      // stale figure indefinitely. Same queryKey, two different answers on screen.
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "estate-summary"],
      });
    },
    onError: (error: Error, _data, context: { previous?: GenericEntry[] } | undefined) => {
      // The draft was never dropped (it's held until onSuccess), so the typed text is still on
      // screen; just roll the optimistic cache merge back to the pre-save snapshot.
      if (context?.previous !== undefined) queryClient.setQueryData(entriesQueryKey(), context.previous);
      // GATE-CLEAR-1: the planner declined the clear — drop the draft too so the source input snaps
      // back to its saved value (nothing changed, nothing cleared). A choice, not an error: no toast.
      if (error instanceof GateClearCancelledError) {
        delete pendingFieldEditsRef.current[_data.fieldId]; // RADIO-RACE-1: keep the ref in step
        setLocalEdits(prev => {
          if (!prev[_data.fieldId]) return prev;
          const next = { ...prev };
          delete next[_data.fieldId];
          return next;
        });
        return;
      }
      toast({
        title: "Error saving entry",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // ── Per-field "I don't have this" decline (s98, commit 2) ──────────────────────
  // A declined field's entry carries declined=true (no value); it counts as resolved
  // for completion/essentials (commit 1) and the input is replaced by "Not available".
  // `optimisticDeclined` mirrors the toggle locally so the input↔placeholder swap is
  // instant (the refetch then makes it authoritative). Keyed `${fieldId}:${rowId ?? ''}`.
  const declineKey = (fieldId: string, rowId: string | null) => `${fieldId}:${rowId ?? ''}`;
  const [optimisticDeclined, setOptimisticDeclined] = useState<Record<string, boolean>>({});
  // 🔴 s179 (G4 finding 1): the REASON needs the same optimistic mirror the flag has. Without it,
  // `optimisticDeclined` flips `declined` true on the click while `fieldDeclineReason` still reads
  // the STALE entryMap — so the control renders declined-with-no-reason and falls through to the
  // `dont_have` sentence until the refetch lands. A planner who picked "I would rather not store
  // this" was told "You said you do not have this". Same shape as the s154 anti-flicker fix, one
  // layer down: that made `declined` optimistic and left `reason` behind.
  const [optimisticReason, setOptimisticReason] = useState<Record<string, DeclineReason | null>>({});
  const [optimisticNote, setOptimisticNote] = useState<Record<string, string | null>>({});

  const isFieldDeclined = useCallback((fieldId: string, rowId: string | null): boolean => {
    const entry = entryMap[entryMapKey(fieldId, rowId)];
    // Value wins (s98, commit 3): a field that actually holds a value is NEVER shown as
    // declined — neither the planner nor the executor sees "Not available" over real data.
    // (Can't happen through the UI, but a document extraction could write a value onto a
    // previously-declined entry.) Single chokepoint: this flows to every display site —
    // cards, the legacy drawer, and ExpandableSectionLayout via getCellDeclined.
    if (entry?.value && entry.value.trim() !== "") return false;
    const k = declineKey(fieldId, rowId);
    if (k in optimisticDeclined) return optimisticDeclined[k];
    return entry?.declined === true;
  }, [optimisticDeclined, entryMap]);

  // s179: the reason behind a decline, read from the same entryMap chokepoint as isFieldDeclined
  // so the status line and the declined state can never disagree about which cell they describe.
  //
  // 🔴 G4 FIX. This shipped as `entryMap[\`:\`]` — the row key's `${fieldId}:${rowId}` interpolation
  // was destroyed by a scripted edit and the literal survived as a valid template. It compiled, it
  // typechecked, and it looked up a key that can never exist, so EVERY per-row decline resolved to
  // `null` and fell through to the `dont_have` sentence. That is the whole of G4 finding 1: the
  // reason reached the browser and the reader threw it away one line before the render.
  //
  // 🔴 And the optimistic override, which the first cut also missed: `optimisticDeclined` flips the
  // FLAG on the click while `entryMap` stays stale until the refetch, so without a matching mirror
  // for the REASON the control renders declined-with-no-reason for that window — the same
  // `dont_have` fallback, arriving by a second route. Both must flip together (s154 anti-flicker,
  // one layer down: that fix made `declined` optimistic and left `reason` behind).
  const fieldDeclineReason = useCallback((fieldId: string, rowId: string | null): DeclineReason | null => {
    const k = declineKey(fieldId, rowId);
    if (k in optimisticReason) return optimisticReason[k];
    const entry = entryMap[entryMapKey(fieldId, rowId)];
    return (entry?.declineReason as DeclineReason | undefined) ?? null;
  }, [optimisticReason, entryMap]);

  // s179 rebuild: the planner's location note, same optimistic-then-entryMap chokepoint as the reason.
  const fieldDeclineNote = useCallback((fieldId: string, rowId: string | null): string | null => {
    const k = declineKey(fieldId, rowId);
    if (k in optimisticNote) return optimisticNote[k];
    const entry = entryMap[entryMapKey(fieldId, rowId)];
    return (entry?.declineNote as string | undefined) ?? null;
  }, [optimisticNote, entryMap]);

  const setDeclinedMutation = useMutation({
    mutationFn: async ({ fieldId, sectionId, rowId, declined, reason, note }: { fieldId: string; sectionId: string; rowId: string | null; declined: boolean; reason?: DeclineReason; note?: string | null }) => {
      if (!clientData?.id || !category?.id) throw new Error("Category not loaded — please refresh the page");
      const res = await apiRequest("POST", `/api/clients/${clientData.id}/generic-entries`, {
        categoryId: category.id,
        sectionId,
        fieldId,
        rowId,
        declined,
        // s179: the reason rides the same single-writer generic-entries POST as the flag.
        ...(declined && reason ? { declineReason: reason } : {}),
        ...(declined && reason && note !== undefined ? { declineNote: note } : {}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Couldn't update" }));
        throw new Error(err.message || "Couldn't update");
      }
      return res.json();
    },
    onSuccess: () => {
      setLastSavedAt(new Date());
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress"] });
    },
    onError: (error: Error, vars) => {
      // Roll back the optimistic flag on failure.
      setOptimisticReason(prev => {
        const next = { ...prev };
        delete next[declineKey(vars.fieldId, vars.rowId)];
        return next;
      });
      setOptimisticNote(prev => {
        const next = { ...prev };
        delete next[declineKey(vars.fieldId, vars.rowId)];
        return next;
      });
      setOptimisticDeclined(prev => {
        const next = { ...prev };
        delete next[declineKey(vars.fieldId, vars.rowId)];
        return next;
      });
      toast({ title: "Couldn't update", description: error.message, variant: "destructive" });
    },
    onSettled: async (_d, _e, vars) => {
      // s154 1a — anti-flicker: AWAIT the entries refetch before dropping the optimistic override.
      // Previously the override was dropped the instant the MUTATION settled, while the invalidated
      // refetch was still in flight — so isFieldDeclined briefly read the STALE cache (old declined
      // value), oscillating the control (Not available → link → Not available). invalidateQueries
      // resolves once the active refetch completes, so the cache is authoritative before we drop.
      await queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType] });
      setOptimisticDeclined(prev => {
        const next = { ...prev };
        delete next[declineKey(vars.fieldId, vars.rowId)];
        return next;
      });
      // s179: the reason override drops on the SAME tick as the flag's, after the same awaited
      // refetch. Dropping them at different times would reintroduce the flicker in one field only.
      setOptimisticReason(prev => {
        const next = { ...prev };
        delete next[declineKey(vars.fieldId, vars.rowId)];
        return next;
      });
      setOptimisticNote(prev => {
        const next = { ...prev };
        delete next[declineKey(vars.fieldId, vars.rowId)];
        return next;
      });
    },
  });

  const toggleDecline = useCallback((field: GenericField, sectionId: string, rowId: string | null, declined: boolean, reason?: DeclineReason, note?: string | null) => {
    if (readOnly) return;
    setOptimisticDeclined(prev => ({ ...prev, [declineKey(field.id, rowId)]: declined }));
    // s179: mirror the REASON alongside the flag, so the status line never lags the state.
    setOptimisticReason(prev => ({ ...prev, [declineKey(field.id, rowId)]: declined ? (reason ?? null) : null }));
    setOptimisticNote(prev => ({ ...prev, [declineKey(field.id, rowId)]: declined ? (note ?? null) : null }));
    setDeclinedMutation.mutate({ fieldId: field.id, sectionId, rowId, declined, reason, note });
  }, [readOnly, setDeclinedMutation]);

  // OTHER-SPECIFY-1 (s155, D-155.6): persist specify_text for an "Other" select via the standard
  // generic-entries write. NO value sent → the select value is untouched (and the server's force-null
  // guard reads the stored value to confirm it's still an "Other" option). NON-required, so no
  // completion/ERS invalidation beyond the entries refetch.
  const saveSpecifyMutation = useMutation({
    mutationFn: async ({ fieldId, sectionId, rowId, specifyText }: { fieldId: string; sectionId: string; rowId: string | null; specifyText: string }) => {
      if (!clientData?.id || !category?.id) throw new Error("Category not loaded — please refresh the page");
      const res = await apiRequest("POST", `/api/clients/${clientData.id}/generic-entries`, {
        categoryId: category.id, sectionId, fieldId, rowId, specifyText,
      });
      if (!res.ok) { const err = await res.json().catch(() => ({ message: "Couldn't save" })); throw new Error(err.message || "Couldn't save"); }
      return res.json();
    },
    onSuccess: () => setLastSavedAt(new Date()),
    onError: (error: Error) => toast({ title: "Couldn't save", description: error.message, variant: "destructive" }),
    onSettled: async () => { await queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType] }); },
  });

  // Card-path notes save-status (s79): the Detail popup's notes route through the
  // shared debounced upsert. When it settles while a notes save is armed, surface
  // "Saved" on the popup. The arm ref prevents cross-talk with other card saves.
  const prevUpsertPendingRef = useRef(false);
  useEffect(() => {
    if (prevUpsertPendingRef.current && !upsertEntryMutation.isPending && notesSaveArmedRef.current) {
      notesSaveArmedRef.current = false;
      markNotesSaved();
    }
    prevUpsertPendingRef.current = upsertEntryMutation.isPending;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsertEntryMutation.isPending]);

  // Per-field debounced autosave (session 73): card fields persist ~800ms after
  // the last keystroke, so there is no Save button. Blur flushes immediately.
  const fieldDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const handleFieldChange = (fieldId: string, key: string, value: any) => {
    if (readOnly) return;
    // Clear AI highlight on first edit
    if (key === 'value') {
      setFieldHighlights(prev => {
        if (!prev[fieldId]) return prev;
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
    setLocalEdits(prev => ({
      ...prev,
      [fieldId]: {
        ...(prev[fieldId] || {}),
        [key]: value,
      },
    }));
    // RADIO-RACE-1: mirror into the ref SYNCHRONOUSLY so a same-tick blur (radio/yes_no) reads it.
    pendingFieldEditsRef.current[fieldId] = { ...(pendingFieldEditsRef.current[fieldId] || {}), [key]: value };
    // Debounced autosave — capture (key, value) directly to avoid stale-closure reads.
    if (fieldDebounceRef.current[fieldId]) clearTimeout(fieldDebounceRef.current[fieldId]);
    fieldDebounceRef.current[fieldId] = setTimeout(() => {
      delete fieldDebounceRef.current[fieldId];
      upsertEntryMutation.mutate({ fieldId, [key]: value } as Partial<GenericEntry> & { fieldId: string });
      // FIELDFLICKER-1 (s142): the draft is NOT dropped here — it's held until the mutation's
      // onSuccess (after the optimistic cache merge + server confirm). The controlled input thus
      // reads the typed value from localEdits for the WHOLE save and never falls back to a
      // mid-flight cache read — which caused the reported success-path flicker AND a 1-frame
      // error-path blip. Drop-in-onSuccess mirrors the table path (upsertTableEntryMutation).
    }, 800);
  };

  const handleFieldBlur = async (fieldId: string) => {
    if (readOnly) return;
    // Blur flushes immediately — cancel any pending debounce for this field.
    if (fieldDebounceRef.current[fieldId]) {
      clearTimeout(fieldDebounceRef.current[fieldId]);
      delete fieldDebounceRef.current[fieldId];
    }
    // Clear AI highlight on blur even if no edit (treat focus+blur as "confirmed")
    setFieldHighlights(prev => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
    // RADIO-RACE-1: read the pending edit from the SYNCHRONOUS ref (fresh even when onChange fired
    // in this same tick), falling back to the state map. Fixes discrete-control saves dropping.
    const edits = pendingFieldEditsRef.current[fieldId] ?? localEdits[fieldId];
    if (!edits || Object.keys(edits).length === 0) return;

    try {
      await upsertEntryMutation.mutateAsync({ fieldId, ...edits });
      delete pendingFieldEditsRef.current[fieldId];
      setLocalEdits(prev => {
        const newEdits = { ...prev };
        delete newEdits[fieldId];
        return newEdits;
      });
    } catch {
      // error handled by mutation's onError (toast)
    }
  };

  const saveConditionsMutation = useMutation({
    mutationFn: async ({ fieldId, conditions }: { fieldId: string; conditions: ConditionRule | null }) => {
      if (!clientData?.id) throw new Error("No client");
      const res = await apiRequest("PATCH", `/api/clients/${clientData.id}/custom-fields/${fieldId}`, { conditions });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories", category?.id, "sections"] });
      toast({ title: "Rule saved", description: "Conditional logic has been updated." });
      setConditionField(null);
      setConditionDraft(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error saving rule", description: error.message, variant: "destructive" });
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      const entry = entryMap[fieldId];
      if (!entry) throw new Error("Entry not found");
      // GATE-CLEAR-1: deleting a source field closes its gate — confirm any dependent clears first.
      // DELETE has no body, so the confirm rides as a ?confirmGateClear query param.
      await writeWithGateClear((confirm) =>
        apiRequest("DELETE", `/api/generic-entries/${entry.id}${confirm ? "?confirmGateClear=true" : ""}`),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType]
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/unified/progress"]
      });
      setDeleteDialogOpen(false);
      setSelectedFieldId(null);
    },
    onError: (error: Error) => {
      // GATE-CLEAR-1: planner declined the clear — nothing was deleted (delete isn't optimistic).
      // Close the delete dialog and stay silent (a choice, not an error).
      if (error instanceof GateClearCancelledError) {
        setDeleteDialogOpen(false);
        setSelectedFieldId(null);
        return;
      }
      toast({
        title: "Error deleting entry",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDeleteClick = (fieldId: string) => {
    setSelectedFieldId(fieldId);
    setDeleteDialogOpen(true);
  };

  const saveSectionOrderMutation = useMutation({
    mutationFn: async (orders: Array<{ sectionId: string; sortOrder: number }>) => {
      await apiRequest("PUT", `/api/clients/${clientData?.id}/section-order`, { orders });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ["/api/generic-categories", category?.id, "sections"] 
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error saving section order",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    if (readOnly) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sectionsWithFields.findIndex(s => s.id === active.id);
    const newIndex = sectionsWithFields.findIndex(s => s.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(sectionsWithFields, oldIndex, newIndex);
      const orders = newOrder.map((s, idx) => ({ sectionId: s.id, sortOrder: idx }));
      saveSectionOrderMutation.mutate(orders);
    }
  };

  // s99: section-level Hide Category (hidden-subcategories) mutations + handlers
  // removed — the N/A × (client_section_na) is now the single tuck-away path.

  // Delete a client custom field
  const deleteCustomFieldMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      if (!clientData?.id) throw new Error("No client");
      await apiRequest("DELETE", `/api/clients/${clientData.id}/custom-fields/${fieldId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "custom-fields"] });
      setDeleteColumnConfirm(null);
    },
  });

  // Rename a client custom field
  const renameCustomFieldMutation = useMutation({
    mutationFn: async ({ fieldId, name }: { fieldId: string; name: string }) => {
      if (!clientData?.id) throw new Error("No client");
      const res = await apiRequest("PATCH", `/api/clients/${clientData.id}/custom-fields/${fieldId}`, { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "custom-fields"] });
    },
  });

  const hideFieldMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      if (!clientData?.id || !category?.id) throw new Error("Missing client");
      const field = [...fields, ...customFields].find(f => f.id === fieldId);
      if (!field?.sectionId) throw new Error("Field not found");
      const res = await apiRequest("POST", `/api/clients/${clientData.id}/generic-entries`, {
        categoryId: category.id,
        sectionId: field.sectionId,
        fieldId,
        isHidden: true,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType],
      });
    },
    onError: (error: Error) => {
      toast({ title: "Error hiding item", description: error.message, variant: "destructive" });
    },
  });

  const unhideFieldMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      const entry = entryMap[fieldId];
      if (!entry) return;
      await apiRequest("PATCH", `/api/generic-entries/${entry.id}`, { isHidden: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType],
      });
    },
    onError: (error: Error) => {
      toast({ title: "Error restoring item", description: error.message, variant: "destructive" });
    },
  });

  const [pendingFieldInstances, setPendingFieldInstances] = useState<Record<string, string[]>>({});
  const [renamingFieldId, setRenamingFieldId] = useState<string | null>(null);
  const [customizingSectionId, setCustomizingSectionId] = useState<string | null>(null);
  const [addingFieldName, setAddingFieldName] = useState("");
  // Add a new blank custom field in a section
  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): each fire mints a custom field row.
  const addBlankFieldFlight = useSingleFlight();

  const addBlankFieldMutation = useMutation({
    mutationFn: async ({ sectionId, name }: { sectionId: string; name: string }) => {
      if (!clientData?.id) throw new Error("No client");
      const res = await apiRequest("POST", `/api/clients/${clientData.id}/custom-fields/blank`, { sectionId, name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "custom-fields"] });
      setAddingFieldName("");
    },
    onError: (error: Error) => {
      toast({ title: "Error adding item", description: error.message, variant: "destructive" });
    },
  });

  // Reorder fields within a section
  const reorderFieldsMutation = useMutation({
    mutationFn: async (updates: Array<{ id: string; sortOrder: number }>) => {
      if (!clientData?.id) throw new Error("No client");
      await apiRequest("PUT", `/api/clients/${clientData.id}/fields/reorder`, { updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "custom-fields"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error reordering items", description: error.message, variant: "destructive" });
    },
  });

  // Add row mutation (table layout): creates an anchor entry for a new rowId
  const addRowFlight = useSingleFlight();

  const addRowMutation = useMutation({
    mutationFn: async ({ sectionId }: { sectionId: string; sectionName: string }) => {
      if (!clientData?.id || !category?.id) throw new Error("Missing client or category");
      const section = sectionsWithFields.find(s => s.id === sectionId);
      const firstField = section?.fields[0];
      if (!firstField) throw new Error("Section has no fields");
      const rowId = crypto.randomUUID();
      const res = await apiRequest("POST", `/api/clients/${clientData.id}/generic-entries`, {
        categoryId: category.id,
        sectionId,
        fieldId: firstField.id,
        rowId,
        value: "",
        isComplete: false,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to add row" }));
        throw new Error(err.message || "Failed to add row");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientData?.id, "generic-entries", category?.categoryType],
      });
    },
    onError: (error: Error) => {
      toast({ title: "Error adding row", description: error.message, variant: "destructive" });
    },
  });

  const handleAddRow = (sectionId: string, sectionName: string) => {
    if (readOnly) {
      toast({ title: "Read-only", description: "Cannot add rows in read-only mode", variant: "destructive" });
      return;
    }
    // SINGLE-FLIGHT-PRIMITIVE-1 (s170). The rowId is a fresh `crypto.randomUUID()` minted inside
    // the mutationFn, so two fires = two genuinely-distinct blank rows and no server backstop can
    // collapse them. One lock covers both add affordances on this page (the table "Add Row" button
    // and the expandable layout's "Add <record>"), which is correct — they are the same action.
    addRowFlight.runQuiet(() => addRowMutation.mutateAsync({ sectionId, sectionName }));
  };

  const getFieldValue = (fieldId: string, key: string): string => {
    const entry = entryMap[fieldId];
    const localEdit = localEdits[fieldId];
    if (key === 'value') {
      return (localEdit?.value ?? entry?.value ?? "") as string;
    }
    if (key === 'notes') {
      return (localEdit?.notes ?? entry?.notes ?? "") as string;
    }
    return "";
  };

  if (!clientData) {
    return <NoClientSelected />;
  }

  if (categoryLoading || sectionsLoading || entriesLoading) {
    return <LoadingState />;
  }

  if (!category) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <p>Category not found: {categorySlug}</p>
        <p className="text-sm mt-2">This category may not exist or hasn't been migrated yet.</p>
      </div>
    );
  }

  // Fixed cols (completed + details + optional delete) plus the flexible content col
  const colSpan = readOnly ? 3 : 4;

  return (
    <div className="space-y-4">
      <AutofillHoneypot />
      {/* Hidden file input for per-field upload — gated: only render if a visible section supports uploadFromComputer */}
      {filteredSectionsWithFields.some(s => getAffordances(s.slug).perField.uploadFromComputer) && (
        <input
          ref={fileUpload.fileInputRef}
          type="file"
          className="hidden"
          accept={DOCUMENT_ACCEPT}
          onChange={fileUpload.handleFileChange}
        />
      )}
      <WouterLink href="/dashboard" className="inline-flex items-center gap-1.5 text-base font-medium text-foreground hover:underline transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </WouterLink>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <PageHeader
          icon={getIcon(category.icon)}
          title={category.name}
          description={
            categorySlug === CATEGORY_SLUGS.ABOUT_YOU
              ? 'Who you are. The basics anyone settling your estate will need.'
              : categorySlug === CATEGORY_SLUGS.MY_WISHES_AND_LEGACY
              ? 'Manage your wishes, letters, and instructions'
              : categorySlug === CATEGORY_SLUGS.LEGAL
              ? 'Your will, powers of attorney, and legal documents.'
              : categorySlug === CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE
              ? 'Accounts, policies, and the people who help manage them.'
              : categorySlug === CATEGORY_SLUGS.ASSETS
              ? 'What you own, and where to find it.'
              : categorySlug === CATEGORY_SLUGS.PERSONAL_INFORMATION
              ? 'About you — the things your family might not know offhand.'
              : categorySlug === CATEGORY_SLUGS.CHILDREN_AND_DEPENDENTS
              ? 'The people who depend on you.'
              : categorySlug === CATEGORY_SLUGS.FAMILY_FRIENDS_AND_PETS
              ? "The people and pets who'd want to know."
              : categorySlug === CATEGORY_SLUGS.ONLINE_PRESENCE
              ? 'Your accounts, subscriptions, and digital life.'
              : categorySlug === CATEGORY_SLUGS.WHERE_IS_EVERYTHING
              ? "Where your things are, for the people who'll need to find them."
              : `Manage your ${category.name.toLowerCase()} information`
          }
        />
        <div className="flex items-center gap-2">
          {categorySlug === CATEGORY_SLUGS.ABOUT_YOU && !readOnly && (
            <SectionSaveStatus
              isPending={upsertEntryMutation.isPending}
              isError={upsertEntryMutation.isError}
              lastSavedAt={lastSavedAt}
            />
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={searchInputRef}
              placeholder="Search fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9 w-44 autofill-detect"
              autoComplete="new-password"
              name={`field-filter-${searchInputId}`}
              type="search"
              data-testid="input-search-fields"
            />
            <button
              type="button"
              onClick={() => {
                if (searchInputRef.current) searchInputRef.current.value = '';
                setSearchQuery('');
              }}
              className={`absolute right-2 top-2.5 text-muted-foreground hover:text-foreground transition-opacity ${searchQuery ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-label="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* s100: category-level key for the shaded recommended rows — shown once under
          the header on any category that contains ≥1 section with a shaded set, so the
          per-section explanation isn't repeated on every section. */}
      {categoryRendersShadedRow && (
        <p className="text-[13px] text-warm-taupe -mt-2 mb-1 flex items-center gap-1.5" data-testid="shaded-key">
          <span
            className="inline-block h-3 w-3 rounded-[2px] border-l-[3px]"
            style={{ backgroundColor: SAGE[50], borderLeftColor: SAGE[600] }}
            aria-hidden="true"
          />
          Shaded rows are recommended for everyone.
        </p>
      )}

      {/* "Where Is Everything" is a derived read-only view (no data-entry sections of
          its own) — render the aggregator component standalone instead of the section
          table. The editable "Anything else" band lives inside it (the still-active
          where-are-they section, read by slug; hidden from My Wishes & Legacy). */}
      {categorySlug === CATEGORY_SLUGS.WHERE_IS_EVERYTHING && clientData?.id && (
        <WhereIsEverything clientId={clientData.id} readOnly={readOnly} />
      )}

      {/* s111 Build 6 polish: Protect Your Access renders a bespoke two-section page (the
          .oesif setup home) instead of the flat generic card. Completion is unaffected —
          the component writes the same generic_entries / .oesif stores the pill + essential
          #1 read. Generic section table is suppressed for this category below. */}
      {categorySlug === CATEGORY_SLUGS.PROTECT_YOUR_ACCESS && clientData?.id && !readOnly && (
        <ProtectYourAccess
          clientId={clientData.id}
          readOnly={readOnly}
          secureFileSetupAt={clientData.secureFileSetupAt ?? null}
        />
      )}

      {/* s171 F-S170.1 — the read-only fallback for a planner-only category.
          `ProtectYourAccess` calls `useSensitiveDataSession()`, which THROWS when the provider is
          absent; the executor shell mounts no providers, so the un-gated mount above took down the
          whole app tree (top-level boundary, shell and all) the moment an executor followed their
          own sidebar link. The `!readOnly` gate matches the precedent every other Cat A surface in
          this file already follows (the `SensitiveFieldInput` branch above).
          This panel is REQUIRED, not decorative: the generic section table below is suppressed for
          this category, so without it a read-only viewer gets a header over an empty page. */}
      {categorySlug === CATEGORY_SLUGS.PROTECT_YOUR_ACCESS && readOnly && (
        <Card data-testid="planner-only-category-fallback">
          <CardContent className="p-6 flex gap-3 items-start">
            <Lock className="h-5 w-5 text-warm-taupe shrink-0 mt-0.5" aria-hidden="true" />
            <div className="space-y-1.5">
              <p className="font-medium">This section isn't part of the estate plan you can view.</p>
              <p className="text-sm text-muted-foreground">
                Protect Your Access is where the plan owner set up their secure file — a separate,
                encrypted file they kept themselves. The passwords and unlock codes they set up here
                are kept only in that file, never on our servers. If they left you that file, you'll need
                it and its passcode, if they set one. If you can't find it, check the locations listed
                under Where Is Everything.
              </p>
            </div>
          </CardContent>
        </Card>
      )}


      {/* s84: the wizard-residue "Get Started" splash (EmptyCategoryState) was removed —
          the section accordion is the single category surface (each section carries its
          own empty-state guide). This also fixes the latched-splash bug (a staleTime:
          Infinity entries query holding [] during SPA nav kept the splash sticky). */}
      {categorySlug !== CATEGORY_SLUGS.WHERE_IS_EVERYTHING && categorySlug !== CATEGORY_SLUGS.PROTECT_YOUR_ACCESS && (
        <Card>
          <CardContent className="p-0">
            {/* s194: DndContext renders its own live-region + instructions <div>s, so it must
                sit OUTSIDE <table> — inside <TableBody> those divs become children of <tbody>
                and React fires validateDOMNesting (s71). SortableContext emits no DOM and stays. */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
            <Table style={{ width: "100%", tableLayout: "fixed" }} containerClassName="overflow-visible">
            <colgroup>
              <col style={{ width: `${FIXED_COLUMN_WIDTHS.completed}px` }} />
              {!readOnly && <col style={{ width: `${FIXED_COLUMN_WIDTHS.delete}px` }} />}
              <col style={{ width: `${FIXED_COLUMN_WIDTHS.details}px` }} />
              <col />{/* flexible content column */}
            </colgroup>
              <TableBody>
                {searchQuery.trim() && filteredSectionsWithFields.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground">
                      No fields match &ldquo;{searchQuery}&rdquo;
                    </TableCell>
                  </TableRow>
                )}
                {/* Category-level auto-NA (Children & Dependents): one treatment for the
                    whole category; gated sections are filtered out of renderSections. */}
                {isCategoryNA && categoryGate && !searchQuery.trim() && (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="p-0 bg-muted/5">
                      <SectionNotApplicable
                        title={category?.name ?? "This section"}
                        onMarkApplicable={() => markGateApplicable(categoryGate)}
                        isPending={situationFlagMutation.isPending}
                        readOnly={readOnly}
                      />
                    </TableCell>
                  </TableRow>
                )}
                  <SortableContext
                    items={renderSections.map(s => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {renderSections.map(section => {
                      const isExpanded = rendersExpanded(categorySlug) || expandedSectionId === section.id;
                      const visibleFields = section.fields.filter(f =>
                        evaluateShowIf(f.conditions as FieldConditions | null, liveEntryMap)
                      );
                      const isSectionReadOnly = !!readOnly;
                      const isCardLayout = section.layoutType === "card";
                      const isExpandable = section.layoutType === "expandable";

                      // Section-level auto-NA (pets / property / business): when the
                      // gate is false, the section header stays but its body collapses
                      // to a "Mark as applicable" affordance. (Children is handled at
                      // category level above and never reaches here.)
                      const sectionNaGate = SECTION_NA_GATE[section.slug];
                      // s85: manual N/A (D-84.8) folds into the same `isSectionNA`
                      // variable, so every existing body-suppression / nag /
                      // green-check guard honors it automatically. Auto and manual
                      // never overlap (eligible set excludes gated sections), so a
                      // truthy `sectionNaGate` always means the auto path below.
                      const isManualNA = sectionNa.naSlugs.has(section.slug);
                      // s177 (A2): the ONE eligibility predicate — it subtracts the permanently
                      // ineligible sections (D-177.8 your-executor, D-177.14 government-ids) so a
                      // ruled exclusion cannot be defeated by appending a slug to the eligible set.
                      const isManualNaEligible = !readOnly && isManualNaEligibleSection(section.slug);
                      // s162 (CATEGORY-GATE-DEFORK-1): the RENDER path goes through the shared
                      // predicate too — this is the site that actually draws the tucked-away
                      // panel, so a fork here is what a user sees. flag-OR-rows applies: a
                      // populated section never collapses to "doesn't apply to you right now".
                      const isSectionNA = (!!sectionNaGate && isSectionAutoNA(categorySlug, section.slug, situationFlags, sectionSlugsWithRows)) || isManualNA;

                      // Build grouped fields for card layout (pure function, no hook).
                      // Ungrouped fields (fieldGroup == null) always appear first,
                      // then named groups in the order they first appear in sortOrder.
                      const groupedFields = (() => {
                        if (!isCardLayout) return [];
                        const sorted = [...section.fields].sort((a, b) => a.sortOrder - b.sortOrder);
                        const ungrouped: GenericField[] = [];
                        const namedGroups = new Map<string, GenericField[]>();
                        const namedGroupOrder: string[] = [];
                        for (const f of sorted) {
                          if (!f.fieldGroup) {
                            ungrouped.push(f);
                          } else {
                            if (!namedGroups.has(f.fieldGroup)) {
                              namedGroups.set(f.fieldGroup, []);
                              namedGroupOrder.push(f.fieldGroup);
                            }
                            namedGroups.get(f.fieldGroup)!.push(f);
                          }
                        }
                        const groups: Array<{ groupName: string | null; fields: GenericField[] }> = [];
                        if (ungrouped.length > 0) {
                          groups.push({ groupName: null, fields: ungrouped });
                        }
                        for (const name of namedGroupOrder) {
                          groups.push({ groupName: name, fields: namedGroups.get(name)! });
                        }
                        return groups;
                      })();

                      // P2 #3: fractional progress for the header nudge. Same shared
                      // source as the green check. N/A sections carry no fraction.
                      const headerProgress = isSectionNA
                        ? null
                        : sectionProgress(completionSectionOf(section), entries, { removedRowIds: executorRemovedRowIds });
                      // "empty" = no user-touched entry in the section — drives the not_started
                      // vs attention status (no new query — reuses the page's `entries`).
                      //
                      // 🔴 s178 (CONSISTENCY-PASS-1, member d): this asks `entryUserActioned`, the
                      // canonical D-164 predicate, not `isSeeded !== true`. The old test was a
                      // reader the s164 provenance migration missed: since decline/undo stopped
                      // touching `is_seeded`, a planner who DECLINED every field on a still-seeded
                      // section had acted on it and the section still read "Nothing recorded yet"
                      // rather than counting what was outstanding. A decline is an answer
                      // (D-177.17c), so it is a touch.
                      const sectionFieldIds = new Set(section.fields.map((f) => f.id));
                      const hasTouchedEntry = entries.some(
                        (e) => sectionFieldIds.has(e.fieldId) && entryUserActioned(e),
                      );
                      // s97: single status derivation (complete / attention / not_started
                      // / na / no_status), driven off the SAME shared progress + N/A flags.
                      const headerStatus = computeSectionStatus({
                        isNA: isSectionNA,
                        progress: headerProgress,
                        hasUserData: hasTouchedEntry,
                      });
                      // s97 (6C): section-specific helper copy, shown only for
                      // attention / not_started. An EXPANDED expandable section renders
                      // it inside its body (§2.4), so the header line is suppressed only
                      // in that case — collapsed sections still show it under the name.
                      const helperText =
                        headerStatus.status === "attention" || headerStatus.status === "not_started"
                          ? sectionHelperText(section.slug)
                          : undefined;
                      // s99 (D-99.2): collapsed-row data preview — the identifying-field
                      // value of each USER-TOUCHED row (untouched scaffold defaults are
                      // excluded so suggestions don't read as real data). Override field
                      // key where curated, else the section's first basic field. N/A
                      // sections show nothing.
                      // s162 (RECORD-IDENTITY-DISPLAY-1): a section may name records with SEVERAL
                      // fields (the two person sections split first/last at s156), so the preview
                      // joins the parts — "Avery Barber", not "Avery". Single-key sections take
                      // the same path with one part and are unchanged.
                      // s199: the identity is declared on the fields this page already holds.
                      const previewFieldKeys = identityKeysFromFields(section.fields);
                      const previewFields = previewFieldKeys
                        .map((k) => section.fields.find((f) => f.fieldKey === k))
                        .filter((f): f is NonNullable<typeof f> => !!f);
                      const previewField = previewFields[0];
                      let dataPreview = "";
                      if (previewField && !isSectionNA) {
                        const labels = (tableSectionRows[section.id] || [])
                          .filter((rowId) =>
                            section.fields.some((f) => {
                              const e = entryMap[entryMapKey(f.id, rowId)];
                              return e?.isSeeded === false && (e.value ?? "").trim() !== "";
                            }),
                          )
                          .map((rowId) => composeRecordLabel(previewFields.map((pf) => formatPreviewValue(
                            entryMap[entryMapKey(pf.id, rowId)]?.value,
                            entryMap[entryMapKey(pf.id, rowId)]?.specifyText, // OTHER-SPECIFY-1: "Other — {detail}" label
                          ))));
                        dataPreview = composeSectionPreview(labels);
                      }

                      return (
                        <Fragment key={section.id}>
                          <SortableSectionRow
                            section={section}
                            anchorId={`section-anchor-${section.slug}`}
                            allowReorder={category?.categoryType === 'custom'}
                            isExpanded={isExpanded}
                            noChevron={rendersExpanded(categorySlug)}
                            onToggle={rendersExpanded(categorySlug) ? () => {} : () => {
                              if (customizingSectionId === section.id) setCustomizingSectionId(null);
                              toggleSection(section.id);
                            }}
                            colSpan={colSpan}
                            readOnly={isSectionReadOnly}
                            onCustomize={(!readOnly && isCardLayout && getAffordances(section.slug).section.customize) ? () => setCustomizingSectionId(customizingSectionId === section.id ? null : section.id) : undefined}
                            isCustomizing={customizingSectionId === section.id}
                            hasSummary={!!summaryBySectionId[section.id]}
                            onViewSummary={() => setViewSummarySectionId(section.id)}
                            progress={headerProgress}
                            status={headerStatus}
                            dataPreview={dataPreview}
                            onMarkNotApplicable={(isManualNaEligible && !isSectionNA) ? () => requestMarkNa(section.slug) : undefined}
                          />

                          {/* s97 Fix 5: "new rows added" notice after a fan-out populate,
                              directly under the header so it's visible without scrolling.
                              (The section-level analyzing/error banner was removed with
                              Fill from Document — DB-45.) */}
                          {newRowsBySection[section.id] > 0 && (
                            <TableRow key={`extract-${section.id}`}>
                              <TableCell colSpan={colSpan} className="p-2 bg-muted/5">
                                <div className="space-y-2">
                                  {newRowsBySection[section.id] > 0 && (
                                    <div
                                      className="flex items-center gap-3 rounded-lg border border-sage-200 px-4 py-3"
                                      style={{ backgroundColor: SAGE[50] }}
                                      data-testid={`new-rows-added-${section.slug}`}
                                    >
                                      <Sparkles className="h-4 w-4 shrink-0" style={{ color: SAGE[600] }} />
                                      <span className="text-sm text-sage-900">
                                        <span className="font-semibold">{newRowsBySection[section.id]} new row{newRowsBySection[section.id] !== 1 ? "s" : ""} added</span> — scroll down to review them
                                      </span>
                                      <span className="ml-auto flex items-center gap-3">
                                        <button
                                          className="text-xs font-medium text-sage-800 hover:text-sage-900 underline"
                                          onClick={() => scrollToSection(section.id, section.slug)}
                                          data-testid={`scroll-new-rows-${section.slug}`}
                                        >
                                          Scroll to them
                                        </button>
                                        <button
                                          aria-label="Dismiss"
                                          className="text-muted-foreground hover:text-foreground"
                                          onClick={() => clearNewRows(section.id)}
                                          data-testid={`dismiss-new-rows-${section.slug}`}
                                        >
                                          <XIcon className="h-4 w-4" />
                                        </button>
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Section-level N/A affordance — replaces the body. Auto-NA
                              (gate, s73) routes "Bring it back" to the situation flag;
                              manual N/A (s85) routes it to the section-na delete. */}
                          {isExpanded && isSectionNA && (
                            <TableRow key={`na-${section.id}`}>
                              <TableCell colSpan={colSpan} className="p-0 bg-muted/5">
                                <SectionNotApplicable
                                  title={section.name}
                                  onMarkApplicable={() => sectionNaGate
                                    ? markGateApplicable(sectionNaGate)
                                    : sectionNa.bringBack(section.slug)}
                                  isPending={situationFlagMutation.isPending || sectionNa.isPending}
                                  readOnly={readOnly}
                                />
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Where Is Everything — injected above the Where Are They? table */}
                          {isExpanded && section.slug === SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY && clientData?.id && (
                            <TableRow key={`wie-${section.id}`}>
                              <TableCell colSpan={colSpan} className="p-3 bg-muted/5">
                                <WhereIsEverything clientId={clientData.id} />
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Expandable layout (Build 1): compact basic-field row that expands into the full record */}
                          {isExpanded && !isSectionNA && isExpandable && EXPANDABLE_LAYOUT_CONFIG[section.slug] && clientData?.id && (
                            <TableRow key={`expandable-${section.id}`}>
                              <TableCell colSpan={colSpan} className="p-0 bg-muted/5">
                                {pendingExtractionBySection[section.id] && (
                                  <PendingExtractionBanner
                                    pending={pendingExtractionBySection[section.id]}
                                    onReview={() => setConfirmingExtractionSection(section.id)}
                                    onDiscard={() => discardPendingExtraction(section.id)}
                                  />
                                )}
                                <ExpandableSectionLayout
                                  section={section}
                                  config={EXPANDABLE_LAYOUT_CONFIG[section.slug]}
                                  helperText={helperText}
                                  rowIds={[
                                    ...(tableSectionRows[section.id] || []).filter(rowId => !hiddenTableRows[section.id]?.has(rowId)),
                                    // Phase 4R: pending "record you found" rows render inline with the normal widgets.
                                    ...(pendingExecutorRows[section.id] ?? []),
                                  ]}
                                  readOnly={isSectionReadOnly}
                                  clientId={clientData.id}
                                  secureFileSetupAt={clientData.secureFileSetupAt ?? null}
                                  getCellValue={getTableCellValue}
                                  getCellIsSeeded={(fieldId, rowId) => entryMap[entryMapKey(fieldId, rowId)]?.isSeeded === true}
                                  getCellDeclined={(fieldId, rowId) => isFieldDeclined(fieldId, rowId)}
                                  getCellDeclineReason={(fieldId, rowId) => fieldDeclineReason(fieldId, rowId)}
                                  getCellDeclineNote={(fieldId, rowId) => fieldDeclineNote(fieldId, rowId)}
                                  onSetCellDeclined={(field, rowId, declined, reason, note) => toggleDecline(field, section.id, rowId, declined, reason, note)}
                                  getCellSpecify={(fieldId, rowId) => entryMap[entryMapKey(fieldId, rowId)]?.specifyText ?? ""}
                                  onSaveSpecify={(field, rowId, text) => saveSpecifyMutation.mutate({ fieldId: field.id, sectionId: section.id, rowId, specifyText: text })}
                                  expandedRowIds={(expandedRowsBySection[section.id] ?? EMPTY_EXPANDED_ROWS) as Set<string>}
                                  onSetRowExpanded={(rowId, open) => setRowExpanded(section.id, rowId, open)}
                                  onCellChange={handleTableCellChange}
                                  onCellBlur={(field, rowId) => handleTableCellBlur(field, rowId, section.id)}
                                  onSaveFieldValue={(field, rowId, value) =>
                                    // GATE-CLEAR-1: a declined clear rejects with GateClearCancelledError;
                                    // the mutation's onError already rolled back the source cell, so swallow
                                    // it here (real errors still propagate).
                                    upsertTableEntryMutation.mutateAsync({ field, rowId, sectionId: section.id, value })
                                      .catch((e) => { if (!(e instanceof GateClearCancelledError)) throw e; })
                                  }
                                  renderDrawerExtra={
                                    // Planner-only invite button (legal-your-executor); stays !readOnly-gated. The
                                    // executor's own row chrome now rides renderRowActions below, not here.
                                    section.slug === SECTION_SLUGS.LEGAL_YOUR_EXECUTOR
                                      ? (rowId) => {
                                          const ex = executorInviteByRowId.get(rowId);
                                          if (!ex) return null;
                                          // s156 G (ruling 10): an invitation must not go out before the planner has
                                          // chosen what this executor may SEE. Resolve this row's access_level and
                                          // let the button gate on it — the value, not merely the field's presence.
                                          const alField = section.fields.find((f) => f.fieldKey === FIELD_KEYS.ACCESS_LEVEL);
                                          const accessLevel = alField
                                            ? (entries.find((en) => en.rowId === rowId && en.fieldId === alField.id)?.value ?? null)
                                            : null;
                                          return <ExecutorInviteButton clientId={clientData.id} executor={ex} accessLevel={accessLevel} />;
                                        }
                                      : undefined
                                  }
                                  fieldsEditableWhenReadOnly={executorEditActive && section.writeMode !== "append"}
                                  getFieldEdit={executorEditActive
                                    ? (fieldId, rowId) => fieldEditOf(executorOverlayFor(section.slug), rowId, fieldId)
                                    : undefined}
                                  renderRowActions={executorEditActive && section.writeMode !== "append"
                                    ? (rowId) => (
                                        isPendingExecutorRow(rowId) ? (
                                          <ExecutorPendingRowActions
                                            rowId={rowId}
                                            saving={savingPendingRowId === rowId}
                                            onSave={() => savePendingExecutorRow(section, rowId)}
                                            onCancel={() => dropPendingExecutorRow(section.id, rowId)}
                                          />
                                        ) : (
                                          <ExecutorRowActions
                                            clientId={clientData.id}
                                            sectionSlug={section.slug}
                                            rowId={rowId}
                                            fieldKey={section.fields[0]?.fieldKey}
                                            onChanged={() => invalidateExecutorOverlay(section.slug)}
                                          />
                                        )
                                      )
                                    : undefined}
                                  removedRowIds={executorRemovedRowIds}
                                  onUndoRemoval={executorEditActive ? (rowId) => undoExecutorRemoval(section.slug, rowId) : undefined}
                                  removedByLabel={executorEditActive ? undefined : "removed by the executor"}
                                  getRowIdentity={(rowId) => getTableCellValue(section.fields[0]?.id ?? "", rowId)}
                                  onAddRow={() => handleAddRow(section.id, section.name)}
                                  addRowDisabled={addRowFlight.inFlight}
                                  onDeleteRow={(rowId) => deleteRowMutation.mutate({ rowId, sectionId: section.id })}
                                  onHideRow={(rowId) => setHiddenTableRows(prev => {
                                    const sectionHidden = new Set(prev[section.id] ?? []);
                                    sectionHidden.add(rowId);
                                    return { ...prev, [section.id]: sectionHidden };
                                  })}
                                  onUploadFile={(rowId, file) => handleExpandableRowUpload(section, rowId, file)}
                                  extractingRowIds={new Set((tableSectionRows[section.id] || []).filter(rowId => isExtracting(section.id, rowId)))}
                                  getRowDocCount={(rowId) => getRowDocCount(section, rowId)}
                                  documentsByKey={fieldDocuments}
                                  onEnsureRowDocs={(rowId) => ensureRowDocuments(section, rowId)}
                                  onDownloadDocument={handleDownloadDocument}
                                  onViewFile={setViewFile}
                                  onViewSummary={(doc) => doc.extractedMetadata?.summary && setViewingSummaryDoc({ filename: doc.filename, uploadedAt: doc.uploadedAt, summary: doc.extractedMetadata.summary })}
                                  onDeleteDocument={(docId, rowId) => handleDeleteRowDocument(section, rowId, docId)}
                                  revealRowId={revealRowBySection[section.id] ?? null}
                                  onRowRevealed={() => setRevealRowBySection(prev => ({ ...prev, [section.id]: null }))}
                                  isRowLocked={(rowId) => {
                                    // Two lock sources:
                                    // (1) built-in locked rows — identified by their deterministic seeded
                                    //     rowId (isLockedBuiltIn). User-added rows get random UUIDs, so a
                                    //     duplicate of a built-in id_type is never locked (s73 fix);
                                    // (2) C&D Gov-IDs cascade rows whose seeded `id_type` entry is unedited.
                                    const govFormLocked = isLockedBuiltIn(rowId);
                                    const govIdsCascadeLocked =
                                      section.slug === SECTION_SLUGS.CHILDREN_DEPENDENTS_GOV_IDS &&
                                      (() => {
                                        const f = section.fields.find((f) => f.fieldKey === 'id_type');
                                        return f ? entryMap[entryMapKey(f.id, rowId)]?.isSeeded === true : false;
                                      })();
                                    return govFormLocked || govIdsCascadeLocked;
                                  }}
                                />
                                {/* Phase 4R: "Add a record you found" appends a blank pending row the executor
                                    fills inline. Testimony (write_mode='append') is fully read-only for the
                                    executor (s145 ruling) — no add; instead a preserved-as-written note. */}
                                {executorEditActive && section.writeMode !== "append" && (
                                  <div className="px-4 pb-3">
                                    <ExecutorAddButton sectionSlug={section.slug} recordNoun={EXPANDABLE_LAYOUT_CONFIG[section.slug]?.recordNoun} onClick={() => addPendingExecutorRow(section.id)} />
                                  </div>
                                )}
                                {executorEditActive && section.writeMode === "append" && (
                                  <div className="px-4 pb-3">
                                    <p className="text-sm text-muted-foreground italic">
                                      {plannerFirstName ? `${plannerFirstName}'s own words — preserved as written.` : "Their own words — preserved as written."}
                                    </p>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Card layout: inline accordion (s87 F86-5: the executor section
                              is now a normal expandable section — its former custom
                              <YourExecutorCard> body + special-cases were removed). */}
                          {isExpanded && !isSectionNA && isCardLayout && (
                            <TableRow>
                              <TableCell colSpan={colSpan} className="p-0 bg-muted/5">
                                <div>

                                  {/* ── Persistent AI Document Summary ── */}
                                  {section.aiSummary && (
                                    <div
                                      className="px-4 py-3 border-b text-sm"
                                      style={{ backgroundColor: "hsl(168 76% 42% / 0.08)", borderColor: "hsl(168 76% 42% / 0.25)" }}
                                    >
                                      <p className="text-xs font-semibold text-primary mb-1 uppercase tracking-wide flex items-center gap-1">
                                        <FileSearch className="h-3 w-3" />
                                        AI Document Summary
                                      </p>
                                      <p className="text-foreground leading-relaxed">{section.aiSummary}</p>
                                    </div>
                                  )}

                                  {/* ── Pending extraction banner ── */}
                                  {pendingExtractionBySection[section.id] && (
                                    <PendingExtractionBanner
                                      pending={pendingExtractionBySection[section.id]}
                                      onReview={() => setConfirmingExtractionSection(section.id)}
                                      onDiscard={() => discardPendingExtraction(section.id)}
                                    />
                                  )}

                                  {/* ── Customize mode ── */}
                                  {customizingSectionId === section.id && (() => {
                                    const allSectionFields = [...section.fields].sort((a, b) => a.sortOrder - b.sortOrder);
                                    return (
                                      <div>
                                        <div className="px-4 py-2 bg-primary/5 border-b flex items-center gap-2">
                                          <Settings2 className="h-3.5 w-3.5 text-primary" />
                                          <span className="text-xs font-semibold text-primary uppercase tracking-wide">Customize items</span>
                                          <span className="text-xs text-muted-foreground ml-1">— drag to reorder · toggle visibility · rename or delete custom items</span>
                                        </div>
                                        <DndContext
                                          sensors={sensors}
                                          collisionDetection={closestCenter}
                                          onDragEnd={(event) => {
                                            const { active, over } = event;
                                            if (over && active.id !== over.id) {
                                              const oldIdx = allSectionFields.findIndex(f => f.id === active.id);
                                              const newIdx = allSectionFields.findIndex(f => f.id === over.id);
                                              const reordered = arrayMove(allSectionFields, oldIdx, newIdx);
                                              const updates = reordered.map((f, i) => ({ id: f.id, sortOrder: i * 10 }));
                                              reorderFieldsMutation.mutate(updates);
                                            }
                                          }}
                                        >
                                          <SortableContext items={allSectionFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                                            {allSectionFields.map(field => {
                                              const isFieldHidden = hiddenFieldIds.has(field.id);
                                              return (
                                                <SortableCustomizeFieldRow
                                                  key={field.id}
                                                  field={field}
                                                  isHidden={isFieldHidden}
                                                  renamingFieldId={renamingFieldId}
                                                  onToggleHide={() => isFieldHidden ? unhideFieldMutation.mutate(field.id) : hideFieldMutation.mutate(field.id)}
                                                  onRename={() => setRenamingFieldId(renamingFieldId === field.id ? null : field.id)}
                                                  onRenameSubmit={(name) => {
                                                    renameCustomFieldMutation.mutate({ fieldId: field.id, name });
                                                    setRenamingFieldId(null);
                                                  }}
                                                  onRenameCancel={() => setRenamingFieldId(null)}
                                                  onDelete={() => deleteCustomFieldMutation.mutate(field.id)}
                                                />
                                              );
                                            })}
                                          </SortableContext>
                                        </DndContext>
                                        {/* Add new item */}
                                        <div className="flex items-center gap-2 px-4 py-3 border-t bg-muted/20">
                                          <Input
                                            value={addingFieldName}
                                            onChange={(e) => setAddingFieldName(e.target.value)}
                                            placeholder="New item name…"
                                            className="h-8 text-sm flex-1"
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' && addingFieldName.trim()) {
                                                addBlankFieldFlight.runQuiet(() => addBlankFieldMutation.mutateAsync({ sectionId: section.id, name: addingFieldName.trim() }));
                                              }
                                              if (e.key === 'Escape') setAddingFieldName("");
                                            }}
                                          />
                                          <Button
                                            size="sm"
                                            className="h-8 px-3 text-xs"
                                            disabled={!addingFieldName.trim() || addBlankFieldFlight.inFlight || addBlankFieldMutation.isPending}
                                            onClick={() => {
                                              if (addingFieldName.trim()) {
                                                addBlankFieldFlight.runQuiet(() => addBlankFieldMutation.mutateAsync({ sectionId: section.id, name: addingFieldName.trim() }));
                                              }
                                            }}
                                          >
                                            <Plus className="h-3.5 w-3.5 mr-1" />
                                            Add Item
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* ── Normal view: card form grid ── */}
                                  {customizingSectionId !== section.id && (() => {
                                    const sectionIsLoading = (fileUpload.activeSectionId === section.id && fileUpload.isUploading) || sectionExtracting(section.id);
                                    const sectionSummary = aiSummary[section.id];
                                    const sectionHasHighlights = section.fields.some(f => fieldHighlights[f.id]);
                                    return (
                                      <div className="p-4 space-y-4">
                                        {/* AI loading pulse bar */}
                                        {sectionIsLoading && (
                                          <div className="rounded-lg overflow-hidden border border-teal-200 sticky top-0 z-20" style={{ backgroundColor: '#E6FFFA' }}>
                                            <div className="px-4 py-3 flex items-center gap-3">
                                              <div className="flex gap-1">
                                                <span className="block h-2 w-2 rounded-full animate-bounce" style={{ backgroundColor: '#1ABC9C', animationDelay: '0ms' }} />
                                                <span className="block h-2 w-2 rounded-full animate-bounce" style={{ backgroundColor: '#1ABC9C', animationDelay: '150ms' }} />
                                                <span className="block h-2 w-2 rounded-full animate-bounce" style={{ backgroundColor: '#1ABC9C', animationDelay: '300ms' }} />
                                              </div>
                                              <span className="text-sm font-medium" style={{ color: '#1ABC9C' }}>AI is reading your document…</span>
                                            </div>
                                            <div className="h-1 w-full bg-teal-100 relative overflow-hidden">
                                              <div className="absolute inset-y-0 left-0 w-1/2 animate-pulse" style={{ backgroundColor: '#1ABC9C', animation: 'slideRight 1.5s ease-in-out infinite' }} />
                                            </div>
                                          </div>
                                        )}
                                        {/* AI summary banner (shown after load, hidden once all highlights cleared) */}
                                        {!sectionIsLoading && sectionSummary && sectionHasHighlights && (
                                          <div className="rounded-lg px-4 py-3 flex items-start gap-3 border border-teal-200" style={{ backgroundColor: '#E6FFFA' }}>
                                            <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
                                            <p className="text-sm" style={{ color: '#0d6e5c' }}>
                                              {sectionSummary.filled > 0
                                                ? <>AI extracted <strong>{sectionSummary.filled}</strong> of <strong>{sectionSummary.total}</strong> field{sectionSummary.total !== 1 ? 's' : ''}. Tap any highlighted field to review.</>
                                                : <>No fields could be extracted. Please fill in the fields manually.</>}
                                            </p>
                                          </div>
                                        )}
                                        {!sectionIsLoading && sectionSummary && sectionSummary.filled === 0 && !sectionHasHighlights && (
                                          <div className="rounded-lg px-4 py-3 flex items-start gap-3 border border-amber-200 bg-amber-50">
                                            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                                            <p className="text-sm text-amber-800">No fields could be extracted. Please fill in the fields manually.</p>
                                          </div>
                                        )}
                                        {(cardSectionInstanceRowIds[section.id] ?? [null]).map((instanceRowId, instanceIdx) => {
                                          const instances = cardSectionInstanceRowIds[section.id] ?? [null];
                                          // About You's 4 yes/no toggles are the canonical reversal surface for the
                                          // situational gates (session 73): they read from / write to the `clients`
                                          // columns via situation-flags, NOT dead generic_entries. Detect them by fieldKey.
                                          const aboutYouGateOf = (fId: string): AutoNaGate | null => {
                                            if (section.slug !== SECTION_SLUGS.ABOUT_YOU) return null;
                                            const f = section.fields.find(x => x.id === fId);
                                            return (f?.fieldKey && ABOUT_YOU_GATE_FIELDS[f.fieldKey]) || null;
                                          };
                                          // s119: capture-only About You flags — persist to a `clients` column via
                                          // situation-flags like a gate, but gate NO section (no tuck-away dialog).
                                          // Returns the clients column key (e.g. "hasFirearms"), else null.
                                          const aboutYouCaptureFlagOf = (fId: string): string | null => {
                                            if (section.slug !== SECTION_SLUGS.ABOUT_YOU) return null;
                                            const f = section.fields.find(x => x.id === fId);
                                            return (f?.fieldKey && ABOUT_YOU_CAPTURE_FLAG_FIELDS[f.fieldKey]) || null;
                                          };
                                          const getInstValue = (fId: string) => {
                                            const gate = aboutYouGateOf(fId);
                                            if (gate) {
                                              const v = clientData?.[gate];
                                              return v === true ? "true" : v === false ? "false" : "";
                                            }
                                            // s119: capture-only flag reads from / writes to the clients column, like a gate.
                                            const captureFlag = aboutYouCaptureFlagOf(fId);
                                            if (captureFlag) {
                                              const v = (clientData as any)?.[captureFlag];
                                              return v === true ? "true" : v === false ? "false" : "";
                                            }
                                            return instanceRowId === null
                                              ? getFieldValue(fId, 'value')
                                              : (tableLocalEdits[`${fId}:${instanceRowId}`] ?? entryMap[`${fId}:${instanceRowId}`]?.value ?? "");
                                          };
                                          const handleInstChange = (fId: string, v: string) => {
                                            const gate = aboutYouGateOf(fId);
                                            if (gate) {
                                              handleGateToggle(gate, v === "true", GATE_DISPLAY_NAME[gate]);
                                              return;
                                            }
                                            // s119: capture-only flag — silent situation-flags write to the clients
                                            // column (NO tuck-away confirmation; mirrors work_situation → ownsBusiness).
                                            const captureFlag = aboutYouCaptureFlagOf(fId);
                                            if (captureFlag) {
                                              situationFlagMutation.mutate({ [captureFlag]: v === "true" });
                                              return;
                                            }
                                            // FIELD-AUDIT-1 #9 (D-154.4): owns_business (a reactivated About You gate) is now
                                            // the SOLE writer of clients.owns_business. work_situation only PREFILLS it —
                                            // "Self-employed" pre-answers Yes ONCE, when the planner hasn't answered
                                            // owns_business yet (clientData.ownsBusiness == null). It never writes false and
                                            // never overwrites an existing answer, so an employed side-business owner keeps
                                            // their Yes. Silent (no tuck-away confirmation): the select change is explicit.
                                            const fk = section.fields.find(f => f.id === fId)?.fieldKey;
                                            if (
                                              section.slug === SECTION_SLUGS.ABOUT_YOU &&
                                              fk === FIELD_KEYS.WORK_SITUATION &&
                                              deriveOwnsBusiness(v) &&
                                              clientData?.ownsBusiness == null
                                            ) {
                                              situationFlagMutation.mutate({ ownsBusiness: true });
                                            }
                                            return instanceRowId === null
                                              ? handleFieldChange(fId, 'value', v)
                                              : handleTableCellChange(fId, instanceRowId, v);
                                          };
                                          const handleInstBlur = (fi: GenericField) => {
                                            if (aboutYouGateOf(fi.id) || aboutYouCaptureFlagOf(fi.id)) return; // persisted via situation-flags mutation
                                            return instanceRowId === null
                                              ? handleFieldBlur(fi.id)
                                              : handleTableCellBlur(fi, instanceRowId, section.id);
                                          };
                                          return (
                                            <div key={instanceRowId ?? 'default'} className="space-y-4">
                                              {instances.length > 1 && (
                                                <div className="flex items-center justify-between px-1">
                                                  <span className="text-sm font-semibold text-foreground">{section.name} #{instanceIdx + 1}</span>
                                                  {instanceRowId !== null && !isSectionReadOnly && (
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                                      onClick={() => deleteCardInstance(section.id, instanceRowId)}>
                                                      <XIcon className="h-4 w-4" />
                                                    </Button>
                                                  )}
                                                </div>
                                              )}
                                          {/* ── Group-aware renderer (About You and future sections with SECTION_GROUPS config) ── */}
                                          {(() => {
                                            const groupConfig = getSectionGroups(section.slug);
                                            if (!groupConfig) return null;

                                            const fieldByKey = new Map(section.fields.map(f => [f.fieldKey, f]));

                                            const allGroupedKeys = new Set(groupConfig.groups.flatMap(g => g.fieldKeys));
                                            const ungroupedFields = section.fields
                                              // s154 round 2 (item 3): the ungrouped fallback must exclude inactive
                                              // tombstones AND derived/non-input fields (CARD_HIDDEN_FIELD_KEYS,
                                              // e.g. full_legal_name) — otherwise their VALUES render bare with no
                                              // label under the last group (the s154 round-2 leak).
                                              .filter(f => f.fieldKey
                                                && f.isActive !== false
                                                && !CARD_HIDDEN_FIELD_KEYS.has(f.fieldKey)
                                                && !allGroupedKeys.has(f.fieldKey)
                                                && !hiddenFieldIds.has(f.id))
                                              .sort((a, b) => a.sortOrder - b.sortOrder);

                                            // s109 (AY-1 Build 1): each group is an independently-collapsible
                                            // Accordion item (type="multiple" so several can stay open). Groups
                                            // default expanded unless `defaultCollapsed` is set (interim Secure
                                            // Information). Smooth height animation comes from the shared shadcn
                                            // Accordion primitive (accordion-down/up keyframes) — G7, no bespoke
                                            // collapse. The card-level autosave indicator stays below, outside.
                                            const defaultOpenGroupIds = groupConfig.groups
                                              .filter(g => !g.defaultCollapsed)
                                              .map(g => g.id);

                                            return (
                                              <div className="pb-8">
                                                <Accordion
                                                  type="multiple"
                                                  // PLANNER-LENS-1 (s152): controlled so the field-landing effect can open the
                                                  // sub-card holding a deep-linked field. Falls back to defaultOpenGroupIds until
                                                  // the planner (or the effect) touches it.
                                                  value={openGroupsBySection[section.id] ?? defaultOpenGroupIds}
                                                  onValueChange={(v) => setOpenGroupsBySection(prev => ({ ...prev, [section.id]: v as string[] }))}
                                                  className="mt-2"
                                                >
                                                {groupConfig.groups.map((group, gIdx) => {
                                                  const groupFields = group.fieldKeys
                                                    .map(key => fieldByKey.get(key))
                                                    .filter((f): f is GenericField => !!f && !hiddenFieldIds.has(f.id));

                                                  // s175 (SPOUSE-GATE-SUBSTRATE-1): the spouse quartet used to be filtered
                                                  // here by a hardcoded Set before this line ever ran. They now carry spec
                                                  // `conditions` like every other gated field, so ONE evaluator decides
                                                  // visibility for the whole card — no second substrate to remember.
                                                  const visibleGroupFields = groupFields.filter(f =>
                                                    evaluateShowIf(f.conditions as FieldConditions | null, liveEntryMap),
                                                  );

                                                  if (visibleGroupFields.length === 0) return null;

                                                  return (
                                                    <AccordionItem
                                                      key={group.id}
                                                      value={group.id}
                                                      className="border-b border-stone-200/70 last:border-b-0"
                                                    >
                                                      {/* Collapsible group header — chevron on the LEFT (matches
                                                          ExpandableSectionLayout / the app's section headers: ChevronRight
                                                          collapsed, rotates down when open), then the Fraunces H2 + subtitle.
                                                          No resting background; warm-stone hover (#ECE7DC, the one hover
                                                          convention used by section headers — s109 Build 2) + cursor-pointer
                                                          signal clickability. Built on AccordionPrimitive so the shared shadcn
                                                          AccordionTrigger (right-side chevron) stays untouched. */}
                                                      <AccordionPrimitive.Header className="flex">
                                                        <AccordionPrimitive.Trigger className="flex flex-1 items-center gap-3 px-6 py-5 text-left cursor-pointer transition-colors hover:bg-[#ECE7DC] [&[data-state=open]>svg]:rotate-90">
                                                          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                                                          <div>
                                                            <h2 style={{ fontFamily: FONT.serif, fontSize: 22, fontWeight: 500, color: WARM.ink, marginBottom: group.subtitle ? 4 : 0 }}>
                                                              {group.title}
                                                            </h2>
                                                            {group.subtitle && (
                                                              <p style={{ fontFamily: FONT.sans, fontSize: 13, color: WARM.taupe, lineHeight: 1.65 }}>
                                                                {group.subtitle}
                                                              </p>
                                                            )}
                                                          </div>
                                                        </AccordionPrimitive.Trigger>
                                                      </AccordionPrimitive.Header>

                                                      {/* Field grid */}
                                                      <AccordionContent className="px-6 pb-8 pt-2">
                                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
                                                          {visibleGroupFields.map(field => {
                                                            // Use literal col-span class (template strings are purged by Tailwind JIT)
                                                            const colClass = getColSpanClass(field.fieldKey, field.fieldType);
                                                            const fieldHighlight = instanceRowId === null ? fieldHighlights[field.id] : undefined;
                                                            const docCount = fieldDocuments[field.id]?.length ?? 0;

                                                            // s88 (Cat A Phase 1 fix): Category A fields on About You no longer
                                                            // route through the legacy SifRoutedField (which bound to + saved the
                                                            // server value, bypassing the intercept). They fall through to the
                                                            // normal CardFieldInput path → SensitiveFieldInput (store-driven,
                                                            // never the server value, no server save).
                                                            const isToggle = field.fieldType === 'yes_no';
                                                            return (
                                                              <div key={field.id} className={colClass} {...lensFieldAnchor(section.slug, field.fieldKey)}>
                                                                {/* s99 Fix 1: green left-border accent removed (two-zone separation replaces it). */}
                                                                <div style={fieldHighlight ? { backgroundColor: fieldHighlight === 'confident' ? '#E6FFFA' : '#FFFBEB', borderRadius: '0.375rem', padding: '4px' } : {}}>
                                                                  {/* s109 Build 2: yes_no toggles put the question label + switch on ONE
                                                                      compact line (no dead space). Other fields keep label-above-input. */}
                                                                  <div className={isToggle ? "flex items-center gap-3" : undefined}>
                                                                    {/* Label */}
                                                                    <div className={isToggle ? "flex items-center gap-1.5" : "flex items-center gap-1.5 mb-1.5"}>
                                                                      <label
                                                                        htmlFor={`input-card-${field.id}`}
                                                                        className="text-xs font-medium text-muted-foreground leading-none cursor-default"
                                                                      >
                                                                        {field.name}
                                                                        {fieldRendersStar(section.slug, field) && (
                                                                          <RequiredStar />
                                                                        )}
                                                                                                                                                {fieldHighlight === 'confident' && (
                                                                          <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#1ABC9C', color: '#fff' }}>AI filled</span>
                                                                        )}
                                                                        {fieldHighlight === 'uncertain' && (
                                                                          <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#D69E2E', color: '#fff' }}>Please verify</span>
                                                                        )}
                                                                      </label>
                                                                      {getAffordances(section.slug).perField.notes && (
                                                                        <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                                                          onClick={() => handleOpenDetail(field, section)} title="Notes & documents">
                                                                          <FileText className="h-3 w-3" />
                                                                        </Button>
                                                                      )}
                                                                    </div>
                                                                    {/* Input — hidden when the field is declined (s98) */}
                                                                    {!isFieldDeclined(field.id, instanceRowId) && (
                                                                    <CardFieldInput
                                                                      field={{
                                                                        ...field,
                                                                        placeholder: field.placeholder || (field.fieldKey ? (FIELD_PLACEHOLDERS[field.fieldKey] ?? undefined) : undefined),
                                                                      } as GenericField}
                                                                      value={getInstValue(field.id)}
                                                                      onChange={(v) => handleInstChange(field.id, v)}
                                                                      onBlur={() => handleInstBlur(field)}
                                                                      readOnly={isSectionReadOnly}
                                                                      clientData={clientData}
                                                                    />
                                                                    )}
                                                                  </div>
                                                                  {/* OTHER-SPECIFY-1 (s155, D-155.6): "Other" → a small non-required "Tell us more" box
                                                                      beneath the select. Self-gates (renders only on a specifyOnOther field with an Other
                                                                      value); persists to specify_text; never enters completion. */}
                                                                  {!isFieldDeclined(field.id, instanceRowId) && (
                                                                    <SpecifyOnOtherInput
                                                                      specifyOnOther={field.specifyOnOther}
                                                                      value={getInstValue(field.id)}
                                                                      specifyText={instanceRowId === null
                                                                        ? (entryMap[field.id]?.specifyText ?? "")
                                                                        : (entryMap[entryMapKey(field.id, instanceRowId)]?.specifyText ?? "")}
                                                                      onSave={(text) => saveSpecifyMutation.mutate({ fieldId: field.id, sectionId: section.id, rowId: instanceRowId, specifyText: text })}
                                                                      readOnly={isSectionReadOnly}
                                                                      testId={`specify-${field.id}${instanceRowId ? `-${instanceRowId}` : ''}`}
                                                                    />
                                                                  )}
                                                                  {/* DB-8: declarative capture-upstream below a matching toggle.
                                                                      Gated purely by config (captureForToggle) — no per-field branch.
                                                                      s109 Build 2: indented (pl-4) so the mini-list reads as the
                                                                      toggle's child within its full-width, vertically-stacked block. */}
                                                                  {(() => {
                                                                    const cap = field.fieldKey ? captureForToggle(field.fieldKey) : undefined;
                                                                    if (!cap || !clientData?.id) return null;
                                                                    // select-field-copy (s109 Build 4a) + select-mini-list (s111 Build 5) are
                                                                    // gated by the field's SELECT value (string), not the boolean toggle; only
                                                                    // field-copy / select-field-copy copy FROM a source field.
                                                                    const isSelectCopy = cap.type === "select-field-copy";
                                                                    const isSelectGated = isSelectCopy || cap.type === "select-mini-list";
                                                                    const sourceFieldId = (cap.type === "field-copy" || isSelectCopy)
                                                                      ? section.fields.find(f => f.fieldKey === cap.sourceField)?.id
                                                                      : undefined;
                                                                    return (
                                                                      <div className="pl-4">
                                                                        <CaptureUpstreamRenderer
                                                                          def={cap}
                                                                          clientId={clientData.id}
                                                                          value={isSelectGated ? false : getInstValue(field.id) === "true"}
                                                                          selectValue={isSelectGated ? getInstValue(field.id) : undefined}
                                                                          sourceValue={sourceFieldId ? getInstValue(sourceFieldId) : undefined}
                                                                        />
                                                                      </div>
                                                                    );
                                                                  })()}
                                                                  {/* s119: capture-only toggle guidance — shown on "Yes" only, in the
                                                                      standard guidance-line style (matches field helper text below), NOT a
                                                                      warning banner. Indented (pl-4) so it reads as the toggle's child,
                                                                      like a capture mini-list. Config-driven via ABOUT_YOU_TOGGLE_HINTS. */}
                                                                  {(() => {
                                                                    const hint = field.fieldKey ? ABOUT_YOU_TOGGLE_HINTS[field.fieldKey] : undefined;
                                                                    if (!hint || getInstValue(field.id) !== "true") return null;
                                                                    return (
                                                                      <p className="pl-4" style={{ fontFamily: FONT.sans, fontSize: 13, color: WARM.taupe, lineHeight: 1.4, marginTop: 6 }}>
                                                                        {hint}
                                                                      </p>
                                                                    );
                                                                  })()}
                                                                  {/* "I don't have this" / "Not available" (s98) */}
                                                                  <FieldDeclineControl
                                                                    declined={isFieldDeclined(field.id, instanceRowId)}
                                                                    declineReason={fieldDeclineReason(field.id, instanceRowId)}
                                                                    declineNote={fieldDeclineNote(field.id, instanceRowId)}
                                                                    eligible={!!field.declineEligible && !aboutYouGateOf(field.id) && field.fieldKey !== FIELD_KEYS.WORK_SITUATION /* D-152.8: declineEligible drives it (Cat-A now eligible → value-blind presence metadata); s111: the 4 work options cover everyone — no decline */}
                                                                    hasValue={!!getInstValue(field.id)}
                                                                    readOnly={isSectionReadOnly}
                                                                    pending={setDeclinedMutation.isPending}
                                                                    onDecline={(reason, note) => toggleDecline(field, section.id, instanceRowId, true, reason, note)}
                                                                    onUndo={() => toggleDecline(field, section.id, instanceRowId, false)}
                                                                    testId={`decline-${field.id}${instanceRowId ? `-${instanceRowId}` : ''}`}
                                                                  />
                                                                  {/* Helper text — plain taupe, no border */}
                                                                  {field.instructions && (
                                                                    <p style={{ fontFamily: FONT.sans, fontSize: 13, color: WARM.taupe, lineHeight: 1.4, marginTop: 6 }}>
                                                                      {field.instructions}
                                                                    </p>
                                                                  )}
                                                                  {/* s155 (D-155.4): flow-9 govid-name-sync notice. The planner's legal name (composed
                                                                      from the name parts) is silently mirrored onto their locked Government ID rows on save;
                                                                      this surfaces that highest-silent-flow copy once the family name is entered.
                                                                      The Rent→Housing pointer that used to sit here is REMOVED — its copy is a
                                                                      select-field-copy, so the standard SavedElsewhereNotice now rides inside
                                                                      CaptureUpstreamRenderer for both the Own→Properties and Rent→Housing branches
                                                                      (collapsing the former toast + pointer double-notice into one line). */}
                                                                  {field.fieldKey === FIELD_KEYS.FAMILY_NAME && !!getInstValue(field.id) && (
                                                                    <SavedElsewhereNotice
                                                                      message="Your name is saved on your Government ID records."
                                                                      testId="saved-elsewhere-govid-name"
                                                                    />
                                                                  )}
                                                                  {/* Doc count badge */}
                                                                  {docCount > 0 && (
                                                                    <button type="button"
                                                                      className="mt-1 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                                                                      onClick={() => handleOpenDetail(field, section)}>
                                                                      <Paperclip className="h-3 w-3" />
                                                                      {docCount} doc{docCount !== 1 ? 's' : ''}
                                                                    </button>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      </AccordionContent>
                                                    </AccordionItem>
                                                  );
                                                })}
                                                </Accordion>

                                                {/* Ungrouped fields — defensive; should be empty for About You */}
                                                {ungroupedFields.length > 0 && (
                                                  <div className="mt-10 px-6">
                                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
                                                      {ungroupedFields.map(field => (
                                                        <div key={field.id} className={`col-span-12 md:${fieldWidthToColSpan(field.layoutWidth, field.fieldType)}`}>
                                                          <CardFieldInput
                                                            field={field}
                                                            value={getInstValue(field.id)}
                                                            onChange={(v) => handleInstChange(field.id, v)}
                                                            onBlur={() => handleInstBlur(field)}
                                                            readOnly={isSectionReadOnly}
                                                            clientData={clientData}
                                                          />
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}

                                                {/* Autosave status — fields persist on change (debounced) + on blur; no button */}
                                                {!isSectionReadOnly && (
                                                  <div className="flex justify-end mt-2">
                                                    <SectionSaveStatus
                                                      isPending={upsertEntryMutation.isPending}
                                                      isError={upsertEntryMutation.isError}
                                                      lastSavedAt={lastSavedAt}
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })()}

                                          {/* ── Existing flat group renderer (all sections without SECTION_GROUPS config) ── */}
                                          {!getSectionGroups(section.slug) && groupedFields.map((group, gIdx) => {
                                          const visibleGroupFields = group.fields.filter(f =>
                                            !hiddenFieldIds.has(f.id) &&
                                            evaluateShowIf(f.conditions as FieldConditions | null, liveEntryMap)
                                          );
                                          if (visibleGroupFields.length === 0) return null;

                                          const regularFields = visibleGroupFields.filter(f => f.fieldType !== 'notes');
                                          const notesFields = visibleGroupFields.filter(f => f.fieldType === 'notes');

                                          return (
                                            <div key={gIdx} className="bg-card border rounded-lg shadow-sm overflow-hidden">
                                              {/* Soft group header */}
                                              <div className="px-4 py-2.5 bg-muted/30 border-b">
                                                <span className="text-sm font-semibold text-foreground">
                                                  {group.groupName || section.name}
                                                </span>
                                              </div>

                                              {/* Field grid */}
                                              <div className="p-4">
                                                <div className="grid grid-cols-12 gap-x-4 gap-y-4">
                                                  {regularFields.map(field => {
                                                    const docCount = fieldDocuments[field.id]?.length ?? 0;
                                                    const fieldHighlight = instanceRowId === null ? fieldHighlights[field.id] : undefined;
                                                    return (
                                                      <div key={field.id} className={fieldWidthToColSpan(field.layoutWidth)} {...lensFieldAnchor(section.slug, field.fieldKey, instanceRowId)}>
                                                      {/* s99 Fix 1: green left-border accent removed. */}
                                                      <div style={{
                                                        paddingLeft: '8px',
                                                        ...(fieldHighlight ? { backgroundColor: fieldHighlight === 'confident' ? '#E6FFFA' : '#FFFBEB', borderRadius: '0.375rem' } : {}),
                                                      }}>
                                                        {/* Label row */}
                                                        <div className="flex items-center gap-1.5 mb-1.5">
                                                          <Label htmlFor={`input-card-${field.id}`} className="text-xs font-medium text-muted-foreground leading-none cursor-default">
                                                            {field.name}
                                                            {fieldRendersStar(section.slug, field) && (
                                                              <RequiredStar />
                                                            )}
                                                                                                                        {field.isCritical && status !== 'complete' && (
                                                              <Badge variant="destructive" className="text-[10px] ml-1.5 py-0 px-1">Critical</Badge>
                                                            )}
                                                            {field.isCustom && (
                                                              <Badge variant="secondary" className="text-[10px] ml-1 py-0 px-1 font-normal">Custom</Badge>
                                                            )}
                                                            {fieldHighlight === 'confident' && (
                                                              <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#1ABC9C', color: '#fff' }}>AI filled</span>
                                                            )}
                                                            {fieldHighlight === 'uncertain' && (
                                                              <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#D69E2E', color: '#fff' }}>Please verify</span>
                                                            )}
                                                          </Label>
                                                          {/* Field actions */}
                                                          <div className="flex items-center gap-0.5 shrink-0">
                                                            {!isSectionReadOnly && field.isCustom && (
                                                              <>
                                                                <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                                                  onClick={() => setRenamingFieldId(renamingFieldId === field.id ? null : field.id)} title="Rename">
                                                                  <Pencil className="h-3 w-3" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" className={`h-5 w-5 transition-colors ${field.conditions ? 'text-blue-500 hover:text-blue-700' : 'text-muted-foreground hover:text-blue-500'}`}
                                                                  onClick={() => {
                                                                    setConditionDraft((field.conditions as ConditionRule | null) ?? null);
                                                                    setConditionField({ field, section });
                                                                  }}
                                                                  title="Conditional logic">
                                                                  <GitBranch className="h-3 w-3" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive"
                                                                  onClick={() => deleteCustomFieldMutation.mutate(field.id)} title="Delete"
                                                                  disabled={deleteCustomFieldMutation.isPending}>
                                                                  <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                              </>
                                                            )}
                                                            {!isSectionReadOnly && !field.isCustom && getAffordances(section.slug).perField.hide && (
                                                              <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                                                onClick={() => hideFieldMutation.mutate(field.id)} title="Hide"
                                                                disabled={hideFieldMutation.isPending}>
                                                                <EyeOff className="h-3 w-3" />
                                                              </Button>
                                                            )}
                                                            {getAffordances(section.slug).perField.notes && (
                                                              <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                                                onClick={() => handleOpenDetail(field, section)} title="Notes & documents">
                                                                <FileText className="h-3 w-3" />
                                                              </Button>
                                                            )}
                                                            {!isSectionReadOnly && !['file_ref', 'file_upload', 'signature', 'password', 'pin'].includes(field.fieldType) && getAffordances(section.slug).perField.sendToPhone && (
                                                              <SendToPhoneDialog
                                                                clientId={clientData.id}
                                                                targetType="generic_section"
                                                                targetId={section.id}
                                                                targetName={section.name}
                                                                fieldId={field.id}
                                                                fieldLabel={field.name}
                                                                onClose={() => {
                                                                  queryClient.invalidateQueries({
                                                                    queryKey: ["/api/clients", clientData.id, "generic-entries", category?.categoryType],
                                                                  });
                                                                }}
                                                                trigger={
                                                                  <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-primary" title={`Send to Phone — ${field.name}`}>
                                                                    <Smartphone className="h-3 w-3" />
                                                                  </Button>
                                                                }
                                                              />
                                                            )}
                                                            {!isSectionReadOnly && !['file_ref', 'file_upload', 'signature', 'password', 'pin'].includes(field.fieldType) && getAffordances(section.slug).perField.uploadFromComputer && (
                                                              <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                                                title={`Upload from Computer — ${field.name}`}
                                                                disabled={fileUpload.uploadingKey === field.id}
                                                                onClick={() => fileUpload.triggerUpload(section.id, { fieldId: field.id })}
                                                              >
                                                                {fileUpload.uploadingKey === field.id
                                                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                                                  : <Paperclip className="h-3 w-3" />}
                                                              </Button>
                                                            )}
                                                          </div>
                                                        </div>

                                                        {/* Rename input */}
                                                        {field.isCustom && !isSectionReadOnly && renamingFieldId === field.id && (
                                                          <div className="flex items-center gap-2 mb-1.5">
                                                            <Input autoFocus defaultValue={field.name} className="h-7 text-sm"
                                                              placeholder="Field name"
                                                              onBlur={(e) => {
                                                                const newName = e.target.value.trim();
                                                                if (newName && newName !== field.name) renameCustomFieldMutation.mutate({ fieldId: field.id, name: newName });
                                                                setRenamingFieldId(null);
                                                              }}
                                                              onKeyDown={(e) => {
                                                                if (e.key === 'Enter') e.currentTarget.blur();
                                                                if (e.key === 'Escape') setRenamingFieldId(null);
                                                              }}
                                                            />
                                                            <span className="text-xs text-muted-foreground whitespace-nowrap">Enter to save</span>
                                                          </div>
                                                        )}

                                                        {/* Helper text — plain taupe, no border */}
                                                        {field.instructions && (
                                                          <p style={{ fontFamily: FONT.sans, fontSize: 13, color: WARM.taupe, lineHeight: 1.4, marginTop: 6 }}>
                                                            {field.instructions}
                                                          </p>
                                                        )}

                                                        {/* Value input — hidden when declined (s98) */}
                                                        {!isFieldDeclined(field.id, instanceRowId) && (
                                                        <CardFieldInput
                                                          field={field}
                                                          value={getInstValue(field.id)}
                                                          onChange={(v) => handleInstChange(field.id, v)}
                                                          onBlur={() => handleInstBlur(field)}
                                                          readOnly={isSectionReadOnly}
                                                          clientData={clientData}
                                                          uploadProps={field.fieldType === "file_upload" ? {
                                                            onUploadFile: async (file: File) => {
                                                              const entry2 = entryMap[field.id] || pendingEntriesRef.current.get(field.id);
                                                              if (!entry2) throw new Error("No entry yet — save the field first");
                                                              const attached = await attachDocumentToEntry(entry2.id, file);
                                                              if (!attached) return; // duplicate drop
                                                              queryClient.invalidateQueries({ queryKey: ["/api/generic-entries/documents/counts"] });
                                                              toast({ title: "Document uploaded", description: "Your document has been attached successfully." });
                                                            },
                                                            docCount,
                                                          } : undefined}
                                                        />
                                                        )}
                                                        {/* OTHER-SPECIFY-1 (s155, D-155.6): "Other" → non-required "Tell us more" box.
                                                            Self-gating; persists to specify_text; never enters completion. */}
                                                        {!isFieldDeclined(field.id, instanceRowId) && (
                                                          <SpecifyOnOtherInput
                                                            specifyOnOther={field.specifyOnOther}
                                                            value={getInstValue(field.id)}
                                                            specifyText={instanceRowId === null
                                                              ? (entryMap[field.id]?.specifyText ?? "")
                                                              : (entryMap[entryMapKey(field.id, instanceRowId)]?.specifyText ?? "")}
                                                            onSave={(text) => saveSpecifyMutation.mutate({ fieldId: field.id, sectionId: section.id, rowId: instanceRowId, specifyText: text })}
                                                            readOnly={isSectionReadOnly}
                                                            testId={`specify-${field.id}${instanceRowId ? `-${instanceRowId}` : ''}`}
                                                          />
                                                        )}
                                                        {/* "I don't have this" / "Not available" (s98) */}
                                                        <FieldDeclineControl
                                                          declined={isFieldDeclined(field.id, instanceRowId)}
                                                                    declineReason={fieldDeclineReason(field.id, instanceRowId)}
                                                                    declineNote={fieldDeclineNote(field.id, instanceRowId)}
                                                          eligible={!!field.declineEligible /* D-152.8: declineEligible drives it; Cat-A eligible → presence metadata */}
                                                          hasValue={!!getInstValue(field.id)}
                                                          readOnly={isSectionReadOnly}
                                                          pending={setDeclinedMutation.isPending}
                                                          onDecline={(reason, note) => toggleDecline(field, section.id, instanceRowId, true, reason, note)}
                                                          onUndo={() => toggleDecline(field, section.id, instanceRowId, false)}
                                                          testId={`decline-${field.id}${instanceRowId ? `-${instanceRowId}` : ''}`}
                                                        />

                                                        {/* Document count badge */}
                                                        {docCount > 0 && (
                                                          <button type="button"
                                                            className="mt-1 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                                                            onClick={() => handleOpenDetail(field, section)}>
                                                            <Paperclip className="h-3 w-3" />
                                                            {docCount} doc{docCount !== 1 ? 's' : ''}
                                                          </button>
                                                        )}

                                                      </div>

                                                      {/* Per-field duplicate instances (ephemeral, session-only) */}
                                                      {instanceRowId === null && (pendingFieldInstances[field.id] ?? []).map(instRowId => (
                                                        <div key={instRowId} style={{ borderLeft: '3px solid transparent', paddingLeft: '8px' }}>
                                                          <div className="flex items-center justify-between mb-1.5">
                                                            <Label className="text-xs font-medium text-muted-foreground leading-none cursor-default">
                                                              {field.name}
                                                              {fieldRendersStar(section.slug, field) && (
                                                                <RequiredStar />
                                                              )}
                                                            </Label>
                                                            <Button
                                                              variant="ghost" size="icon"
                                                              className="h-5 w-5 text-muted-foreground hover:text-destructive"
                                                              title="Remove"
                                                              onClick={() => deleteFieldInstance(field.id, instRowId)}
                                                            >
                                                              <XIcon className="h-3.5 w-3.5" />
                                                            </Button>
                                                          </div>
                                                          <CardFieldInput
                                                            field={field}
                                                            value={tableLocalEdits[entryMapKey(field.id, instRowId)] ?? ""}
                                                            onChange={(v) => handleTableCellChange(field.id, instRowId, v)}
                                                            onBlur={() => {}}
                                                            readOnly={isSectionReadOnly}
                                                            clientData={clientData}
                                                          />
                                                        </div>
                                                      ))}

                                                      {/* "+ Add another [field name]" link */}
                                                      {instanceRowId === null && !isSectionReadOnly && !['repeating_group', 'table'].includes(field.fieldType) && getAffordances(section.slug).perField.addAnother && (
                                                        <button
                                                          type="button"
                                                          className="text-sm text-left transition-opacity hover:opacity-70"
                                                          style={{ color: '#1ABC9C' }}
                                                          onClick={() => addFieldInstance(field.id)}
                                                        >
                                                          + Add another {field.name}
                                                        </button>
                                                      )}
                                                      </div>
                                                    );
                                                  })}
                                                </div>

                                                {/* Consolidated notes fields at bottom */}
                                                {notesFields.length > 0 && (
                                                  <div className={`space-y-3 ${regularFields.length > 0 ? 'mt-4 pt-4 border-t' : ''}`}>
                                                    {notesFields.map(field => {
                                                      return (
                                                        <div key={field.id} style={{ paddingLeft: '8px' }}>
                                                          <Label className="text-xs font-medium text-muted-foreground leading-none cursor-default block mb-1.5">
                                                            {field.name}
                                                            {fieldRendersStar(section.slug, field) && (
                                                              <RequiredStar />
                                                            )}
                                                          </Label>
                                                          <CardFieldInput
                                                            field={field}
                                                            value={getInstValue(field.id)}
                                                            onChange={(v) => handleInstChange(field.id, v)}
                                                            onBlur={() => handleInstBlur(field)}
                                                            readOnly={isSectionReadOnly}
                                                            clientData={clientData}
                                                          />
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                            </div>
                                          );
                                        })}
                                        {!isSectionReadOnly && getAffordances(section.slug).section.addAnotherSection && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full border-dashed text-muted-foreground hover:text-foreground hover:border-primary/50"
                                            onClick={() => addCardInstance(section.id)}
                                          >
                                            <Plus className="h-4 w-4 mr-2" />
                                            Add Another {section.name}
                                          </Button>
                                        )}
                                      </div>
                                    );
                                  })()}

                                </div>
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Table layout: dynamic inner table with section.fields as columns */}
                          {/* WAT section uses the unified WhereIsEverything component above instead */}
                          {isExpanded && !isSectionNA && section.layoutType === "table" && section.slug !== SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY && (
                            <TableRow>
                              <TableCell colSpan={colSpan} className="p-0">
                                {/* AI loading / summary banner for table sections */}
                                {(() => {
                                  const tableIsLoading = (fileUpload.activeSectionId === section.id && fileUpload.isUploading) || sectionExtracting(section.id);
                                  const tableSummary = aiSummary[section.id];
                                  const tableHasHighlights = section.fields.some(f =>
                                    Object.keys(fieldHighlights).some(k => k.startsWith(`${f.id}:`))
                                  );
                                  if (tableIsLoading) return (
                                    <div className="border-b overflow-hidden sticky top-0 z-20" style={{ backgroundColor: '#E6FFFA' }}>
                                      <div className="px-4 py-2 flex items-center gap-3">
                                        <div className="flex gap-1">
                                          <span className="block h-2 w-2 rounded-full animate-bounce" style={{ backgroundColor: '#1ABC9C', animationDelay: '0ms' }} />
                                          <span className="block h-2 w-2 rounded-full animate-bounce" style={{ backgroundColor: '#1ABC9C', animationDelay: '150ms' }} />
                                          <span className="block h-2 w-2 rounded-full animate-bounce" style={{ backgroundColor: '#1ABC9C', animationDelay: '300ms' }} />
                                        </div>
                                        <span className="text-sm font-medium" style={{ color: '#1ABC9C' }}>AI is reading your document…</span>
                                      </div>
                                    </div>
                                  );
                                  if (!tableIsLoading && tableSummary && tableHasHighlights) return (
                                    <div className="px-4 py-2 flex items-center gap-2 border-b" style={{ backgroundColor: '#E6FFFA' }}>
                                      <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
                                      <p className="text-sm" style={{ color: '#0d6e5c' }}>
                                        AI filled <strong>{tableSummary.filled}</strong> cell{tableSummary.filled !== 1 ? 's' : ''} — highlighted cells need your review.
                                      </p>
                                    </div>
                                  );
                                  if (!tableIsLoading && tableSummary && tableSummary.filled === 0 && !tableHasHighlights) return (
                                    <div className="px-4 py-2 flex items-center gap-2 border-b bg-amber-50">
                                      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                                      <p className="text-sm text-amber-800">No fields could be extracted. Please fill in manually.</p>
                                    </div>
                                  );
                                  return null;
                                })()}
                                {/* ── Persistent AI Document Summary (table sections) ── */}
                                {section.aiSummary && (
                                  <div
                                    className="px-4 py-3 border-b text-sm"
                                    style={{ backgroundColor: "hsl(168 76% 42% / 0.08)", borderColor: "hsl(168 76% 42% / 0.25)" }}
                                  >
                                    <p className="text-xs font-semibold text-primary mb-1 uppercase tracking-wide flex items-center gap-1">
                                      <FileSearch className="h-3 w-3" />
                                      AI Document Summary
                                    </p>
                                    <p className="text-foreground leading-relaxed">{section.aiSummary}</p>
                                  </div>
                                )}
                                {(() => {
                                  const VISIBLE_COL_COUNT = 20;
                                  const hasExtraFields = section.fields.length > VISIBLE_COL_COUNT;
                                  const visibleFields = hasExtraFields ? section.fields.slice(0, VISIBLE_COL_COUNT) : section.fields;
                                  const extraFields = hasExtraFields ? section.fields.slice(VISIBLE_COL_COUNT) : [];
                                  const CHEV_W = 24;
                                  // Sticky left positions
                                  const deleteColLeft = 0;
                                  const chevColLeft = isSectionReadOnly ? 0 : 32;
                                  const firstDataColLeft = chevColLeft + (hasExtraFields ? CHEV_W : 0);
                                  // Total column count for colSpan (non-readonly context)
                                  const totalColCount = (isSectionReadOnly ? 0 : 1) + (hasExtraFields ? 1 : 0) + visibleFields.length + (isSectionReadOnly ? 0 : 5);
                                  return (
                                    <div style={{ overflowX: 'auto', width: '100%', display: 'block' }}>
                                      <table className="text-sm border-collapse" style={{ width: '100%', minWidth: 'max-content', tableLayout: 'auto' }}>
                                        <thead>
                                          <tr style={{ borderBottom: `1px solid ${WARM.stone}` }}>
                                            {/* Delete header */}
                                            {!isSectionReadOnly && (
                                              <th className="text-center py-2 px-1" style={{ width: 32, minWidth: 32, position: 'sticky', left: deleteColLeft, zIndex: 3, backgroundColor: WARM.stoneLight, borderRight: `1px solid ${WARM.stone}` }}>
                                                <Trash2 className="h-3.5 w-3.5 mx-auto" style={{ color: 'rgba(43,42,38,0.5)' }} />
                                              </th>
                                            )}
                                            {/* Chevron header */}
                                            {hasExtraFields && (
                                              <th style={{ width: CHEV_W, minWidth: CHEV_W, position: 'sticky', left: chevColLeft, zIndex: 3, backgroundColor: WARM.stoneLight, borderRight: `1px solid ${WARM.stone}` }} />
                                            )}
                                            {/* Visible field headers */}
                                            {visibleFields.map((field, fieldIdx) => (
                                              <th
                                                key={field.id}
                                                {...lensColumnAnchor(section.slug, field.fieldKey)}
                                                className="text-left py-2 px-2 font-semibold text-xs whitespace-nowrap"
                                                style={{
                                                  width: field.columnWidth || 150,
                                                  minWidth: field.columnWidth || 100,
                                                  color: WARM.charcoal,
                                                  backgroundColor: WARM.stoneLight,
                                                  ...(fieldIdx === 0 ? { position: 'sticky', left: firstDataColLeft, zIndex: 3, borderRight: `1px solid ${WARM.stone}` } : {}),
                                                }}
                                              >
                                                <span className="flex items-center gap-1.5 group/col">
                                                  {getIcon(field.icon, { size: 'h-3 w-3' })}
                                                  {toTitleCase(field.name)}
                                                  {fieldRendersStar(section.slug, field) && <RequiredStar className="ml-0" />}
                                                                                                    {!isSectionReadOnly && field.isCustom && (
                                                    <button
                                                      className="opacity-0 group-hover/col:opacity-100 transition-opacity ml-0.5 rounded hover:bg-red-500/20 p-0.5"
                                                      title={`Delete column "${field.name}"`}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteColumnConfirm({ sectionId: section.id, fieldId: field.id, fieldName: field.name });
                                                      }}
                                                    >
                                                      <XIcon className="h-3 w-3" style={{ color: 'rgba(43,42,38,0.6)' }} />
                                                    </button>
                                                  )}
                                                </span>
                                              </th>
                                            ))}
                                            {/* Action headers */}
                                            {!isSectionReadOnly && (
                                              <>
                                                <th style={{ width: 32, minWidth: 32, backgroundColor: WARM.stoneLight }} />
                                                <th style={{ width: 32, minWidth: 32, backgroundColor: WARM.stoneLight }} />
                                                <th colSpan={2} className="text-center py-1 text-xs font-medium whitespace-nowrap" style={{ backgroundColor: WARM.stoneLight, color: SAGE[600], borderLeft: `1px solid ${WARM.stone}`, minWidth: 80 }}>Add Documents</th>
                                              </>
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(tableSectionRows[section.id] || []).filter(rowId => !hiddenTableRows[section.id]?.has(rowId)).map(rowId => {
                                            // "Gov Form" badge: map each built-in `_locked` row's deterministic
                                            // rowId → its gov-form ids (same index basis as the seeder /
                                            // lockedBuiltInRowId). Keyed by rowId, not value — so only the
                                            // genuine built-in shows the badge.
                                            const _govFormsByRowId: Record<string, string[]> = {};
                                            ((section.defaultRows ?? []) as any[])
                                              .filter((r: any) => r.__v === undefined) // same basis as the seeder (lockedBuiltInRowId index)
                                              .forEach((dr: any, i: number) => {
                                                if (dr._locked === 'true' && dr._govForms) {
                                                  _govFormsByRowId[lockedBuiltInRowId(section.slug, i)] = String(dr._govForms).split(',');
                                                }
                                              });
                                            const rowGovForms: string[] | undefined = _govFormsByRowId[rowId];
                                            // Section-specific lock: Gov IDs cascade rows are locked
                                            // while their id_type entry is seeded (not yet user-edited).
                                            const _isGovIdsCascadeRow = section.slug === SECTION_SLUGS.CHILDREN_DEPENDENTS_GOV_IDS && (() => {
                                              const f = section.fields.find(f => f.fieldKey === 'id_type');
                                              return f ? (entryMap[entryMapKey(f.id, rowId)]?.isSeeded === true) : false;
                                            })();
                                            // Lock built-in rows by their deterministic seeded rowId
                                            // (isLockedBuiltIn) — user-added rows (random UUID) are never
                                            // locked, so a duplicate id_type stays deletable (s73 fix).
                                            const isLockedRow = isLockedBuiltIn(rowId) || _isGovIdsCascadeRow;
                                            const isRowExpanded = expandedTableRows.has(`${section.id}:${rowId}`);
                                            const toggleRowExpand = () => {
                                              setExpandedTableRows(prev => {
                                                const next = new Set(prev);
                                                const key = `${section.id}:${rowId}`;
                                                if (next.has(key)) next.delete(key);
                                                else next.add(key);
                                                return next;
                                              });
                                            };
                                            return (
                                              <Fragment key={rowId}>
                                                <tr
                                                  className="border-b"
                                                >
                                                  {/* Delete */}
                                                  {!isSectionReadOnly && (
                                                    <td className="text-center py-2 px-1" style={{ width: 32, position: 'sticky', left: deleteColLeft, zIndex: 2, backgroundColor: '#fff', borderRight: '1px solid #E2E8F0' }}>
                                                      {isLockedRow ? (
                                                        <span className="flex items-center justify-center h-6 w-6 text-muted-foreground" title="Required for government forms — cannot be removed">
                                                          <Lock className="h-3 w-3" style={{ color: 'hsl(168 76% 42%)' }} />
                                                        </span>
                                                      ) : (
                                                        <Button aria-label="Delete row"
                                                          variant="ghost"
                                                          size="icon"
                                                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                                          onClick={() => deleteRowMutation.mutate({ rowId, sectionId: section.id })}
                                                          disabled={deleteRowMutation.isPending}
                                                        >
                                                          <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                      )}
                                                    </td>
                                                  )}
                                                  {/* Chevron toggle */}
                                                  {hasExtraFields && (
                                                    <td className="text-center py-2 px-0" style={{ width: CHEV_W, position: 'sticky', left: chevColLeft, zIndex: 2, backgroundColor: '#fff', borderRight: '1px solid #E2E8F0' }}>
                                                      <button
                                                        onClick={toggleRowExpand}
                                                        className="flex items-center justify-center w-full h-full rounded hover:bg-muted/20 transition-colors"
                                                        title={isRowExpanded ? 'Collapse' : `Show ${extraFields.length} more field${extraFields.length !== 1 ? 's' : ''}`}
                                                      >
                                                        {isRowExpanded
                                                          ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                                          : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                                                      </button>
                                                    </td>
                                                  )}
                                                  {/* Visible field cells */}
                                                  {visibleFields.map((field, fieldIdx) => {
                                                    const isPassword = field.fieldType === 'password' || field.fieldType === 'pin';
                                                    const isMultiline = field.fieldType === 'multiline';
                                                    const cellHighlight = fieldHighlights[entryMapKey(field.id, rowId)];
                                                    const isFirstCol = fieldIdx === 0;
                                                    // s110: freeze the section's IDENTITY field on a locked row (not the
                                                    // positional first column). isFirstCol still drives the sticky column
                                                    // styling; only the read-only freeze keys on the identity field so the
                                                    // same Gov-IDs bug can't surface if a locked section is ever tabular.
                                                    const isIdentityCol = isLockedIdentityField(field.fieldKey, visibleFields);
                                                    const stickyBg = cellHighlight === 'confident' ? '#E6FFFA' : cellHighlight === 'uncertain' ? '#FFFBEB' : '#fff';
                                                    const cellValue = getTableCellValue(field.id, rowId);
                                                    return (
                                                      <td
                                                        key={field.id}
                                                        className="py-2 px-1"
                                                        title={!isPassword && cellValue ? cellValue : undefined}
                                                        style={{
                                                          width: field.columnWidth || 150,
                                                          maxWidth: field.columnWidth || 200,
                                                          overflow: 'hidden',
                                                          backgroundColor: isFirstCol ? stickyBg : (cellHighlight === 'confident' ? '#E6FFFA' : cellHighlight === 'uncertain' ? '#FFFBEB' : undefined),
                                                          transition: 'background-color 0.5s ease',
                                                          ...(isFirstCol ? { position: 'sticky', left: firstDataColLeft, zIndex: 2, borderRight: '1px solid #E2E8F0' } : {}),
                                                        }}
                                                      >
                                                        {isIdentityCol && isLockedRow ? (
                                                          <div className="flex items-center gap-1.5 px-2 py-1 min-w-0">
                                                            <span className="text-xs text-foreground truncate flex-1">{cellValue}</span>
                                                          </div>
                                                        ) : (
                                                          <EditableCell
                                                            value={cellValue}
                                                            compact={true}
                                                            onChange={(v) => handleTableCellChange(field.id, rowId, v)}
                                                            onBlur={() => handleTableCellBlur(field, rowId, section.id)}
                                                            type={isPassword ? 'password' : field.fieldType === 'phone' ? 'tel' : field.fieldType === 'email' ? 'email' : field.fieldType === 'currency' ? 'currency' : field.fieldType === 'date' ? 'date' : field.fieldType === 'number' ? 'number' : 'text'}
                                                            options={field.fieldType === 'select' ? ((field.options as { choices?: string[] } | null)?.choices ?? []) : undefined}
                                                            multiline={isMultiline}
                                                            placeholder={field.placeholder || ""}
                                                            readOnly={isSectionReadOnly}
                                                            testId={`input-table-${field.id}-${rowId}`}
                                                            ariaLabel={field.name}
                                                            isCategoryA={isCategoryAField(field)}
                                                            field={field}
                                                            clientId={clientData?.id}
                                                            rowId={rowId}
                                                            secureFileSetupAt={clientData?.secureFileSetupAt ?? null}
                                                          />
                                                        )}
                                                      </td>
                                                    );
                                                  })}
                                                  {/* Hide + Notes + Phone + Upload actions */}
                                                  {!isSectionReadOnly && (
                                                    <>
                                                      <td className="text-center py-2 px-1" style={{ width: 32 }}>
                                                        <Button
                                                          variant="ghost"
                                                          size="icon"
                                                          className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                                          title={hiddenTableRows[section.id]?.has(rowId) ? "Unhide row" : "Hide from your view — removes this item from your sidebar"}
                                                          onClick={() => {
                                                            setHiddenTableRows(prev => {
                                                              const sectionHidden = new Set(prev[section.id] ?? []);
                                                              if (sectionHidden.has(rowId)) sectionHidden.delete(rowId);
                                                              else sectionHidden.add(rowId);
                                                              return { ...prev, [section.id]: sectionHidden };
                                                            });
                                                          }}
                                                        >
                                                          {hiddenTableRows[section.id]?.has(rowId)
                                                            ? <Eye className="h-3.5 w-3.5 text-amber-500" />
                                                            : <EyeOff className="h-3.5 w-3.5" />}
                                                        </Button>
                                                      </td>
                                                      <td className="text-center py-2 px-1" style={{ width: 32 }}>
                                                        <Button
                                                          variant="ghost"
                                                          size="icon"
                                                          className="h-6 w-6 text-muted-foreground hover:text-primary"
                                                          title="Add additional information — notes, documents, or scan via phone"
                                                          onClick={() => handleOpenTableRowDetail(section, rowId)}
                                                        >
                                                          <FileText className="h-3.5 w-3.5" />
                                                        </Button>
                                                      </td>
                                                      <td className="text-center py-2 px-1" style={{ width: 80, borderLeft: `1px solid ${WARM.stone}` }}>
                                                        <SendToPhoneDialog
                                                          clientId={clientData.id}
                                                          targetType="generic_section"
                                                          targetId={section.id}
                                                          targetName={section.name}
                                                          rowId={rowId}
                                                          rowLabel={getTableCellValue(section.fields[0]?.id, rowId) || undefined}
                                                          onClose={() => {
                                                            queryClient.invalidateQueries({
                                                              queryKey: ["/api/clients", clientData.id, "generic-entries", category?.categoryType],
                                                            });
                                                          }}
                                                          trigger={
                                                            <button className="flex flex-col items-center gap-0.5 px-2 py-1 rounded hover:bg-primary/10 transition-colors" title="Upload from Phone — send a link to your phone to take a photo">
                                                              <Smartphone className="h-3.5 w-3.5" style={{ color: SAGE[600] }} />
                                                              <span className="text-[10px] font-medium whitespace-nowrap" style={{ color: SAGE[600] }}>Upload from Phone</span>
                                                            </button>
                                                          }
                                                        />
                                                      </td>
                                                      <td className="text-center py-2 px-1" style={{ width: 32 }}>
                                                        <Button
                                                          variant="ghost"
                                                          size="icon"
                                                          className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                                          title="Upload from Computer — attach a file from your device"
                                                          disabled={fileUpload.uploadingKey === `row:${rowId}`}
                                                          onClick={() => fileUpload.triggerUpload(section.id, { rowId })}
                                                        >
                                                          {fileUpload.uploadingKey === `row:${rowId}`
                                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                            : <Paperclip className="h-3.5 w-3.5" />}
                                                        </Button>
                                                      </td>
                                                    </>
                                                  )}
                                                </tr>
                                                {/* Expanded row: extra fields */}
                                                {hasExtraFields && isRowExpanded && (
                                                  <tr className="border-b">
                                                    <td colSpan={totalColCount} style={{ backgroundColor: '#f8fffe', padding: '12px 16px' }}>
                                                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 pl-3" style={{ borderLeft: `2px solid ${SAGE[600]}` }}>
                                                        {extraFields.map(field => {
                                                          const isPassword = field.fieldType === 'password' || field.fieldType === 'pin';
                                                          const isMultiline = field.fieldType === 'multiline';
                                                          const cellHighlight = fieldHighlights[entryMapKey(field.id, rowId)];
                                                          return (
                                                            <div key={field.id} {...lensFieldAnchor(section.slug, field.fieldKey, rowId)}>
                                                              <div className="flex items-center gap-1 mb-0.5">
                                                                {getIcon(field.icon, { size: 'h-3 w-3 text-muted-foreground' })}
                                                                <span className="text-xs font-semibold text-muted-foreground">{toTitleCase(field.name)}{fieldRendersStar(section.slug, field) && <RequiredStar />}</span>
                                                              </div>
                                                              <div style={cellHighlight ? { backgroundColor: cellHighlight === 'confident' ? '#E6FFFA' : '#FFFBEB', borderRadius: '0.25rem', transition: 'background-color 0.5s ease' } : undefined}>
                                                                {!isFieldDeclined(field.id, rowId) && (
                                                                <EditableCell
                                                                  value={getTableCellValue(field.id, rowId)}
                                                                  onChange={(v) => handleTableCellChange(field.id, rowId, v)}
                                                                  onBlur={() => handleTableCellBlur(field, rowId, section.id)}
                                                                  type={isPassword ? 'password' : field.fieldType === 'phone' ? 'tel' : field.fieldType === 'email' ? 'email' : field.fieldType === 'currency' ? 'currency' : field.fieldType === 'date' ? 'date' : field.fieldType === 'number' ? 'number' : 'text'}
                                                                  options={field.fieldType === 'select' ? ((field.options as { choices?: string[] } | null)?.choices ?? []) : undefined}
                                                                  multiline={isMultiline}
                                                                  placeholder={field.placeholder || ""}
                                                                  readOnly={isSectionReadOnly}
                                                                  testId={`input-table-${field.id}-${rowId}`}
                                                                  ariaLabel={field.name}
                                                                  isCategoryA={isCategoryAField(field)}
                                                                  field={field}
                                                                  clientId={clientData?.id}
                                                                  rowId={rowId}
                                                                  secureFileSetupAt={clientData?.secureFileSetupAt ?? null}
                                                                />
                                                                )}
                                                                {/* OTHER-SPECIFY-1 (s155, D-155.6): "Other" → non-required detail box; never enters completion. */}
                                                                {!isFieldDeclined(field.id, rowId) && (
                                                                  <SpecifyOnOtherInput
                                                                    specifyOnOther={field.specifyOnOther}
                                                                    value={getTableCellValue(field.id, rowId)}
                                                                    specifyText={entryMap[entryMapKey(field.id, rowId)]?.specifyText ?? ""}
                                                                    onSave={(text) => saveSpecifyMutation.mutate({ fieldId: field.id, sectionId: section.id, rowId, specifyText: text })}
                                                                    readOnly={isSectionReadOnly}
                                                                    testId={`specify-${field.id}-${rowId}`}
                                                                  />
                                                                )}
                                                                {/* "I don't have this" / "Not available" (s98) */}
                                                                <FieldDeclineControl
                                                                  declined={isFieldDeclined(field.id, rowId)}
                                                                  declineReason={fieldDeclineReason(field.id, rowId)}
                                                                  declineNote={fieldDeclineNote(field.id, rowId)}
                                                                  eligible={!!field.declineEligible /* D-152.8: declineEligible drives it; Cat-A eligible → presence metadata */}
                                                                  hasValue={!!getTableCellValue(field.id, rowId)}
                                                                  readOnly={isSectionReadOnly}
                                                                  pending={setDeclinedMutation.isPending}
                                                                  onDecline={(reason, note) => toggleDecline(field, section.id, rowId, true, reason, note)}
                                                                  onUndo={() => toggleDecline(field, section.id, rowId, false)}
                                                                  testId={`decline-${field.id}-${rowId}`}
                                                                />
                                                              </div>
                                                            </div>
                                                          );
                                                        })}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                                {/* Sub-card: vehicle insurance (Vehicle List only) */}
                                                {section.slug === SECTION_SLUGS.ASSETS_VEHICLE_LIST && clientData?.id && (
                                                  <tr>
                                                    <td colSpan={totalColCount} style={{ padding: 0 }}>
                                                      <VehicleInsuranceSubCard
                                                        clientId={clientData.id}
                                                        vehicleSectionId={section.id}
                                                        vehicleRowId={rowId}
                                                        vehicleMakeModel={section.fields.find(f => f.fieldKey === 'make_model') ? getTableCellValue(section.fields.find(f => f.fieldKey === 'make_model')!.id, rowId) : undefined}
                                                        vehicleType={section.fields.find(f => f.fieldKey === 'vehicle_type') ? getTableCellValue(section.fields.find(f => f.fieldKey === 'vehicle_type')!.id, rowId) : undefined}
                                                        readOnly={isSectionReadOnly}
                                                      />
                                                    </td>
                                                  </tr>
                                                )}
                                                {/* Inbound-link display: InsuresPicker (Insurance Policies only) */}
                                                {section.slug === SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES && clientData?.id && (
                                                  <tr>
                                                    <td colSpan={totalColCount} className="px-3 py-1.5" style={{ backgroundColor: '#F5F2EC', borderTop: '1px dashed #C8BFA8' }}>
                                                      <InsuresPicker
                                                        clientId={clientData.id}
                                                        policySectionId={section.id}
                                                        policyRowId={rowId}
                                                        policyType={section.fields.find(f => f.fieldKey === 'type') ? getTableCellValue(section.fields.find(f => f.fieldKey === 'type')!.id, rowId) : undefined}
                                                        readOnly={isSectionReadOnly}
                                                      />
                                                    </td>
                                                  </tr>
                                                )}
                                              </Fragment>
                                            );
                                          })}
                                          {/* Empty state — shown when no rows exist yet */}
                                          {(tableSectionRows[section.id] || []).length === 0 && (
                                            <tr>
                                              <td colSpan={totalColCount} className="py-4 px-4 text-center">
                                                <p className="text-sm text-muted-foreground">
                                                  Scan a document with your phone to fill this in automatically — or tap <strong>Add Row</strong> to enter manually.
                                                </p>
                                              </td>
                                            </tr>
                                          )}
                                          {/* Add Row */}
                                          {!isSectionReadOnly && (
                                            <tr>
                                              <td colSpan={totalColCount} className="py-2 px-2">
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                                                  onClick={() => handleAddRow(section.id, section.name)}
                                                  disabled={addRowFlight.inFlight || addRowMutation.isPending}
                                                >
                                                  <PlusCircle className="h-3.5 w-3.5" />
                                                  Add Row
                                                </Button>
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                })()}
                              </TableCell>
                            </TableRow>
                          )}

                          {/* Completion confirmation (s80; s84 #4a) — appears at a section
                              the moment it flips complete. A plain "Saved — [Section]
                              complete" note that auto-dismisses; the "Next" directive was
                              dropped so the dashboard NEXT UP card owns "what's next". */}
                          {justCompletedSectionId === section.id && isExpanded && (
                            <TableRow>
                              <TableCell colSpan={colSpan} className="p-0">
                                <div
                                  data-testid="continuation-banner"
                                  className="mx-4 my-2 flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm"
                                  style={{ borderColor: SAGE[600], backgroundColor: SAGE[50], color: SAGE[600] }}
                                >
                                  <Check className="h-4 w-4 shrink-0" />
                                  <span>Saved — <span className="font-semibold">{section.name}</span> complete</span>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </SortableContext>

                {/* Hidden Items section */}
                {!readOnly && hiddenFieldIds.size > 0 && (() => {
                  const allHiddenFields = allSectionsWithFields.flatMap(sec =>
                    sec.fields.filter(f => hiddenFieldIds.has(f.id)).map(f => ({ field: f, sectionName: sec.name }))
                  );
                  if (allHiddenFields.length === 0) return null;
                  return (
                    <TableRow>
                      <TableCell colSpan={colSpan} className="p-0">
                        <div className="border-t">
                          <div
                            className="flex items-center gap-2 px-4 py-2 bg-muted/20 cursor-pointer hover:bg-muted/30"
                            onClick={() => setShowHidden(!showHidden)}
                          >
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium text-muted-foreground">
                              Hidden Items ({allHiddenFields.length})
                            </span>
                            {showHidden ? <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" /> : <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />}
                          </div>
                          {showHidden && (
                            <div className="divide-y px-4">
                              {allHiddenFields.map(({ field, sectionName }) => (
                                <div key={field.id} className="flex items-center gap-3 py-2">
                                  <EyeOff className="h-4 w-4 text-muted-foreground shrink-0" />
                                  <span className="text-sm flex-1">{field.name}</span>
                                  <span className="text-xs text-muted-foreground">{sectionName}</span>
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => unhideFieldMutation.mutate(field.id)}
                                    disabled={unhideFieldMutation.isPending}
                                  >
                                    <Eye className="h-3.5 w-3.5 mr-1" />
                                    Unhide
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
            </DndContext>
        </CardContent>
        </Card>
      )}

      {/* Passive page-level thumbs (DB-16 polish): one prompt at the bottom of the
          category page (replaces the per-section thumbs). F&F-only (self-gated). */}
      {!readOnly && <CategoryThumbs categorySlug={categorySlug} categoryName={category?.name ?? ""} />}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => selectedFieldId && deleteEntryMutation.mutate(selectedFieldId)}
        title="Delete Entry"
        description="Are you sure you want to delete this entry? This action cannot be undone."
        confirmLabel="Delete"
        isPending={deleteEntryMutation.isPending}
      />

      <DeleteConfirmDialog
        open={!!deleteColumnConfirm}
        onOpenChange={(open) => { if (!open) setDeleteColumnConfirm(null); }}
        onConfirm={() => deleteColumnConfirm && deleteCustomFieldMutation.mutate(deleteColumnConfirm.fieldId)}
        title={`Delete column "${deleteColumnConfirm?.fieldName ?? ''}"`}
        description="This will permanently remove the column and all data in it. This action cannot be undone."
        confirmLabel="Delete column"
        isPending={deleteCustomFieldMutation.isPending}
      />

      {/* D-177.7 (s177, A2): essentials speed bump for "This doesn't apply to me". */}
      <SectionNaConfirmDialog
        sectionSlug={naConfirmSlug}
        sectionName={sections.find((s) => s.slug === naConfirmSlug)?.name}
        onConfirm={() => { if (naConfirmSlug) sectionNa.markNa(naConfirmSlug); setNaConfirmSlug(null); }}
        onCancel={() => setNaConfirmSlug(null)}
      />

      {/* Tuck-away confirm modal removed (s79): gate toggles apply immediately with a
          non-blocking Undo toast (see handleGateToggle). */}

      <DetailDialog
        open={!!detailField}
        onOpenChange={(open) => { if (!open) { setDetailField(null); setDetailRowId(null); resetNotesSaveStatus(); } }}
        title={detailField?.name || ""}
        isCritical={detailField?.isCritical}
        testId="dialog-field-detail"
      >
        {detailField && creatingEntryFieldId === detailField.id && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Preparing...</span>
          </div>
        )}
        {detailField && creatingEntryFieldId !== (detailRowId ? entryMapKey(detailField.id, detailRowId) : detailField.id) && (() => {
          const sectionAllowsDocs = detailFieldSection?.allowDocuments ?? true;
          const detailKey = detailRowId ? entryMapKey(detailField.id, detailRowId) : detailField.id;
          const detailEntry = detailRowId ? entryMap[detailKey] : (entryMap[detailField.id] || pendingEntriesRef.current.get(detailField.id));

          // Build extraction fields from the section's fields that have an extractionKey set
          const sectionExtractionFields: ExtractionField[] = (detailFieldSection?.fields ?? [])
            .filter((f) => f.extractionKey && f.extractionKey.trim())
            .map((f) => ({ name: f.name, extractionKey: f.extractionKey! }));

          const currentNotes = detailRowId
            ? (entryMap[detailKey]?.notes ?? "")
            : getFieldValue(detailField.id, 'notes');

          const handleNotesChange = detailRowId
            ? (notes: string) => {
                // For table rows, save notes inline via PATCH if entry exists
                if (detailEntry?.id) {
                  setNotesSaveStatus('saving');
                  patchEntryMutation.mutateAsync({ entryId: detailEntry.id, body: { notes } })
                    .then(() => {
                      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "generic-entries"] });
                      markNotesSaved();
                    })
                    .catch(() => setNotesSaveStatus('idle'));
                }
              }
            : (notes: string) => {
                // Card path: arm the shared-upsert watcher so its completion flips to "Saved".
                setNotesSaveStatus('saving');
                notesSaveArmedRef.current = true;
                handleFieldChange(detailField.id, 'notes', notes);
              };

          const handleNotesBlur = detailRowId
            ? () => {} // notes already saved onChange for table rows
            : () => handleFieldBlur(detailField.id);

          return (
            <>
              {detailField.instructions && (
                <InstructionsSection instructions={detailField.instructions} />
              )}

              {/* No targetType — Send-to-Phone is suppressed here. This dialog used to pass
                  "personal_info", whose server handlers were dropped in 77247a22 and 500'd on
                  call (retired s167, D-164.14 bug 1). Reviving phone upload on this surface is
                  a design question — see deferred-builds CARD-DOC-UPLOAD-1; the substrate is
                  the generic_section SendToPhoneDialog already mounted on expandable rows. */}
              <DocumentSection
                notes={currentNotes}
                onNotesChange={handleNotesChange}
                onNotesBlur={handleNotesBlur}
                saveStatus={notesSaveStatus}
                notesPlaceholder="Add notes about this item..."
                documents={sectionAllowsDocs ? (fieldDocuments[detailKey] || []) : []}
                loadingDocs={sectionAllowsDocs ? loadingFieldDocs.has(detailKey) : false}
                readOnly={readOnly}
                hideDocuments={!sectionAllowsDocs}
                clientId={clientData?.id}
                targetId={detailEntry?.id}
                targetName={detailField.name}
                onUploadFile={uploadDetailDocument}
                onDownloadDocument={handleDownloadDocument}
                onDeleteDocument={handleDeleteDocument}
                downloadEndpoint="/api/generic-documents"
                extractionFields={sectionExtractionFields}
                onAnalysisComplete={(docId, extractedMetadata) => {
                  saveExtractionMutation.mutate({ docId, fieldId: detailField.id, extractedMetadata });
                }}
              />
            </>
          );
        })()}
      </DetailDialog>

      {/* File-view modal (Build 1 expandable layout) */}
      <FileViewModal
        file={viewFile}
        onClose={() => setViewFile(null)}
        onDownload={handleDownloadDocument}
      />

      {/* GATE-CLEAR-1 (s147): planner confirm when a gate-closing edit would clear dependent fields. */}
      {gateClearDialog}

      {/* Condition rule editor dialog (custom fields — advisor/client) */}
      <ConditionDialog
        open={!!conditionField}
        onOpenChange={(open) => {
          if (!open) { setConditionField(null); setConditionDraft(null); }
        }}
      >
        <ConditionDialogContent className="max-w-lg" data-testid="dialog-condition-editor">
          <ConditionDialogHeader>
            <ConditionDialogTitle className="flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-primary" />
              Conditional Logic — {conditionField?.field.name}
            </ConditionDialogTitle>
          </ConditionDialogHeader>
          <div className="py-2">
            <ConditionRuleEditor
              conditions={conditionDraft}
              onChange={setConditionDraft}
              availableFields={
                (conditionField?.section.fields ?? [])
                  .filter(f => f.id !== conditionField?.field.id)
                  .map(f => ({
                    id: f.id,
                    name: f.name,
                    fieldType: f.fieldType,
                    options: f.options as { choices?: string[] } | null,
                  }))
              }
            />
          </div>
          <ConditionDialogFooter>
            <Button variant="outline" onClick={() => { setConditionField(null); setConditionDraft(null); }}>
              Cancel
            </Button>
            <Button
              onClick={() => conditionField && saveConditionsMutation.mutate({ fieldId: conditionField.field.id, conditions: conditionDraft })}
              disabled={saveConditionsMutation.isPending}
              data-testid="button-save-condition"
            >
              {saveConditionsMutation.isPending ? "Saving…" : "Save Rule"}
            </Button>
          </ConditionDialogFooter>
        </ConditionDialogContent>
      </ConditionDialog>

      {/* Confirm Extraction Modal (shared with the guided wizard — s76) */}
      {confirmingExtractionSection && (
        <ConfirmExtractionModal
          open={true}
          onOpenChange={(open) => { if (!open) { setConfirmingExtractionSection(null); setConfirmingExtractionRowId(null); } }}
          fields={confirmExtractionState[confirmingExtractionSection] ?? []}
          onFieldChange={(idx, val) => {
            setConfirmExtractionState(prev => {
              const arr = [...(prev[confirmingExtractionSection] ?? [])];
              arr[idx] = { ...arr[idx], value: val };
              return { ...prev, [confirmingExtractionSection]: arr };
            });
          }}
          saving={savingConfirmedExtraction}
          onDiscard={async () => {
            const pending = pendingExtractionBySection[confirmingExtractionSection];
            if (pending) {
              await deletePendingMutation.mutateAsync(pending.id);
              setPendingExtractionBySection(prev => { const n = {...prev}; delete n[confirmingExtractionSection]; return n; });
            }
            setConfirmingExtractionSection(null);
            setConfirmingExtractionRowId(null);
          }}
          onConfirm={async (fieldsToWrite) => {
            const pending = pendingExtractionBySection[confirmingExtractionSection];
            if (!pending) return;
            setSavingConfirmedExtraction(true);
            try {
              // DB-45 1H: the modal returns only the fields to write — every safe field
              // plus the destructive ones the planner explicitly opted to replace.
              const fields = fieldsToWrite;
              // Route the row-scoped (expandable) extraction to its row; card layout sends none → null.
              await confirmPendingMutation.mutateAsync({ pendingId: pending.id, body: { fields, rowId: confirmingExtractionRowId ?? undefined } });
              setSummaryBySectionId(prev => ({
                ...prev,
                [confirmingExtractionSection]: {
                  text: (pending as any).summaryText ?? "",
                  documentName: pending.documentName,
                  fieldsFilled: fields.filter(f => f.value).length,
                  date: new Date().toLocaleString(),
                },
              }));
              setPendingExtractionBySection(prev => { const n = {...prev}; delete n[confirmingExtractionSection]; return n; });
              queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "generic-entries"] });
              queryClient.invalidateQueries({ queryKey: ["/api/unified/progress", clientData?.id] });
              queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData?.id, "document-summaries", category?.id] });
              setConfirmingExtractionSection(null);
              setConfirmingExtractionRowId(null);
            } catch (e: any) {
              toast({ title: "Failed to save", description: e.message, variant: "destructive" });
            } finally {
              setSavingConfirmedExtraction(false);
            }
          }}
        />
      )}

      {/* Cross-section fan-out (s96): banner + per-section review modal. The banner
          sits fixed at the bottom so it reads as "below" the upload review. */}
      {fanout && !fanoutDismissed && !fanoutOpen && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(640px,calc(100vw-2rem))] shadow-lg">
          <FanOutBanner
            sections={fanout.sections}
            populatedCount={fanoutPopulatedIds.size}
            allPopulated={fanout.sections.every((s) => fanoutPopulatedIds.has(s.sectionId))}
            onReview={() => setFanoutOpen(true)}
            onSkip={() => setFanoutDismissed(true)}
            onClose={() => setFanoutDismissed(true)}
          />
        </div>
      )}
      {fanout && (
        <FanOutReviewModal
          open={fanoutOpen}
          onOpenChange={setFanoutOpen}
          sections={fanout.sections}
          populatedSectionIds={fanoutPopulatedIds}
          populatingSectionId={fanoutPopulatingId}
          onPopulate={populateFanoutSection}
          onPopulateAll={populateAllFanout}
          populatingAll={fanoutPopulatingAll}
          allDoneCount={fanoutAllDoneCount}
          reuploadWarning={fanout.reuploadWarning}
        />
      )}

      {/* s96: per-document AI summary modal */}
      <DocumentSummaryModal doc={viewingSummaryDoc} onOpenChange={(open) => { if (!open) setViewingSummaryDoc(null); }} />

      {/* View Summary Modal */}
      {viewSummarySectionId && summaryBySectionId[viewSummarySectionId] && (
        <Dialog open={true} onOpenChange={(open) => { if (!open) setViewSummarySectionId(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" aria-hidden="true" /> Document Summary</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {(() => {
                const s = summaryBySectionId[viewSummarySectionId];
                return (
                  <>
                    <div className="flex items-center justify-between text-xs text-muted-foreground border-b pb-2">
                      <span>{s.documentName}</span>
                      <span>Processed {s.date}</span>
                    </div>
                    {s.text && <p className="text-foreground leading-relaxed">{s.text}</p>}
                    <p className="text-xs text-muted-foreground">Fields auto-filled: {s.fieldsFilled}</p>
                  </>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
